import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useDragControls } from 'framer-motion';
import { GoogleMap, useJsApiLoader, Marker, OverlayView, Polyline } from '@react-google-maps/api';
import {
  MapPin, Navigation, Clock, Menu, History, CreditCard,
  Bell, User, LogOut, ChevronRight, ChevronDown, Activity, ArrowLeft, X, Shield, ShieldAlert, DollarSign, MessageCircle, Star, Plus, Minus, Phone, Gift, Share2, Globe, Pencil, Search, CalendarClock, Crosshair, LocateFixed, Tag, Wallet as WalletIcon, ArrowUpDown, Hand, Megaphone, Building2, Check, Ticket, FerrisWheel
} from 'lucide-react';
import { getRideOptions, loadRates, loadPersonalizedOffer, getLiveOffer } from '../../services/pricingEngine';
import { searchLocation, reverseGeocode, setPickupLocation, resolvePlaceId, initPlacesService } from '../../services/mapService';
import { loadMapZones, onZonesUpdated, findNoWaitingZone, findParkingZone } from '../../services/zoneService';
import { LL, projectOnPath, pathBetween, shortestTurn, stepFor, advanceAlong } from '../../utils/routeMotion';
import { walletService } from '../../services/walletService';
import { Location, RideType } from '../../types/rides';
import { useLanguage } from '../../hooks/useLanguage';
import RideHistory from '../../components/dashboard/RideHistory';
import { clearAuthSession } from '../../utils/authSession';
import { useSafeAreaInsets } from '../../utils/safeAreaInsets';
import PaymentMethods from '../../components/dashboard/PaymentMethods';
import Wallet from '../../components/dashboard/Wallet';
import TripsActivity from '../../components/dashboard/TripsActivity';
import Notifications from '../../components/dashboard/Notifications';
import Profile from '../../components/dashboard/Profile';
import ChatBox from '../../components/chat/ChatBox';
import { usePushNotifications } from '../../hooks/usePushNotifications';
import { registerForPush } from '../../lib/push';
import Promotions from '../../components/dashboard/Promotions';
import SpinAndWin from '../../components/dashboard/SpinAndWin';
import PromotionsFeed from '../../components/dashboard/PromotionsFeed';
import PromoNudge from '../../components/dashboard/PromoNudge';
import CorporateTickets from '../../components/dashboard/CorporateTickets';
import UpdatePrompt from '../../components/dashboard/UpdatePrompt';
import EnableNotificationsPrompt from '../../components/dashboard/EnableNotificationsPrompt';
import EmergencyContacts from '../../components/dashboard/EmergencyContacts';
import { useShakeDetection } from '../../hooks/useShakeDetection';
import { startTripRecording, stopTripRecording } from '../../lib/tripRecorder';
import { setStatusBarLightBackground } from '../../utils/nativeStatusBar';
import { pushBackHandler, consumeBack } from '../../lib/backHandler';

type SidebarTab = 'home' | 'history' | 'payment' | 'wallet' | 'trips' | 'notifications' | 'profile' | 'promotions' | 'places' | 'emergency' | 'spin';

const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY || '';
// NOTE: REACT_APP_GOOGLE_MAP_ID is deliberately no longer read. A vector Map ID
// makes Google take the palette from the cloud console and ignore the `styles`
// below entirely - so setting that env var would silently disable both map
// looks. It was only ever here for 360° rotation, which nothing in this app
// uses. See the <GoogleMap options> comment.

const mapContainerStyle = { width: '100%', height: '100%' };
const defaultCenter = { lat: -1.2921, lng: 36.8219 };
const LIBRARIES: ('places')[] = ['places'];

// The map is light on EVERY screen now - opening the app, picking a
// destination, and watching the driver all look the same. It used to switch from
// a dark idle palette to this one the moment a ride was accepted, which meant
// the first thing anyone saw was the map the app no longer uses, and the change
// mid-booking read as a glitch rather than a mode. The rest of the app's chrome
// (sheets, drawer, header) stays dark - it's the MAP that went light, not the
// brand.

// Daylight palette - the one riders actually read the city on.
//
// Two things drive it. White roads on a DARKER ground, because that figure-
// ground separation is what makes a road network readable at a glance - it's the
// job Bolt's sage does, and the first version of this got it wrong by taking
// "white and gold, not white and green" to mean the ground should be near-white
// too. Ground and roads ended up within a few percent of each other and the
// whole map washed out. The ground is now a warm sand with real weight in it,
// and gold is spent only on the route, the vehicle and trunk-road casings.
//
// And landmarks stay ON. The first version inherited `poi: off` from the old
// dark map, which was defensible when this palette only appeared during tracking
// (nothing should compete with the approaching driver) but is wrong now that the
// same map is the booking screen: parks, schools, hospitals, fuel stations and
// bus stops are how someone works out where they are and where they're going.
// Business LABELS are still suppressed - that's the clutter - but their icons
// stay, which is the balance the reference strikes too.
const boltDayStyles: google.maps.MapTypeStyle[] = [
  // Warm sand, deliberately well clear of white. Measured: this puts the
  // road/ground contrast at 1.61, against 1.56 sampled off the Bolt reference -
  // the previous #f2efe6 ground managed 1.15, which is why it looked washed out.
  { elementType: 'geometry', stylers: [{ color: '#d8cba8' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#544e3e' }] },
  // White halo behind label text - the only thing keeping street names legible
  // once they cross a white road.
  { elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff' }, { weight: 3 }] },
  { featureType: 'administrative', elementType: 'geometry', stylers: [{ visibility: 'off' }] },
  // Landmarks on, so the map can be navigated by what people actually steer by.
  { featureType: 'poi', stylers: [{ visibility: 'on' }] },
  { featureType: 'poi', elementType: 'labels.icon', stylers: [{ visibility: 'on' }] },
  // ...but not a wall of shop names. Icons carry the landmark; the name is only
  // worth the pixels for the big civic stuff below.
  { featureType: 'poi.business', elementType: 'labels.text', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#b9cc9c' }] },
  { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#5f6b4c' }] },
  { featureType: 'poi.medical', elementType: 'geometry', stylers: [{ color: '#eccfc8' }] },
  { featureType: 'poi.school', elementType: 'geometry', stylers: [{ color: '#dbcfae' }] },
  { featureType: 'poi.attraction', elementType: 'geometry', stylers: [{ color: '#e0c9a4' }] },
  // Bus stops and stations - the reference shows these, and in Nairobi a stage
  // is a more reliable landmark than a street name.
  { featureType: 'transit', stylers: [{ visibility: 'on' }] },
  { featureType: 'transit.station', elementType: 'labels.text', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit.line', elementType: 'geometry', stylers: [{ color: '#c4b493' }] },
  // Buildings sit LIGHTER than the ground, the way they do in the reference -
  // pale blocks separated by sand, which is what gives a city block its texture.
  { featureType: 'landscape.man_made', elementType: 'geometry', stylers: [{ color: '#f2ede1' }] },
  { featureType: 'landscape.natural', elementType: 'geometry', stylers: [{ color: '#d3c5a0' }] },
  { featureType: 'road', elementType: 'geometry.fill', stylers: [{ color: '#ffffff' }] },
  // Sand-toned casing rather than grey: it's the same warm family as the ground,
  // so roads separate cleanly without introducing a second, colder neutral.
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#bfae88' }, { weight: 1 }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#6b6555' }] },
  { featureType: 'road.highway', elementType: 'geometry.fill', stylers: [{ color: '#ffffff' }] },
  // The one place gold appears in the basemap: trunk roads get a gold casing, so
  // the road hierarchy is legible in the brand's own colour.
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#d9ab5c' }, { weight: 1.6 }] },
  { featureType: 'road.arterial', elementType: 'geometry.stroke', stylers: [{ color: '#d3c3a0' }] },
  // Deeper than the old wash - a river should read as water at a glance, not as
  // a slightly bluer patch of pavement.
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#a8c4d2' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#5d7280' }] },
];

// Route line: dark-gold casing under a gold core, the same two-tone treatment
// Bolt uses in dark green. The casing is what makes the line survive crossing a
// white road - a single gold stroke on white loses its edges at a glance.
const ROUTE_CASING = '#8a5a10';
const ROUTE_CORE = '#F5A623';

// What the parking choice says, per kind of facility.
//
// The original wording was written for malls and said so in every line - "Drop
// me inside", "Driver drives into the mall", "I'm not going into the mall". At
// JKIA that last one is nonsense: nobody at an airport is going into a mall,
// they're catching a flight. Wrong copy at the exact moment money is added to a
// fare is worse than no copy, so the facility's category (a column on the zone,
// not a guess from its name) picks the right sentences.
//
// Each pair has to work as a genuine either/or. The second option is a
// statement about the trip - "I'll walk in from the drop-off point" - not merely
// the absence of the first, because that's the choice a rider is actually making.
const PARKING_COPY: Record<string, { lede: string; inTitle: string; inSub: string; outTitle: string; outSub: string }> = {
  mall: {
    lede: 'Your driver pays at the barrier to bring you inside.',
    inTitle: 'Drop me inside',
    inSub: 'Driver drives into the mall',
    outTitle: 'Just drop me at the gate',
    outSub: "I'm not going into the mall",
  },
  airport: {
    lede: 'Your driver pays at the barrier to reach the terminal.',
    inTitle: 'Drop me at the terminal',
    inSub: 'Driver drives into the airport',
    outTitle: 'Drop me outside',
    outSub: "I'll walk in from the drop-off point",
  },
  station: {
    lede: 'Your driver pays at the barrier to reach the platform entrance.',
    inTitle: 'Drop me at the entrance',
    inSub: 'Driver drives into the station',
    outTitle: 'Drop me outside',
    outSub: "I'll walk in from the road",
  },
};
// Zones written before the category column existed have none, and the
// overwhelming majority of them are malls.
const parkingCopyFor = (category?: string | null) => PARKING_COPY[category || 'mall'] || PARKING_COPY.mall;

const TRACKING_STATUSES = ['pending', 'accepted', 'arrived', 'in_progress'];

let directionsService: google.maps.DirectionsService | null = null;

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000') + '/api/v1';

// Sound cue for ride events (accepted / arrived / cancelled / new message).
const playCustomerCue = () => {
  try {
    const a = new Audio('/alert.mp3');
    a.volume = 1.0;
    a.play().catch(() => {});
  } catch {}
};

// Recently selected destinations, for quick re-select on the Where-to screen.
const getRecentPlaces = (): any[] => {
  try { return JSON.parse(localStorage.getItem('recentPlaces') || '[]'); } catch { return []; }
};
const saveRecentPlace = (loc: any) => {
  try {
    if (!loc?.address) return;
    const list = getRecentPlaces().filter((p: any) => p.address !== loc.address);
    list.unshift({ address: loc.address, name: loc.name || loc.address.split(',')[0], lat: loc.lat, lng: loc.lng });
    localStorage.setItem('recentPlaces', JSON.stringify(list.slice(0, 8)));
  } catch {}
};

// Pick a representing icon for the driver's vehicle from its type.
const vehicleIcon = (t?: string) => {
  const s = (t || '').toLowerCase();
  if (/bike|moto/.test(s)) return '/icons/bike.png';
  if (/lorry|truck|send|xl|cargo/.test(s)) return '/icons/lorry.png';
  return '/icons/car.png';
};

// Straight-line distance in metres - used to estimate the driver's ETA to pickup
// from their live position without spending a Directions API call every 3s.
const haversineMeters = (a: { lat: number; lng: number }, b: { lat: number; lng: number }): number => {
  const R = 6371000;
  const dLat = (b.lat - a.lat) * Math.PI / 180;
  const dLng = (b.lng - a.lng) * Math.PI / 180;
  const x = Math.sin(dLat / 2) ** 2 +
    Math.cos(a.lat * Math.PI / 180) * Math.cos(b.lat * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
};

// (Compass bearing lives in utils/routeMotion.ts now - the marker animator is
// the only thing that needs it, and it needs it alongside the rest of the
// route-walking maths.)

// Seconds → "1 min 30 sec" / "45 sec". Under ~20s reads as arriving now.
const formatEta = (sec: number): string => {
  if (sec <= 20) return 'Arriving now';
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return m > 0 ? `${m} min ${s} sec` : `${s} sec`;
};

const vehicleTypeInfo: Record<string, { fuelType: string; capacity?: number; goods?: string; safetyTip: string }> = {
  'motorbike': {
    fuelType: 'Petrol',
    capacity: 1,
    safetyTip: 'Be careful while on the Motorbike. Remind Driver to observe speed limits.',
  },
  'safari-electric': {
    fuelType: 'Electric Powered',
    capacity: 1,
    safetyTip: 'Be careful while on the Motorbike. Remind Driver to observe speed limits.',
  },
  'safari-basic': {
    fuelType: 'Petrol / Diesel',
    capacity: 3,
    safetyTip: 'Make sure windows are closed to avoid phone snatchers in traffic jams.',
  },
  'safari-send-bike': {
    fuelType: 'Petrol',
    goods: 'Medium sized packages',
    safetyTip: 'Share ride details with the receiving partner.',
  },
  'safari-send-car': {
    fuelType: 'Petrol / Diesel',
    goods: 'Carries bulky goods & large packages',
    safetyTip: 'Share ride details with the receiving partner.',
  },
  'safari-women': {
    fuelType: 'Petrol / Diesel',
    capacity: 3,
    safetyTip: 'Make sure windows are closed to avoid phone snatchers in traffic jams.',
  },
  'safari-comfort': {
    fuelType: 'Petrol / Diesel',
    capacity: 3,
    safetyTip: 'Make sure windows are closed to avoid phone snatchers in traffic jams.',
  },
  'safari-xl': {
    fuelType: 'Petrol / Hybrid / Diesel',
    capacity: 6,
    safetyTip: 'Make sure windows are closed to avoid phone snatchers in traffic jams.',
  },
};

const CustomerDashboard: React.FC = () => {
  usePushNotifications('customer');                 // Web Push (browser/PWA)
  useEffect(() => { registerForPush(); }, []);      // Native FCM push (Capacitor; no-op on web)
  const navigate = useNavigate();
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: GOOGLE_KEY, libraries: LIBRARIES as any });
  const { lang, setLang, t } = useLanguage();

  const [activeTab, setActiveTab] = useState<SidebarTab>('home');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showPromotions, setShowPromotions] = useState(false);

  // The arrow nudging the customer towards the Promotions megaphone. Shown once
  // per visit to the app: it disappears the moment they open promotions, and
  // comes back the next time they return to the app. `promoNudgeRun` is bumped
  // on every foreground so the nudge REMOUNTS and re-checks for new posters,
  // rather than trusting a list it fetched hours ago.
  const [promoNudgeSeen, setPromoNudgeSeen] = useState(false);
  const [promoNudgeRun, setPromoNudgeRun] = useState(0);

  // Opening promotions from anywhere counts as having seen the nudge - the
  // megaphone itself is tappable, so this must not live on the nudge's onClick.
  const openPromotions = useCallback(() => {
    setShowPromotions(true);
    setPromoNudgeSeen(true);
  }, []);

  // Deliberately NOT persisted. The nudge is meant to return on every visit, so
  // "seen" is per-foreground state and nothing is written to storage - a cold
  // start then begins at zero for free, and there is no stale flag to clear when
  // a new promo goes up.
  useEffect(() => {
    const revive = () => { setPromoNudgeSeen(false); setPromoNudgeRun(n => n + 1); };
    const onVisible = () => { if (document.visibilityState === 'visible') revive(); };
    document.addEventListener('visibilitychange', onVisible);
    let remove: (() => void) | undefined;
    let cancelled = false;
    (async () => {
      try {
        const { App } = await import('@capacitor/app');
        const handle = await App.addListener('resume', revive);
        if (cancelled) { handle.remove(); return; }
        remove = () => handle.remove();
      } catch { /* not running in the native shell */ }
    })();
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      remove?.();
    };
  }, []);

  // Top-center Wallet pill balance - refreshed on mount and whenever the
  // rider returns to the home tab (e.g. right after a top-up or a ride paid
  // for from the wallet).
  const [headerWalletBalance, setHeaderWalletBalance] = useState<number | null>(null);
  useEffect(() => {
    if (activeTab !== 'home') return;
    walletService.getBalance().then(setHeaderWalletBalance).catch(() => {});
  }, [activeTab]);

  // Banked Spin & Win spins, for the sidebar badge. A free spin nobody notices
  // is a dead feature, and the only other prompt is a push the rider may have
  // declined - so it's fetched whenever the drawer is opened rather than polled.
  const [spinsAvailable, setSpinsAvailable] = useState(0);

  // Unread badge for the sidebar Bell - counts feed entries newer than the last
  // time the user opened the Notifications tab. Polls the feed every 30s.
  const [unreadNotifs, setUnreadNotifs] = useState(0);
  useEffect(() => {
    const API = (process.env.REACT_APP_API_URL || 'http://localhost:8000') + '/api/v1';
    const check = async () => {
      if (document.hidden) return;
      try {
        const token = localStorage.getItem('accessToken');
        const r = await fetch(`${API}/notifications/list`, { headers: { Authorization: `Bearer ${token}` } });
        const list = r.ok ? await r.json() : [];
        if (!Array.isArray(list)) return;
        const lastSeen = localStorage.getItem('notifLastSeen') || '1970-01-01T00:00:00Z';
        setUnreadNotifs(list.filter((n: any) => (n.created_at || '') > lastSeen).length);
      } catch {}
    };
    check();
    const id = setInterval(check, 30000);
    return () => clearInterval(id);
  }, []);
  // Opening the Notifications tab marks everything seen.
  useEffect(() => {
    if (activeTab === 'notifications') {
      localStorage.setItem('notifLastSeen', new Date().toISOString());
      setUnreadNotifs(0);
    }
  }, [activeTab]);

  // Refresh the spin count each time the drawer opens, and again on leaving the
  // Spin tab so a spent spin doesn't leave a stale badge behind.
  useEffect(() => {
    if (!sidebarOpen && activeTab !== 'spin') return;
    const API = (process.env.REACT_APP_API_URL || 'http://localhost:8000') + '/api/v1';
    (async () => {
      try {
        const token = localStorage.getItem('accessToken');
        const r = await fetch(`${API}/spin/state`, { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) return;
        const s = await r.json();
        // A locked wheel shows no badge - nudging towards a screen that can't be
        // used is worse than staying quiet.
        setSpinsAvailable(s?.enabled ? (s.spinsAvailable || 0) : 0);
      } catch {}
    })();
  }, [sidebarOpen, activeTab]);
  const [pickup, setPickup] = useState<Location | null>(null);
  const [dropoff, setDropoff] = useState<Location | null>(null);
  const [pickupQuery, setPickupQuery] = useState('');
  const [dropoffQuery, setDropoffQuery] = useState('');
  const [pickupSuggestions, setPickupSuggestions] = useState<Location[]>([]);
  const [dropoffSuggestions, setDropoffSuggestions] = useState<Location[]>([]);
  const [rideResults, setRideResults] = useState<any[]>([]);
  const [selectedRide, setSelectedRide] = useState<RideType | null>(null);
  const [navigationMode, setNavigationMode] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // Discount code + scheduling
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(0);
  const [discountMsg, setDiscountMsg] = useState('');
  // This rider's live discount - Bolt-style tiered % (big for a brand new
  // account, decaying as they take more rides) when logged in, falling back to
  // the admin's flat offer otherwise - independent of a manually-entered
  // discount code. Loaded eagerly so the idle map can show it before
  // pickup/dropoff are even set (rideResults, where offerPercent normally
  // lives, doesn't exist yet at that point).
  const [liveOfferPct, setLiveOfferPct] = useState<number | null>(null);
  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      await Promise.all([loadRates(), loadPersonalizedOffer()]);
      if (!cancelled) setLiveOfferPct(getLiveOffer()?.percent ?? null);
    };
    refresh();
    const interval = setInterval(refresh, 20000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);
  const [scheduleFor, setScheduleFor] = useState('');
  // Home screen (Bolt-style) service tiles: collapse/expand of the idle
  // "discovery" sheet (separate from `sheetExpanded`, which is the ride-
  // RESULTS sheet's own collapse state, so tapping a tile before any ride
  // search never disturbs that unrelated toggle).
  const [homeSheetExpanded, setHomeSheetExpanded] = useState(false);
  // Controls-driven drag (dragListener={false} below) so more than the tiny
  // handle bar can start the gesture - the discovery view's heading/search/
  // tiles also call sheetDragControls.start() on pointerdown, so a swipe
  // anywhere on that peek content lifts the whole sheet instead of the
  // content div's native scroll swallowing it (a stationary tap still fires
  // its button's onClick normally - Framer only treats it as a drag once the
  // pointer actually moves).
  const sheetDragControls = useDragControls();
  // Set when the rider taps the Motorbike/Electric/Safari Send tile - narrows
  // the ride-options list down to that class once results come back (cleared
  // by "See all rides").
  const [preselectRideType, setPreselectRideType] = useState<RideType | null>(null);
  // Set when the rider taps the Schedule tile - pre-fills the "Schedule for
  // later" field in the Confirm modal once they reach it.
  const [scheduleIntent, setScheduleIntent] = useState(false);
  // Dispatch queue
  const [inQueue, setInQueue] = useState(false);
  const [queuePosition, setQueuePosition] = useState(0);
  const [directions, setDirections] = useState<google.maps.DirectionsResult | null>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [locatingUser, setLocatingUser] = useState(true);
  const [locationFailed, setLocationFailed] = useState(false);
  const searchTimeout = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const bookedRideRef = React.useRef<{ price: number; distance: number; duration: number } | null>(null);
  // Dispatch safety - ONE customer → ONE driver, ever.
  // triedDriverIds: every driver already offered this ride (declined/timed-out),
  // so a request is NEVER re-offered to someone who already saw it (no A→B→C→B).
  // dispatching: re-entrancy lock so two overlapping status-poll ticks can't fire
  // findAnotherDriver twice and put the SAME request on two drivers at once.
  // How many times we've re-offered to drivers who already declined this ride -
  // caps the loop so a lone, repeatedly-declining driver can't ring forever.
  const dispatchingRef = React.useRef(false);
  const driverMarkersRef = React.useRef<Map<string, google.maps.Marker>>(new Map());
  const driverHeadingsRef = React.useRef<Map<string, number>>(new Map()); // last-applied heading per fleet marker
  // Last gold-bake generation each marker set was drawn at - see the bake effect.
  const fleetBakeTickRef = React.useRef(0);
  const activeBakeTickRef = React.useRef(0);
  const activeMarkerRef = React.useRef<google.maps.Marker | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [nearbyDrivers, setNearbyDrivers] = useState<any[]>([]);
  const [activeRideId, setActiveRideId] = useState('');
  const [showDriverDetail, setShowDriverDetail] = useState(false); // expanded driver/vehicle card
  const [recentPlaces, setRecentPlaces] = useState<any[]>([]); // recent destinations for quick select
  // Home/Work each hold UP TO 5 saved places (migrated from the old single-place
  // shape). Tapping Home/Work in "Where to" suggests these; managed from the menu.
  const [savedPlaces, setSavedPlaces] = useState<{ home: any[]; work: any[] }>(() => {
    try {
      const raw = JSON.parse(localStorage.getItem('savedPlaces') || '{}');
      const toArr = (v: any) => Array.isArray(v) ? v.slice(0, 5) : (v ? [v] : []);
      return { home: toArr(raw.home), work: toArr(raw.work) };
    } catch { return { home: [], work: [] }; }
  });
  const [settingPlace, setSettingPlace] = useState<'home' | 'work' | null>(null); // picking a Home/Work address
  const [expandSlot, setExpandSlot] = useState<'home' | 'work' | null>(null); // showing a slot's saved list in search
  const persistSavedPlaces = (next: { home: any[]; work: any[] }) => {
    setSavedPlaces(next);
    localStorage.setItem('savedPlaces', JSON.stringify(next));
  };
  const removeSavedPlace = (slot: 'home' | 'work', idx: number) => {
    persistSavedPlaces({ ...savedPlaces, [slot]: savedPlaces[slot].filter((_, i) => i !== idx) });
  };
  const [bookingForOther, setBookingForOther] = useState(false);
  // ── Corporate rides ──
  // corporateOn is the rider's intent; corporateInfo is the server's answer about
  // which company they belong to. The eligibility error is a centre-screen
  // overlay, not inline text, because it ends the corporate path entirely.
  const [corporateOn, setCorporateOn] = useState(false);
  const [corporateChecking, setCorporateChecking] = useState(false);
  const [corporateInfo, setCorporateInfo] = useState<{ id: string; name: string } | null>(null);
  const [corporateError, setCorporateError] = useState('');
  // Live approval request the rider is waiting on.
  const [corporateRequest, setCorporateRequest] = useState<{ id: string; company: string; expiresAt: string; isTicket?: boolean } | null>(null);
  const [corporateSecsLeft, setCorporateSecsLeft] = useState(0);
  const [corporateOutcome, setCorporateOutcome] = useState<'declined' | 'expired' | null>(null);
  // Ask for a TICKET instead of a car now: the approver says yes when they can,
  // and she calls the driver herself any time in the next 24 hours. Off by
  // default - the instant path is still the common case and must stay one tap.
  const [corporateAsTicket, setCorporateAsTicket] = useState(false);
  // Advisory "I plan to travel around…", shown to the approver. Never enforced.
  const [corporateTicketTime, setCorporateTicketTime] = useState('');
  // Why the company is paying. Required, but satisfiable with one tap on a chip:
  // an approver asked to authorise a fare between two addresses with no purpose
  // cannot actually make a decision, and a free-text box alone would be left
  // blank by most people standing at a kerb in a hurry.
  const [corporateReason, setCorporateReason] = useState('');
  // Set when an approver turns a request into a ticket, so the waiting screen
  // can stop waiting and say what actually happened.
  const [corporateTicketIssued, setCorporateTicketIssued] = useState<{ code: string | null } | null>(null);
  // Bumped to make the ticket list re-read after one is issued.
  const [ticketsRefresh, setTicketsRefresh] = useState(0);
  const [passengerName, setPassengerName] = useState('');
  const [passengerPhone, setPassengerPhone] = useState('');
  const [passengerError, setPassengerError] = useState(false);
  // Safari Send (package delivery) - sender/receiver may not be the rider
  // booking the trip, so the driver needs the receiver's contact separately.
  const [senderName, setSenderName] = useState('');
  const [senderPhone, setSenderPhone] = useState('');
  const [receiverName, setReceiverName] = useState('');
  const [receiverPhone, setReceiverPhone] = useState('');
  const [sendDetailsError, setSendDetailsError] = useState(false);
  // Which ride type the sender/receiver fields follow. This used to read
  // preselectRideType alone - set only by the home service tiles, which were
  // removed - so nothing set it any more and the fields never rendered for either
  // Safari Send type: a package could be booked with no receiver attached to it.
  // What the rider picks in the ride list (selectedRide) is the real signal now;
  // preselectRideType is still honoured for entry points that pre-pick a service.
  const sendRideType = selectedRide || preselectRideType;
  const isSafariSend = sendRideType === 'safari-send-bike' || sendRideType === 'safari-send-car';
  const [addStopMode, setAddStopMode] = useState(false); // search screen is picking a mid-trip stop
  const [editDropoffMode, setEditDropoffMode] = useState(false); // editing dropoff during active trip
  const [dropoffEditMsg, setDropoffEditMsg] = useState('');
  const [stopAddedMsg, setStopAddedMsg] = useState(''); // confirmation toast after adding a stop
  // ─── Map zones ─────────────────────────────────────────────────────────────
  // Fetched once (and cached in localStorage between sessions), then evaluated
  // locally on every pin move - see services/zoneService.ts for why the test
  // runs here rather than server-side.
  const [zonesLoadedAt, setZonesLoadedAt] = useState(0);
  useEffect(() => {
    // Subscribe BEFORE loading: loadMapZones resolves from cache immediately and
    // revalidates behind it, so the interesting event - a newer list arriving -
    // happens after that promise has already settled. Without this the screen
    // would keep evaluating against whatever was cached when it mounted, which
    // is how a mall added today failed to prompt on a phone that had a "fresh"
    // list from before it existed.
    const off = onZonesUpdated(() => setZonesLoadedAt(Date.now()));
    loadMapZones().then(() => setZonesLoadedAt(Date.now())).catch(() => {});
    return off;
  }, []);
  const [zoneNoticeOpen, setZoneNoticeOpen] = useState(false);
  // Whether the rider agreed to cover mall parking for this booking. Defaults to
  // TRUE whenever the drop-off is a mall, because choosing that mall as your
  // destination almost always means going into it - and the failure modes are
  // lopsided. Defaulted off, a rider who doesn't notice leaves the driver paying
  // the barrier out of a fare they've already been commissioned on. Defaulted on
  // and wrong, the rider sees the line item and the live total, and unticks it
  // in one tap before confirming. Reset per booking below.
  const [includeParking, setIncludeParking] = useState(true);
  const [rideStatus, setRideStatus] = useState('');
  // "A ride is live and the rider is watching it happen." Drives the whole
  // daylight map treatment - the white/gold basemap, the gold vehicle, and the
  // Bolt-style Safety pill and recentre button that only exist on this screen.
  const isTracking = TRACKING_STATUSES.includes(rideStatus);
  // The no-waiting zone this pickup sits in, if any. Recomputed whenever the
  // pickup pin moves and once the zone list lands, so a rider dragging a pin
  // along Tom Mboya Street sees the warning appear as they cross into it.
  const pickupZone = useMemo(
    () => findNoWaitingZone(pickup?.lat, pickup?.lng),
    // zonesLoadedAt is the "zones are now available" signal - findNoWaitingZone
    // reads a module-level cache, so nothing else here changes when it fills.
    [pickup?.lat, pickup?.lng, zonesLoadedAt],
  );
  // The mall whose car park this drop-off sits in, if any - null everywhere else,
  // which is what keeps the parking choice off the confirm sheet for the ordinary
  // trip that doesn't need it.
  const dropoffParkingZone = useMemo(
    () => findParkingZone(dropoff?.lat, dropoff?.lng),
    [dropoff?.lat, dropoff?.lng, zonesLoadedAt],
  );
  // What this booking will actually add for parking. Zero unless the drop-off is
  // a mall AND the rider left the option on - so every fare figure below can add
  // it unconditionally instead of re-testing the conditions each time.
  //
  // Deliberately never offered on a CORPORATE or SCHEDULED trip. Both are
  // created on paths that bypass POST /rides/request - the approver's endpoint
  // in routes/corporate.ts, and the dispatcher that materialises a due scheduled
  // trip - and neither stamps parking_fee. Completion could therefore not split
  // it back out, and the driver would be commissioned on their own parking
  // ticket. Both are worth wiring up later; half of it is worse than none, and
  // hiding the choice is honest where charging for it silently would not be.
  const parkingOffered = !!dropoffParkingZone && !corporateOn && !scheduleFor;
  const parkingFee = parkingOffered && includeParking ? (dropoffParkingZone!.parking_fee || 0) : 0;
  // Mall / airport / station wording for whichever facility this drop-off is.
  const parkingCopy = parkingCopyFor(dropoffParkingZone?.category);
  // A fresh destination is a fresh decision: carrying "no parking" over from the
  // last trip to a different mall would silently under-charge, and carrying "yes"
  // to a destination that isn't a mall at all is meaningless.
  useEffect(() => { setIncludeParking(true); }, [dropoff?.lat, dropoff?.lng]);
  const [assignedDriver, setAssignedDriver] = useState<any>(null);
  const [assignedDriverId, setAssignedDriverId] = useState('');
  const [bookingError, setBookingError] = useState('');
  const [noDriversModal, setNoDriversModal] = useState(false);
  const [showSplash, setShowSplash] = useState(true); // cold-start full-screen ripple
  // Compass heading for the location-dot beam (Google-style wedge). Throttled hard -
  // deviceorientation can fire ~60Hz and this component is huge. null = no compass
  // (beam just points north/up).
  const [compassHeading, setCompassHeading] = useState<number | null>(null);
  const compassRef = useRef<{ h: number; t: number }>({ h: -999, t: 0 });
  useEffect(() => {
    const onOrient = (e: any) => {
      let h: number | null = null;
      if (typeof e.webkitCompassHeading === 'number') h = e.webkitCompassHeading; // iOS
      else if (typeof e.alpha === 'number') h = 360 - e.alpha; // Android (absolute preferred)
      if (h == null || Number.isNaN(h)) return;
      const now = Date.now();
      const prev = compassRef.current;
      if (now - prev.t < 250 || Math.abs(h - prev.h) < 4) return;
      compassRef.current = { h, t: now };
      setCompassHeading(h);
    };
    window.addEventListener('deviceorientationabsolute', onOrient, true);
    window.addEventListener('deviceorientation', onOrient, true);
    return () => {
      window.removeEventListener('deviceorientationabsolute', onOrient, true);
      window.removeEventListener('deviceorientation', onOrient, true);
    };
  }, []);
  const [showChat, setShowChat] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  // Which of the two "Enter Destination" rows is currently the editable
  // search field - lets the same suggestions list drive either box (tapping
  // a row switches focus to it), and lets swapPickupDropoff hand editing
  // straight to the box that now needs a fresh location typed into it.
  const [activeSearchField, setActiveSearchField] = useState<'pickup' | 'dropoff'>('dropoff');
  const pickupSearchInputRef = useRef<HTMLInputElement | null>(null);
  // The "Enter Destination" overlay is white (unlike the rest of the app),
  // and it draws full-screen behind the status bar - so the default light
  // (white) status bar icons become invisible against it. Swap to dark icons
  // while it's open, back to light the moment it closes.
  useEffect(() => {
    setStatusBarLightBackground(searchFocused);
    return () => setStatusBarLightBackground(false);
  }, [searchFocused]);

  // The search overlay is sized to the VISUAL viewport (see effect below), so it
  // ends exactly at the top of the soft keyboard instead of running behind it.
  const [vvRect, setVvRect] = useState<{ top: number; height: number } | null>(null);
  // Lock the page behind the full-screen search overlay. Without this, focusing the
  // input let the underlying page scroll/pan (esp. when the soft keyboard opened),
  // pushing the search box off the top of the viewport so you had to scroll to type.
  useEffect(() => {
    if (!searchFocused) return;
    const { body } = document;
    const html = document.documentElement;
    const prevBody = body.style.overflow;
    const prevHtml = html.style.overflow;
    body.style.overflow = 'hidden';
    html.style.overflow = 'hidden';
    window.scrollTo(0, 0);
    // Pin the overlay to the visual viewport. When the iOS keyboard opens it
    // shrinks the visual viewport from the bottom; binding the panel's height to
    // that means the search box stays put instead of iOS scrolling the layout
    // viewport to reveal the input - which was pushing it up under the notch and
    // forcing you to pull the panel back down before you could type.
    const vv = window.visualViewport;
    // iOS fires visualViewport resize/scroll on nearly every frame of the
    // keyboard's slide animation - updating React state synchronously on each
    // one forced a full re-render/reflow of the search panel mid-animation,
    // fighting the keyboard's own animation for main-thread time (Android
    // doesn't need this listener at all, so it never hit this cost). Coalesce
    // into at most one state update per animation frame instead.
    let rafId: number | null = null;
    const sync = () => {
      if (rafId != null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        if (vv) setVvRect({ top: vv.offsetTop, height: vv.height });
      });
    };
    sync();
    vv?.addEventListener('resize', sync);
    vv?.addEventListener('scroll', sync);
    return () => {
      body.style.overflow = prevBody; html.style.overflow = prevHtml;
      vv?.removeEventListener('resize', sync);
      vv?.removeEventListener('scroll', sync);
      if (rafId != null) cancelAnimationFrame(rafId);
      setVvRect(null);
    };
  }, [searchFocused]);
  // How much of the screen the soft keyboard is currently covering, in px (0 when
  // it's closed). The bottom sheet is docked to the bottom of the app shell, which
  // is sized in dvh - and dvh doesn't shrink for the keyboard - so anything typed
  // into the sheet (the Safari Send sender/receiver fields) was left underneath
  // it. The visual viewport is the one thing that does know: what the layout
  // viewport has that the visible one doesn't is exactly the keyboard.
  //
  // Measured globally rather than per-field so any input the sheet ever grows
  // gets the same treatment. Coalesced to one update per frame for the same
  // reason as the search overlay below: iOS fires these on nearly every frame of
  // the keyboard's slide animation.
  const [keyboardPx, setKeyboardPx] = useState(0);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    let rafId: number | null = null;
    const sync = () => {
      if (rafId != null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        const covered = window.innerHeight - vv.height - vv.offsetTop;
        // Ignore small deltas - browser chrome and iOS's own rubber-banding move
        // the visual viewport by a few px without a keyboard being involved.
        setKeyboardPx(covered > 80 ? Math.round(covered) : 0);
      });
    };
    sync();
    vv.addEventListener('resize', sync);
    vv.addEventListener('scroll', sync);
    return () => {
      vv.removeEventListener('resize', sync);
      vv.removeEventListener('scroll', sync);
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, []);
  // Bring the field that was just tapped into the sheet's own scroll area, after
  // the keyboard has finished opening and the lift above has been applied.
  const keepFieldVisible = (e: React.FocusEvent<HTMLElement>) => {
    const el = e.target;
    setTimeout(() => el.scrollIntoView({ block: 'center', behavior: 'smooth' }), 300);
  };
  // Bottom-sheet ("Choose a ride") collapse/expand - swipe the centre handle.
  const [sheetExpanded, setSheetExpanded] = useState(true);
  // Always land on the expanded trip-details form when a destination is set
  // (recent tap, search suggestion, pin-drop, swap - every path). Without
  // this, if the rider had previously collapsed the sheet to peek at the map
  // during an earlier trip, picking a NEW destination would silently reuse
  // that leftover collapsed state - landing on a 132px sliver with the
  // discount badge/Find Rides button all off-screen and nothing but a small
  // handle to swipe up, which read as "can't find rides at all".
  useEffect(() => {
    if (dropoff) setSheetExpanded(true);
  }, [dropoff]);
  // Active Ride Status Panel (Finding driver / Driver found / en route / etc.)
  // collapse/expand - separate handle+state from the sheet above, own swipe
  // tracking so dragging one sheet never affects the other. Defaults
  // collapsed ("swiped down") showing just a one-line status - swipe up for
  // the full driver card and actions.
  const [trackingSheetExpanded, setTrackingSheetExpanded] = useState(false);
  const trackingTouchStartY = useRef<number | null>(null);
  // Shared by the handle bar AND (while collapsed) the one-line summary below
  // it - swiping up to expand only worked when the thumb landed on the thin
  // ~24px handle strip itself; the summary text underneath it (most of the
  // visibly touchable panel while collapsed) had no handlers at all, so a
  // swipe starting there silently did nothing depending on where a phase's
  // status text happened to sit.
  const handleTrackingTouchStart = (e: React.TouchEvent) => { trackingTouchStartY.current = e.touches[0].clientY; };
  const handleTrackingTouchEnd = (e: React.TouchEvent) => {
    if (trackingTouchStartY.current == null) return;
    const dy = e.changedTouches[0].clientY - trackingTouchStartY.current;
    trackingTouchStartY.current = null;
    if (dy > 30) setTrackingSheetExpanded(false);
    else if (dy < -30) setTrackingSheetExpanded(true);
  };
  // The one moment the panel opens itself: the driver accepts. Everything the
  // rider needs right then - who's coming, the call and message buttons
  // (driverContactCard), the plate and ETA - lives in the expanded view, and the
  // collapsed peek is a single line of text with no hint that a swipe is what
  // reveals the rest. Riders were left staring at "Driver is on the way" with no
  // way to reach the driver.
  //
  // Fires only on the TRANSITION into 'accepted' (previous status held in a ref),
  // not on every render while accepted - so if the rider then swipes it back down
  // to watch the map, it stays down. Later phases (arrived, in_progress) keep the
  // existing swipe-to-open behaviour; by then the rider has already seen the card.
  // Recovering an accepted ride on app relaunch counts as a transition ('' ->
  // 'accepted') and opens too, which is the same thing the rider wants there.
  const prevRideStatusRef = useRef('');
  useEffect(() => {
    const prev = prevRideStatusRef.current;
    prevRideStatusRef.current = rideStatus;
    if (rideStatus === 'accepted' && prev !== 'accepted') setTrackingSheetExpanded(true);
  }, [rideStatus]);
  const [showRating, setShowRating] = useState(false);
  // Cash trips: confirm "Pay X to driver" before the rating prompt appears.
  // (Card trips pay via the Paystack modal, which already gates the rating.)
  const [driverPaidConfirmed, setDriverPaidConfirmed] = useState(false);
  const [selectedRating, setSelectedRating] = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  const [ratingSubmitting, setRatingSubmitting] = useState(false);
  const [ratingDone, setRatingDone] = useState(false);
  // True when the rating prompt was re-surfaced on return (trip ended while the
  // customer was away) → the modal greets with "How was your last trip?".
  const [isRecoveredRating, setIsRecoveredRating] = useState(false);
  const ratingPromptedRef = useRef('');
  // Mirror current rating/ride state into refs so the recovery callback (which is
  // memoised with []) can read fresh values without re-subscribing.
  const showRatingRef = useRef(false);
  const activeRideIdRef = useRef('');
  // Trips we've already rated/handled - persisted so the "How was your last trip?"
  // prompt fires ONCE per trip and never comes back after a reload.
  const isTripHandled = (id: string) => {
    try { return (JSON.parse(localStorage.getItem('ratedTrips') || '[]') as string[]).includes(id); }
    catch { return false; }
  };
  const markTripHandled = (id: string) => {
    if (!id) return;
    try {
      const list: string[] = JSON.parse(localStorage.getItem('ratedTrips') || '[]');
      if (!list.includes(id)) { list.push(id); localStorage.setItem('ratedTrips', JSON.stringify(list.slice(-50))); }
    } catch {}
  };
  const [showPayment, setShowPayment] = useState(false);
  const [pendingPaymentRideId, setPendingPaymentRideId] = useState<string | null>(null);
  const [pendingPaymentPrice, setPendingPaymentPrice] = useState(0);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentDone, setPaymentDone] = useState(false);
  const [driverDeclinedMsg, setDriverDeclinedMsg] = useState('');
  const [stops, setStops] = useState<(Location | null)[]>([]);
  const [stopQueries, setStopQueries] = useState<string[]>([]);
  const [stopSuggestions, setStopSuggestions] = useState<Location[][]>([]);
  const [pinMode, setPinMode] = useState(false);
  const [pinTarget, setPinTarget] = useState<'pickup' | 'dropoff'>('dropoff'); // which field the map pin sets
  const [pinCenter, setPinCenter] = useState<{ lat: number; lng: number } | null>(null);
  const [pinGeocoding, setPinGeocoding] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  // What the rider types when they pick "Other" - picking it alone said nothing
  // useful, so every "Other" cancellation reached the admin as the bare word.
  const [cancelReasonOther, setCancelReasonOther] = useState('');
  // What actually gets stored on the ride (ride_requests.cancel_reason) and shown
  // in the admin dashboard: the typed text for "Other", the preset otherwise.
  const cancelReasonToSend = cancelReason === 'Other' ? cancelReasonOther.trim() : cancelReason;
  const [isSearchingDriver, setIsSearchingDriver] = useState(false);
  const [declinedReason, setDeclinedReason] = useState('');
  const [unreadMessages, setUnreadMessages] = useState(0);
  const seenMsgCount = React.useRef(0);
  const prevStatusRef = React.useRef<string>(''); // for one-shot sound cues on status change
  const prevMsgCountRef = React.useRef<number>(-1); // for new-message sound cue
  const [driverLivePos, setDriverLivePos] = useState<{ lat: number; lng: number } | null>(null);
  const [inProgressDirections, setInProgressDirections] = useState<google.maps.DirectionsResult | null>(null);
  const [tripEta, setTripEta] = useState<string>('');
  const [tripMins, setTripMins] = useState<string>('');
  // Live ETA (seconds) of the driver to the pickup while they're on the way.
  const [pickupEtaSec, setPickupEtaSec] = useState<number | null>(null);
  const inProgressDirRef = React.useRef<google.maps.DirectionsService | null>(null);
  const lastDirFetchRef = React.useRef<number>(0);
  const didFitInProgressRef = React.useRef<boolean>(false); // fit the route ONCE per trip, then leave zoom to the user
  // Driver → pickup route shown while the driver is on the way (accepted/arrived).
  const [toPickupDirections, setToPickupDirections] = useState<google.maps.DirectionsResult | null>(null);
  const toPickupDirRef = React.useRef<google.maps.DirectionsService | null>(null);
  const lastToPickupFetchRef = React.useRef<number>(0);
  // ─── Smooth driver-marker movement + heading ───────────────────────────────
  // See the animator inside the live-tracking effect for how these fit together.
  // In short: `poll` produces a queue of waypoints (route-snapped where possible)
  // and a deadline; a single long-lived rAF loop walks that queue at constant
  // speed and eases the icon's rotation towards the direction of travel.
  const markerAnimRef = React.useRef<number | null>(null);
  const lastRealPosRef = React.useRef<{ lat: number; lng: number } | null>(null);
  const lastMoveTsRef = React.useRef<number>(0); // when the driver last actually moved
  const appliedHeadingRef = React.useRef<number>(0);
  // Where the marker is DRAWN right now - lags the newest fix by up to one
  // update, which is exactly what buys the smooth motion. Mutated in place.
  const renderPosRef = React.useRef<{ lat: number; lng: number } | null>(null);
  const renderHeadingRef = React.useRef<number>(0);   // currently drawn rotation
  const targetHeadingRef = React.useRef<number>(0);   // direction of travel it eases towards
  const queueRef = React.useRef<{ lat: number; lng: number }[]>([]); // waypoints left to walk
  const queueStartedRef = React.useRef<number>(0);
  const queueDurationRef = React.useRef<number>(2500);
  const avgGapRef = React.useRef<number>(0);          // rolling mean interval between fixes
  // The route the driver is currently following (driver→pickup, or driver→
  // destination once the trip starts). Movement between two fixes is snapped
  // onto this so the marker takes the road's corners instead of cutting them.
  const routePathRef = React.useRef<{ lat: number; lng: number }[] | null>(null);
  const didFitDriverRef = React.useRef(false);   // auto-fit map to driver+me once per accepted ride
  // Latest values mirrored into refs so the live-tracking effect can read them
  // WITHOUT listing them as deps - otherwise every poll's setAssignedDriver/
  // setPickup (new object each time) tears down & recreates the marker (= blink).
  const assignedDriverRef = React.useRef<any>(null);
  const pickupRef = React.useRef<any>(null);
  const selectedRideRef = React.useRef<string>('');
  const rideStatusRef = React.useRef<string>('');
  const [sosSent, setSosSent] = useState(false);
  const [sosCountdown, setSosCountdown] = useState<number | null>(null); // null = idle, else seconds left
  const sosViaRef = useRef<'manual' | 'shake'>('manual');
  const [shakeEnabled, setShakeEnabled] = useState(() => localStorage.getItem('shakeSosEnabled') === '1');
  const [showVehicleInfo, setShowVehicleInfo] = useState<string | null>(null);

  // Phone back button (Android hardware/gesture back): close whatever overlay
  // is on top first (components below push onto the shared stack - see
  // lib/backHandler), otherwise step the tab navigation back to home, and
  // only minimize the app once there's nowhere left to go back to. This app
  // previously had NO back-button handling at all, so on Android the
  // hardware back button did nothing useful from inside any of these
  // overlays - each one now closes exactly the way its own visible
  // close/cancel/backdrop-tap already does, so back behaves the same as
  // tapping out.
  useEffect(() => {
    if (searchFocused) return pushBackHandler(() => {
      setSearchFocused(false);
      setDropoffSuggestions([]);
      setPickupSuggestions([]);
      setAddStopMode(false);
      setEditDropoffMode(false);
      setActiveSearchField('dropoff');
    });
  }, [searchFocused]);
  useEffect(() => {
    if (sidebarOpen) return pushBackHandler(() => setSidebarOpen(false));
  }, [sidebarOpen]);
  useEffect(() => {
    if (showPromotions) return pushBackHandler(() => setShowPromotions(false));
  }, [showPromotions]);
  useEffect(() => {
    if (showVehicleInfo) return pushBackHandler(() => setShowVehicleInfo(null));
  }, [showVehicleInfo]);
  useEffect(() => {
    if (showDriverDetail) return pushBackHandler(() => setShowDriverDetail(false));
  }, [showDriverDetail]);
  useEffect(() => {
    if (showChat) return pushBackHandler(() => setShowChat(false));
  }, [showChat]);
  useEffect(() => {
    if (pinMode) return pushBackHandler(() => setPinMode(false));
  }, [pinMode]);
  useEffect(() => {
    if (showCancelModal) return pushBackHandler(() => setShowCancelModal(false));
  }, [showCancelModal]);
  useEffect(() => {
    if (noDriversModal) return pushBackHandler(() => setNoDriversModal(false));
  }, [noDriversModal]);
  useEffect(() => {
    if (showConfirm) return pushBackHandler(() => {
      setShowConfirm(false);
      setScheduleFor('');
      setAppliedDiscount(0);
      setDiscountCode('');
      setDiscountMsg('');
    });
  }, [showConfirm]);

  useEffect(() => {
    let removeListener: (() => void) | undefined;
    let cancelled = false;
    (async () => {
      try {
        const { App } = await import('@capacitor/app');
        const handle = await App.addListener('backButton', () => {
          if (consumeBack()) return;
          setActiveTab((current) => {
            if (current !== 'home') return 'home';
            App.minimizeApp().catch(() => {});
            return current;
          });
        });
        if (cancelled) { handle.remove(); return; }
        removeListener = () => handle.remove();
      } catch { /* not running in the native shell */ }
    })();
    return () => removeListener?.();
  }, []);

  // One file per RideType, named after the id - the old set was named by artwork
  // rather than by ride, which is how 'safari-xl' ended up pointing at a file
  // called "Safari Comfort.png".
  const rideIconMap: Record<string, string> = {
    'motorbike': '/icons/rides/motorbike.png',
    'safari-electric': '/icons/rides/safari-electric.png',
    'safari-basic': '/icons/rides/safari-basic.png',
    'safari-send-bike': '/icons/rides/safari-send-bike.png',
    'safari-send-car': '/icons/rides/safari-send-car.png',
    'safari-women': '/icons/rides/safari-women.png',
    'safari-comfort': '/icons/rides/safari-comfort.png',
    'safari-xl': '/icons/rides/safari-xl.png',
  };

  useEffect(() => { assignedDriverRef.current = assignedDriver; }, [assignedDriver]);
  useEffect(() => { pickupRef.current = pickup; }, [pickup]);
  useEffect(() => { selectedRideRef.current = selectedRide || ''; }, [selectedRide]);
  useEffect(() => { rideStatusRef.current = rideStatus; }, [rideStatus]);

  // Cold-start splash ripple sweeps the screen once, then unmounts.
  useEffect(() => {
    const t = setTimeout(() => setShowSplash(false), 1400);
    return () => clearTimeout(t);
  }, []);

  // Ref to the map instance for imperative pan/zoom operations
  const mapRef = useRef<google.maps.Map | null>(null);
  // Whether the user has manually zoomed - prevents auto-fitBounds from re-firing
  const userZoomedRef = useRef(false);
  // Same signal as userZoomedRef, but as state so the recenter button can react to
  // it (a ref alone doesn't trigger a re-render) - shown only once the user has
  // actually panned away from center, hidden again once they recenter.
  const [mapPanned, setMapPanned] = useState(false);
  // Ref for the search input so we can focus it after the slide-up animation
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  // False once the customer manually sets a pickup (pin-drop or search), so the
  // 2s live-location follower below stops overwriting their chosen pickup point.
  const followMyLocationRef = useRef(true);
  // Live-measured height of the bottom search sheet (ResizeObserver, tracks its
  // spring animation in real time) - the recenter button and discount badge sit
  // exactly at this height instead of guessing a dvh value, which always left
  // either a gap or an overlap since the sheet's real height (especially its
  // content-driven 'auto' state) never matches a fixed percentage precisely.
  //
  // Callback ref, not useRef + a mount-only effect: the whole sheet is hidden
  // while recovering an active ride on app open (navigationMode &&
  // activeRideId), so on that path the ref would still be null when a
  // one-time effect ran - permanently stuck at 0 for the rest of the
  // session, even after the ride finishes and the idle sheet reappears. A
  // callback ref re-fires every time the target actually (un)mounts.
  const [sheetHeightPx, setSheetHeightPx] = useState(0);
  // Bottom inset in real pixels (Android nav bar / iOS home indicator). The sheet
  // reserves it as padding, which sits OUTSIDE the measured content heights below,
  // so every height target has to add it back - otherwise the sheet is exactly one
  // nav bar too short and its last control ("Find rides", "Where to?") falls below
  // the fold, needing a scroll to reach.
  const { bottom: safeBottomPx } = useSafeAreaInsets();
  // Mirrors the 24px floor in --sai-dock, so the sheet's measured height and its
  // actual padding-bottom can never disagree - they have to move together or the
  // sheet clips its own pinned footer.
  const sheetBottomPx = Math.max(safeBottomPx, 24);
  const sheetObsRef = useRef<ResizeObserver | null>(null);
  const sheetRefCb = useCallback((el: HTMLDivElement | null) => {
    sheetObsRef.current?.disconnect();
    sheetObsRef.current = null;
    if (el) {
      setSheetHeightPx(el.offsetHeight);
      const ro = new ResizeObserver((entries) => {
        const h = entries[0]?.contentRect?.height;
        if (typeof h === 'number') setSheetHeightPx(h);
      });
      ro.observe(el);
      sheetObsRef.current = ro;
    }
  }, []);
  // Live-measured height of the Active Ride Status Panel's own content
  // (collapsed one-liner or the full expanded driver card) - expanded used to
  // be a flat 75vh with its own overflow-y-auto, i.e. a scrollbar nested
  // inside the panel. Sizing to the real content instead (same min(dvh,
  // content+buffer) pattern as the sheet below) means swiping up grows the
  // panel to fit everything directly, no separate inner scroll competing
  // with the swipe gesture.
  const [trackingContentHeightPx, setTrackingContentHeightPx] = useState(0);
  const trackingContentObsRef = useRef<ResizeObserver | null>(null);
  const trackingContentRefCb = useCallback((el: HTMLDivElement | null) => {
    trackingContentObsRef.current?.disconnect();
    trackingContentObsRef.current = null;
    if (el) {
      setTrackingContentHeightPx(el.offsetHeight);
      const ro = new ResizeObserver((entries) => {
        const h = entries[0]?.contentRect?.height;
        if (typeof h === 'number') setTrackingContentHeightPx(h);
      });
      ro.observe(el);
      trackingContentObsRef.current = ro;
    }
  }, []);
  // Live height of the WHOLE bottom stack while tracking - the no-waiting-zone
  // banner plus the ride panel. The panel alone isn't enough to anchor the
  // Safety and recentre buttons against: the banner adds ~46px when it's there
  // and much more when it's expanded, and either way the buttons have to sit
  // clear of it. offsetHeight rather than contentRect, because the panel's
  // padding-bottom (the phone's nav-bar inset) is part of what covers the map.
  const [trackingStackHeightPx, setTrackingStackHeightPx] = useState(0);
  const trackingStackObsRef = useRef<ResizeObserver | null>(null);
  const trackingStackRefCb = useCallback((el: HTMLDivElement | null) => {
    trackingStackObsRef.current?.disconnect();
    trackingStackObsRef.current = null;
    if (!el) { setTrackingStackHeightPx(0); return; }
    setTrackingStackHeightPx(el.offsetHeight);
    const ro = new ResizeObserver(() => setTrackingStackHeightPx(el.offsetHeight));
    ro.observe(el);
    trackingStackObsRef.current = ro;
  }, []);
  // The map div spans the full screen behind the sheet (absolute inset-0), so
  // centering it on the user's true coordinates puts their "you are here"
  // dot at the geometric middle of the WHOLE screen - which is really the
  // middle of the strip that's still hidden behind the sheet, not the visible
  // map area above it. Shifts the map's actual center south by half the
  // sheet's live-measured height (converted from screen pixels to degrees of
  // latitude at the map's current zoom via the standard Web Mercator
  // meters-per-pixel formula), so the marker itself - still rendered at its
  // real, unshifted lat/lng - projects to the middle of the space that's
  // actually visible. Only the `center` passed to <GoogleMap> moves; `pickup`
  // keeps the true coordinates everywhere else (fare calc, backend, markers).
  const getVisuallyCenteredMapPoint = useCallback((lat: number, lng: number) => {
    const coveredPx = sheetHeightPx || 0;
    if (!coveredPx) return { lat, lng };
    const zoom = mapRef.current?.getZoom() ?? 17;
    const metersPerPixel = (156543.03392 * Math.cos((lat * Math.PI) / 180)) / Math.pow(2, zoom);
    const shiftLat = ((coveredPx / 2) * metersPerPixel) / 111320;
    return { lat: lat - shiftLat, lng };
  }, [sheetHeightPx]);
  // ...and keep that framing true while the sheet is being swiped. The map is a
  // full-screen div behind the sheet, so growing the sheet eats the map from the
  // bottom: the visible strip's middle climbs by half of whatever the sheet just
  // gained, and the "you are here" marker - which the code above deliberately
  // parks at that middle - slides down behind the sheet and out of sight. Swiping
  // "Where to?" up now pans the map up with it (and back down on collapse) by
  // exactly half the height change, so the marker holds its place in the visible
  // area throughout the gesture instead of only being right at the two extremes.
  //
  // panBy, not panTo(getVisuallyCenteredMapPoint(...)): it's relative, so it
  // re-frames whatever the customer is currently looking at rather than yanking
  // the map back to the pickup pin if they'd panned somewhere else. It also fires
  // neither onDragStart nor onZoomChanged, so this can't trip the "user moved the
  // map" flags that reveal the recenter button.
  //
  // sheetHeightPx is the live ResizeObserver measurement, so this runs per frame
  // of the sheet's spring animation with small deltas - the pan tracks the sheet
  // rather than jumping once at the end. contentRect excludes the sheet's own
  // padding, but that padding is constant, so the frame-to-frame delta is exact.
  const prevSheetHeightRef = useRef(0);
  useEffect(() => {
    const prev = prevSheetHeightRef.current;
    prevSheetHeightRef.current = sheetHeightPx;
    // First measurement (0 -> real height) is already accounted for by the
    // initial getVisuallyCenteredMapPoint centering - compensating again here
    // would double it and throw the marker off the top of the screen.
    if (!prev || !sheetHeightPx) return;
    const delta = sheetHeightPx - prev;
    if (Math.abs(delta) < 1) return;
    mapRef.current?.panBy(0, delta / 2);
  }, [sheetHeightPx]);
  // Live-measured height of the idle "Where would you like to go?" content
  // (heading + search launcher + Recent) - the expanded sheet sizes to this
  // instead of a flat 88dvh guess, which used to leave a lot of empty white
  // space below once the service-tile carousel was removed and Recent got
  // capped at 3. min()'d against 70dvh in the actual height calc so a very
  // long Recent list (or tiny-screen wrapping) still can't push the sheet
  // past a sane cap. Callback ref for the same reason as sheetRefCb above.
  const [homeContentHeightPx, setHomeContentHeightPx] = useState(0);
  const homeContentObsRef = useRef<ResizeObserver | null>(null);
  const homeContentRefCb = useCallback((el: HTMLDivElement | null) => {
    homeContentObsRef.current?.disconnect();
    homeContentObsRef.current = null;
    if (el) {
      setHomeContentHeightPx(el.offsetHeight);
      const ro = new ResizeObserver((entries) => {
        const h = entries[0]?.contentRect?.height;
        if (typeof h === 'number') setHomeContentHeightPx(h);
      });
      ro.observe(el);
      homeContentObsRef.current = ro;
    }
  }, []);
  // Live-measured height of whichever content is currently showing once a
  // dropoff is picked - the trip-details form (pickup review, stops, Find
  // Rides) and the ride-options list are mutually exclusive, so the same ref
  // just gets reattached to whichever one is mounted. Previously both states
  // shared one flat 'min(72dvh, 620px)' height so tapping "Find rides" never
  // animated the sheet itself (only the inner content crossfaded) - that's
  // preserved here too (both still read this single measurement), but now
  // it's sized to real content instead of leaving a dark gap below whichever
  // one is actually shorter than 72dvh.
  //
  // A callback ref, not a plain useRef + mount-only effect: this element
  // doesn't exist at all until a dropoff is picked, so a one-time effect
  // (empty deps) that only runs at the PARENT's mount would find the ref
  // still null on a fresh page load and never set up the observer - leaving
  // formOrResultsHeightPx stuck at its 0 fallback (min(72dvh, 0+64px) =
  // 64px) for the rest of the session, clipping the sheet to a sliver with
  // Find Rides scrolled out of reach no matter how far down you scroll. A
  // callback ref re-fires every time the target actually (un)mounts.
  const [formOrResultsHeightPx, setFormOrResultsHeightPx] = useState(0);
  const formOrResultsObsRef = useRef<ResizeObserver | null>(null);
  const formOrResultsRefCb = useCallback((el: HTMLDivElement | null) => {
    formOrResultsObsRef.current?.disconnect();
    formOrResultsObsRef.current = null;
    if (el) {
      setFormOrResultsHeightPx(el.offsetHeight);
      const ro = new ResizeObserver((entries) => {
        const h = entries[0]?.contentRect?.height;
        if (typeof h === 'number') setFormOrResultsHeightPx(h);
      });
      ro.observe(el);
      formOrResultsObsRef.current = ro;
    }
  }, []);

  const isMotorbikeType = (rideType: string) => {
    const rt = (rideType || '').toLowerCase();
    return rt === 'safari-electric' || rt === 'safari-send-bike' || rt === 'motorbike';
  };

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const avatarUrl = user.avatarUrl || localStorage.getItem('avatar') || null;

  // On mount: handle Paystack callback return
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const reference = params.get('reference') || params.get('trxref');
    const rideId = params.get('ride');
    const isScheduled = params.get('scheduled') === '1';
    const isWalletTopup = params.get('wallet_topup') === '1';

    // Wallet top-up returned → verify (which also credits the wallet server-side
    // now, not just the webhook - see backend payment.ts) then land on the
    // Wallet tab; Wallet.tsx's own mount effect re-fetches the now-updated balance.
    if (reference && isWalletTopup) {
      window.history.replaceState({}, '', window.location.pathname);
      walletService.verifyTopUp(reference).catch(() => {});
      setActiveTab('wallet');
      return;
    }

    // Scheduled-trip prepayment returned → create the scheduled trip
    if (reference && isScheduled) {
      window.history.replaceState({}, '', window.location.pathname);
      const stashed = localStorage.getItem('pendingScheduledTrip');
      localStorage.removeItem('pendingScheduledTrip');
      if (stashed) {
        const token = localStorage.getItem('accessToken');
        fetch(`${API_URL}/rides/schedule`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ ...JSON.parse(stashed), reference }),
        }).then(r => r.json()).then(d => {
          if (d?.id) alert('Your trip is scheduled and prepaid! A driver will be dispatched at the set time.');
          else alert('Scheduled trip could not be saved: ' + (d?.error || 'unknown error'));
        }).catch(() => alert('Could not save scheduled trip'));
      }
      return;
    }
    if (!reference) return;

    // Coming back from Paystack - clear everything immediately, synchronously
    window.history.replaceState({}, '', window.location.pathname);
    localStorage.removeItem('pendingCardPaymentRide');
    const token = localStorage.getItem('accessToken');

    if (rideId) {
      // Mark this ride as paid in localStorage so polling never re-triggers
      localStorage.setItem('paidRide', rideId);
      fetch(`${API_URL}/rides/${rideId}/pay`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ reference }),
      }).finally(() => { setShowRating(true); });
    } else {
      setShowRating(true);
    }
  }, []); // eslint-disable-line

  useEffect(() => { showRatingRef.current = showRating; }, [showRating]);
  useEffect(() => { activeRideIdRef.current = activeRideId; }, [activeRideId]);

  // RECOVERY: restore an active ride lost on reload/background - runs on mount,
  // on app re-focus, and as a safety poll, so no manual refresh is needed.
  const recoverActiveRide = useCallback(() => {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    if (!u.id) return;
    const token = localStorage.getItem('accessToken');
    fetch(`${API_URL}/rides/rider-active/${u.id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        // Invalid/expired token → clear session and send to login
        if (r.status === 401) {
          clearAuthSession();
          window.location.href = '/login';
          return null;
        }
        return r.json();
      })
      .then(active => {
        if (active && active.id) {
          setActiveRideId(active.id);
          setRideStatus(active.status);
          setNavigationMode(true);
          if (active.driver_id) setAssignedDriverId(active.driver_id);
          if (active.driver) setAssignedDriver((prev: any) => ({
            ...prev, ...active.driver,
            driver_vehicles: active.driver.driver_vehicles?.length ? active.driver.driver_vehicles : prev?.driver_vehicles,
          }));
          if (active.pickup_lat) setPickup({ lat: active.pickup_lat, lng: active.pickup_lng, address: active.pickup_address });
          if (active.dropoff_lat) setDropoff({ lat: active.dropoff_lat, lng: active.dropoff_lng, address: active.dropoff_address });
          // Stops too, or a reload mid-trip drops every stop off "My destination"
          // while the driver is still routing through them.
          applyServerStops(active.stops);
          return;
        }
        // No active ride - but if the driver ended a trip while we were away (no
        // data / app closed), we still owe a rating. Re-surface it on return so
        // the customer rates before doing anything else; once rated the server
        // returns null and it never asks again.
        // Don't surface a recovered rating while we're already showing one or
        // already handling a ride (e.g. an in-session completion) - that's what
        // made it re-pop/flip wording.
        if (showRatingRef.current || activeRideIdRef.current) return;
        fetch(`${API_URL}/rides/rider-last-unrated/${u.id}`, { headers: { Authorization: `Bearer ${token}` } })
          .then(r => (r.ok ? r.json() : null))
          .then((trip: any) => {
            if (!trip || !trip.id) return;
            // Fire ONCE per trip - guarded by an in-session ref AND a persisted
            // list, so it never comes back after rating or after a reload.
            if (ratingPromptedRef.current === trip.id || isTripHandled(trip.id)) return;
            // A card trip awaiting payment has its own recovery (pay first, then
            // rate) - don't let the rating prompt jump ahead of it.
            if (localStorage.getItem('pendingCardPaymentRide') === trip.id) return;
            ratingPromptedRef.current = trip.id;
            setActiveRideId(trip.id);
            setAssignedDriverId(trip.driver_id || '');
            if (trip.driver) setAssignedDriver((prev: any) => ({ ...prev, ...trip.driver }));
            // The fare must survive an app restart: bookedRideRef is gone by now,
            // so the recovered pay screen reads pendingPaymentPrice (never KES 0).
            if (trip.price != null) setPendingPaymentPrice(Number(trip.price));
            setRideStatus('completed');
            setSelectedRating(0);
            setRatingComment('');
            setRatingDone(false);
            setIsRecoveredRating(true);
            setShowRating(true);
          }).catch(() => {});
      }).catch(() => {});
    // applyServerStops is declared below this hook and only ever calls setState,
    // so it stays out of the deps - naming it here would evaluate the identifier
    // in the dependency array during render, before its `const` is initialised.
  }, []); // eslint-disable-line

  useEffect(() => {
    recoverActiveRide();
    const onVisible = () => { if (document.visibilityState === 'visible') recoverActiveRide(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', recoverActiveRide);
    // Active-ride recovery dead-man's switch. The 3s status poll below is the
    // primary updater; no Supabase Realtime WebSocket is held for idle riders.
    const interval = setInterval(() => { if (!document.hidden) recoverActiveRide(); }, 20000);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', recoverActiveRide);
      clearInterval(interval);
    };
  }, [recoverActiveRide]);

  // NOTE: there used to be an auto-cancel here - if the customer backgrounded the
  // app or lost the page for 15s while a ride was still 'searching'/'pending', the
  // app cancelled their request as "customer_went_offline". That's the wrong read
  // of going offline: riders routinely lock the phone and walk out of the house to
  // meet the driver, switch apps, or drop off mobile data for a minute - and lost
  // the ride they were waiting for, for doing so. Backgrounding is not abandoning.
  //
  // Nothing replaces it on the client. A request that really is abandoned is
  // already handled server-side: dispatch (services/dispatch.ts) gives each search
  // a fixed budget and calls markNoDrivers once it's spent, so an unanswered
  // request ends itself instead of sitting in drivers' lists forever. Cancelling
  // is now only ever something the rider does deliberately.

  // Recovery: if the customer force-closed while a card payment was pending,
  // re-fetch the ride price and resume the payment screen on next launch.
  useEffect(() => {
    const pendingRideId = localStorage.getItem('pendingCardPaymentRide');
    if (!pendingRideId) return;
    const alreadyPaid = localStorage.getItem('paidRide') === pendingRideId;
    if (alreadyPaid) { localStorage.removeItem('pendingCardPaymentRide'); return; }
    const token = localStorage.getItem('accessToken');
    fetch(`${API_URL}/rides/${pendingRideId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then((ride: any) => {
        if (!ride?.price) return;
        setPendingPaymentRideId(pendingRideId);
        setPendingPaymentPrice(ride.price);
        setActiveRideId(pendingRideId);
        setRideStatus('completed');
        setShowPayment(true);
      }).catch(() => {});
  }, []);

  // While queued, poll for a match → transition into the normal ride flow
  useEffect(() => {
    if (!inQueue) return;
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    const token = localStorage.getItem('accessToken');
    const poll = setInterval(async () => {
      try {
        const res = await fetch(`${API_URL}/rides/queue/${u.id}`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        if (data.status === 'matched' && data.ride_id) {
          setInQueue(false);
          setActiveRideId(data.ride_id);
          setRideStatus('pending');
          setNavigationMode(true);
          if (u.paymentMethod === 'card') localStorage.setItem('pendingCardPaymentRide', data.ride_id);
        } else if (data.status === 'waiting') {
          setQueuePosition(data.position || 1);
        } else if (data.status === 'none') {
          setInQueue(false);
        }
      } catch {}
    }, 8000);
    return () => clearInterval(poll);
  }, [inQueue]);

  useEffect(() => {
    if (!navigator.geolocation) { setLocatingUser(false); setLocationFailed(true); return; }
    const onSuccess = async (pos: GeolocationPosition) => {
      const { latitude, longitude } = pos.coords;
      setMapCenter(getVisuallyCenteredMapPoint(latitude, longitude));
      const addr = await reverseGeocode(latitude, longitude);
      const loc = { lat: latitude, lng: longitude, address: addr };
      setPickup(loc);
      setPickupQuery(addr || 'Current Location');
      setLocationFailed(false);
      setLocatingUser(false);
    };
    // High-accuracy GPS often times out (indoors / some iPhones). If it fails,
    // retry once with coarse/network location before giving up - and on final
    // failure surface a manual pickup entry instead of a stuck "Detecting…".
    const tryLocate = (highAccuracy: boolean) => {
      navigator.geolocation.getCurrentPosition(
        onSuccess,
        () => {
          if (highAccuracy) { tryLocate(false); return; }
          setLocatingUser(false);
          setLocationFailed(true);
        },
        { timeout: highAccuracy ? 10000 : 15000, enableHighAccuracy: highAccuracy },
      );
    };
    tryLocate(true);
  }, []);

  // Keep "my location" live on the idle map: re-fetch GPS every 2s and update
  // both the current-location marker and the map, like the driver app's home
  // map. Only runs on the true idle view (no active ride, not mid pin-drop/search)
  // and stops touching pickup the moment the customer manually sets one (pin-drop
  // or a search suggestion) via followMyLocationRef. Address isn't re-geocoded
  // every tick (that would hammer the geocoder) - only the coordinates move.
  useEffect(() => {
    if (!navigator.geolocation) return;
    if (rideStatus !== '' || pinMode || searchFocused) return;
    const tick = () => {
      if (!followMyLocationRef.current || document.hidden) return;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (!followMyLocationRef.current) return;
          const { latitude, longitude } = pos.coords;
          setPickup((prev) => (prev ? { ...prev, lat: latitude, lng: longitude } : prev));
          const visualCenter = getVisuallyCenteredMapPoint(latitude, longitude);
          setMapCenter(visualCenter);
          if (mapRef.current && !userZoomedRef.current) mapRef.current.panTo(visualCenter);
        },
        () => {},
        { enableHighAccuracy: true, maximumAge: 4000, timeout: 8000 },
      );
    };
    const interval = setInterval(tick, 2000);
    return () => clearInterval(interval);
  }, [rideStatus, pinMode, searchFocused]);

  // Inject Places service into mapService once Google Maps is ready
  useEffect(() => {
    if (!isLoaded) return;
    try {
      const svc = new (window as any).google.maps.places.AutocompleteService();
      initPlacesService(svc);
    } catch {}
  }, [isLoaded]);

  // Auto-fetch nearby drivers as soon as GPS is known, keep polling every 5s
  useEffect(() => {
    if (!pickup || navigationMode) return;
    fetchNearbyDrivers(pickup.lat, pickup.lng);
    const interval = setInterval(() => {
      if (document.hidden) return; // pause polling while the app is backgrounded
      fetchNearbyDrivers(pickup.lat, pickup.lng);
    }, 10000);
    return () => clearInterval(interval);
  }, [pickup?.lat, pickup?.lng, navigationMode]);

  // Draw the booking route polyline (pickup → dropoff PREVIEW). Hidden once a
  // driver is assigned - from then on the map shows driver→pickup, then (after the
  // trip starts) the destination route.
  useEffect(() => {
    if (!map || !directions?.routes?.[0]?.overview_path) return;
    if (TRACKING_STATUSES.includes(rideStatus)) return;

    // Same gold-over-dark-gold treatment as the live trip route, so the preview
    // a rider sees before booking and the line they follow afterwards are the
    // same line. It used to be Google-blue, which on the white basemap read as a
    // different app's route.
    const casing = new google.maps.Polyline({
      path: directions.routes[0].overview_path,
      strokeColor: ROUTE_CASING, strokeWeight: 9, strokeOpacity: 1, zIndex: 2, map,
    });
    const core = new google.maps.Polyline({
      path: directions.routes[0].overview_path,
      strokeColor: ROUTE_CORE, strokeWeight: 6, strokeOpacity: 1, zIndex: 3, map,
    });

    return () => {
      casing.setMap(null);
      core.setMap(null);
    };
  }, [directions, map, rideStatus]);

  // Frame the preview route in the map that is ACTUALLY VISIBLE - the strip
  // above the ride-options sheet.
  //
  // This used to run inline the moment results arrived, with a hardcoded 350px
  // bottom inset. That sheet grows to 80dvh, which is roughly 680px on a modern
  // phone, so the route was being fitted into a band the sheet then sat on top
  // of: the line and the two end callouts were drawn correctly and hidden
  // underneath the ride list, which reads exactly like the feature not working.
  //
  // Depending on sheetHeightPx (a live ResizeObserver measurement) also makes
  // this self-debouncing: every frame of the sheet's spring animation re-runs
  // the effect and cancels the pending timeout, so the fit lands once, after the
  // sheet has stopped moving and its height is final.
  useEffect(() => {
    if (!map || rideResults.length === 0) return;
    // Respect a rider who has panned or zoomed - never snap the map back under
    // their fingers.
    if (userZoomedRef.current) return;
    const path = directions?.routes?.[0]?.overview_path;
    if (!path || path.length < 2) return;

    const id = setTimeout(() => {
      const b = new google.maps.LatLngBounds();
      path.forEach((p: any) => b.extend(p));
      // The endpoints too: a route can start or end a few metres off its own
      // polyline, and these are where the callouts hang.
      if (pickup) b.extend({ lat: pickup.lat, lng: pickup.lng });
      if (dropoff) b.extend({ lat: dropoff.lat, lng: dropoff.lng });

      // Insets have to be CLAMPED against the real map height, not just derived
      // from the sheet. This sheet auto-expands to 80dvh the moment results
      // arrive - ~680px on a normal phone - so "sheet height + margin" as a
      // bottom inset came to more than the map itself once the top inset was
      // added. fitBounds given padding that nearly fills its container doesn't
      // clip gracefully, it produces a nonsense zoom, which is why the route and
      // both callouts vanished entirely instead of merely sitting too low.
      const mapH = map.getDiv()?.clientHeight || 0;
      const sheetH = Math.round(sheetHeightPx || 350);
      const strip = Math.max(0, mapH - sheetH);   // map you can actually see
      const MIN_BAND = 70;                        // the route needs somewhere to live

      // Headroom for a callout, which hangs ABOVE its marker and is ~84px tall
      // with its tail. On a small phone the whole visible strip is only ~130px,
      // so a fixed 84 would leave nothing for the route itself - take a share of
      // the strip instead, and never less than a marker's own height.
      const top = strip > 0 ? Math.min(84, Math.max(28, Math.round(strip * 0.4))) : 84;
      let bottom = sheetH + 8;
      if (mapH > 0 && mapH - top - bottom < MIN_BAND) {
        bottom = Math.max(0, mapH - top - MIN_BAND);
      }
      map.fitBounds(b, { top, bottom, left: 44, right: 44 });
    }, 260);
    return () => clearTimeout(id);
  }, [map, rideResults.length, directions, sheetHeightPx, pickup, dropoff]);

  const isBikeType = (rideType: string) => {
    const rt = (rideType || '').toLowerCase();
    return rt.includes('motorbike') || rt.includes('electric') || rt.includes('send-bike');
  };

  const getDriverIcon = (rideType: string) => ({
    url: isBikeType(rideType) ? '/icons/bike.png' : '/icons/car.png',
    scaledSize: new google.maps.Size(56, 56),
    anchor: new google.maps.Point(28, 28),
  });

  // Same gold/orange as the pin-drop mode's marker (the "Drag the map to
  // position the pin" pin) instead of Google's default red teardrop - used for
  // the dropoff/destination marker so every pin on the map matches the app's
  // branding rather than one of them standing out in Google's stock red.
  const getOrangePinIcon = () => ({
    path: 'M12 0C7.31 0 3.5 3.81 3.5 8.5c0 6.5 8.5 15.5 8.5 15.5s8.5-9 8.5-15.5C20.5 3.81 16.69 0 12 0zm0 11.5a3 3 0 1 1 0-6 3 3 0 0 1 0 6z',
    fillColor: '#F5A623',
    fillOpacity: 1,
    // Dark-gold outline, not white: the pin sits on a white basemap now, and a
    // white stroke on white erases its edge entirely. Matches the route casing.
    strokeColor: ROUTE_CASING,
    strokeWeight: 1.5,
    scale: 1.6,
    anchor: new google.maps.Point(12, 24),
  });

  // google.maps.Marker can't rotate a raster icon, so we pre-bake each vehicle
  // PNG into a base64 data URL (via canvas) and inline it inside a rotatable SVG.
  //
  // The bake also recolours the artwork to SafariCab gold. Both source PNGs ship
  // green - which is Bolt's colour, not ours, and it's the single most-looked-at
  // pixel in the app while a rider waits. The tint is a `color` blend, not a
  // flat fill: that swaps hue and saturation while preserving luminance, so the
  // bike keeps its highlights, panel shading and black tyres/seat and simply
  // becomes a gold bike, rather than turning into a gold silhouette.
  const vehicleB64Ref = React.useRef<{ bike?: string; car?: string }>({});
  // State drives the fleet-marker effect (it re-runs on change); the ref is read
  // by the live-tracking effect, whose deps deliberately exclude everything
  // volatile so the assigned driver's marker is never torn down mid-ride.
  const [vehicleBakeTick, setVehicleBakeTick] = useState(0);
  const vehicleBakeTickRef = React.useRef(0);
  useEffect(() => {
    const bake = (src: string, key: 'bike' | 'car') => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const c = document.createElement('canvas');
          c.width = img.naturalWidth; c.height = img.naturalHeight;
          const ctx = c.getContext('2d');
          if (!ctx) return;
          ctx.drawImage(img, 0, 0);
          // 'color' composites source hue+saturation onto destination luminance.
          ctx.globalCompositeOperation = 'color';
          ctx.fillStyle = '#F5A623';
          ctx.fillRect(0, 0, c.width, c.height);
          // fillRect paints the full canvas, including the transparent margin
          // around the vehicle - clip back to the artwork's own alpha or the
          // marker becomes a gold square.
          ctx.globalCompositeOperation = 'destination-in';
          ctx.drawImage(img, 0, 0);
          ctx.globalCompositeOperation = 'source-over';
          vehicleB64Ref.current[key] = c.toDataURL('image/png');
          vehicleBakeTickRef.current += 1;
          setVehicleBakeTick(vehicleBakeTickRef.current);
        } catch {}
      };
      img.src = src;
    };
    bake('/icons/bike.png', 'bike');
    bake('/icons/car.png', 'car');
  }, []);

  // Assigned driver's marker during an active ride: the top-down vehicle (bike or
  // car), tinted gold by the bake above and rotated to the driver's live heading
  // (0° = true north, clockwise), so it points the way they're actually moving
  // and swings as they turn. Falls back to the upright PNG until the bake lands.
  //
  // Icons are cached per (vehicle, 3° bucket). Smooth rotation re-sets the icon
  // several times a second, and rebuilding the SVG + re-encoding the base64 PNG
  // inside it every time is the expensive part - the marker only ever needs 120
  // distinct images, so build each one once and hand back the same object after.
  const iconCacheRef = React.useRef<Map<string, any>>(new Map());
  React.useEffect(() => { iconCacheRef.current.clear(); }, [vehicleBakeTick]);
  const ICON_ANGLE_STEP = 3;
  const getLiveDriverIcon = (h: number, rideType: string) => {
    const bike = isBikeType(rideType);
    const b64 = bike ? vehicleB64Ref.current.bike : vehicleB64Ref.current.car;
    if (!b64) return getDriverIcon(rideType);
    const bucket = ((Math.round(h / ICON_ANGLE_STEP) * ICON_ANGLE_STEP) % 360 + 360) % 360;
    const key = `${bike ? 'b' : 'c'}:${bucket}`;
    const hit = iconCacheRef.current.get(key);
    if (hit) return hit;
    const S = 68;
    const svg =
      `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${S}" height="${S}" viewBox="0 0 ${S} ${S}">` +
      `<g transform="rotate(${bucket} ${S / 2} ${S / 2})">` +
      `<image href="${b64}" xlink:href="${b64}" x="4" y="4" width="${S - 8}" height="${S - 8}" preserveAspectRatio="xMidYMid meet"/>` +
      `</g></svg>`;
    const icon = {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
      scaledSize: new google.maps.Size(S, S),
      anchor: new google.maps.Point(S / 2, S / 2),
    };
    iconCacheRef.current.set(key, icon);
    return icon;
  };

  // Show nearby driver icons - update positions in place to avoid flicker
  useEffect(() => {
    if (!isLoaded || !map || navigationMode) {
      driverMarkersRef.current.forEach(m => m.setMap(null));
      driverMarkersRef.current.clear();
      return;
    }
    const currentIds = new Set(nearbyDrivers.map((d: any) => d.driver_id));
    // The gold bake finished after these markers were created, so they're still
    // showing the raw green PNG. Forget the applied headings, which forces the
    // "meaningful change" test below to re-set every icon exactly once.
    if (vehicleBakeTick !== fleetBakeTickRef.current) {
      fleetBakeTickRef.current = vehicleBakeTick;
      driverHeadingsRef.current.clear();
    }

    // Remove stale markers
    driverMarkersRef.current.forEach((marker, id) => {
      if (!currentIds.has(id)) { marker.setMap(null); driverMarkersRef.current.delete(id); driverHeadingsRef.current.delete(id); }
    });

    // Update or create markers
    nearbyDrivers.forEach((driver: any) => {
      const lat = Number(driver.current_lat ?? driver.lat);
      const lng = Number(driver.current_lng ?? driver.lng);
      if (!lat || !lng) return;
      const pos = { lat, lng };
      // Point each vehicle the way it's actually heading (real GPS bearing, 0° =
      // true north) instead of leaving the whole fleet upright/facing one way.
      const heading = Number(driver.heading) || 0;
      const rideType = driver.ride_type || '';
      const existing = driverMarkersRef.current.get(driver.driver_id);
      if (existing) {
        existing.setPosition(pos);
        // Re-rotate only on a meaningful heading change (or once the icon bake
        // finishes) so the marker never blinks each poll.
        const prevH = driverHeadingsRef.current.get(driver.driver_id);
        const dh = prevH == null ? 999 : Math.abs(((heading - prevH + 540) % 360) - 180);
        if (dh > 6) { existing.setIcon(getLiveDriverIcon(heading, rideType)); driverHeadingsRef.current.set(driver.driver_id, heading); }
      } else {
        const marker = new google.maps.Marker({
          position: pos,
          map,
          icon: getLiveDriverIcon(heading, rideType),
          title: driver.full_name || 'Driver',
          optimized: false,
          zIndex: 10,
        });
        driverMarkersRef.current.set(driver.driver_id, marker);
        driverHeadingsRef.current.set(driver.driver_id, heading);
      }
    });

    return () => {
      driverMarkersRef.current.forEach(m => m.setMap(null));
      driverMarkersRef.current.clear();
    };
  }, [nearbyDrivers, map, isLoaded, navigationMode, vehicleBakeTick]);

  // Keep the animator pointed at whichever route the driver is actually on: the
  // one to the pickup while they're coming, the one to the destination once the
  // trip starts. Flattened to plain {lat,lng} once here rather than calling
  // .lat()/.lng() on LatLng objects inside the animation loop, where the same
  // path is re-walked every frame.
  useEffect(() => {
    const src = rideStatus === 'in_progress' ? inProgressDirections : toPickupDirections;
    const path = src?.routes?.[0]?.overview_path;
    routePathRef.current = path && path.length > 1
      ? path.map((p: any) => ({ lat: p.lat(), lng: p.lng() }))
      : null;
  }, [toPickupDirections, inProgressDirections, rideStatus]);

  // Track assigned driver's live position during active ride. Deps are kept to the
  // stable identifiers only (id/ride/map) - rideStatus and the driver/pickup objects
  // are read through refs so a status transition or a poll's setAssignedDriver never
  // tears down & recreates the marker (that recreation was the blink).
  useEffect(() => {
    if (!isLoaded || !map || !assignedDriverId) {
      if (activeMarkerRef.current) { activeMarkerRef.current.setMap(null); activeMarkerRef.current = null; }
      return;
    }
    const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000') + '/api/v1';

    const poll = async () => {
      // Only track while the driver is on the way / on trip. When the ride ends,
      // drop the marker on the next tick (the effect also cleans up when the ride
      // id / driver clears).
      if (!TRACKING_STATUSES.includes(rideStatusRef.current)) {
        if (activeMarkerRef.current) { activeMarkerRef.current.setMap(null); activeMarkerRef.current = null; }
        return;
      }
      try {
        const token = localStorage.getItem('accessToken');
        // /live returns the driver's FRESH position from Redis (~8s) plus status,
        // vs /driver/location which reads the 55s-throttled driver_status row.
        const res = await fetch(`${API_URL}/rides/${activeRideId}/live`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (data?.driver_lat == null || data?.driver_lng == null) return;
        const pos = { lat: data.driver_lat, lng: data.driver_lng };
        const driverRideType = assignedDriverRef.current?.driver_vehicles?.[0]?.ride_type || selectedRideRef.current || '';

        const prev = lastRealPosRef.current;
        const moved = !prev || haversineMeters(prev, pos) > 2; // ignore GPS jitter

        if (!activeMarkerRef.current) {
          // First fix: place the marker outright, no animation to run from.
          const heading = data.heading != null ? Number(data.heading) : 0;
          renderPosRef.current = { ...pos }; // own copy - the animator mutates it
          renderHeadingRef.current = heading;
          targetHeadingRef.current = heading;
          queueRef.current = [];
          activeMarkerRef.current = new google.maps.Marker({
            position: pos, map,
            icon: getLiveDriverIcon(heading, driverRideType),
            title: assignedDriverRef.current?.full_name || 'Driver',
            zIndex: 999,
            optimized: false,
          });
          appliedHeadingRef.current = heading;
          activeBakeTickRef.current = vehicleBakeTickRef.current;
          lastMoveTsRef.current = performance.now();
          // Auto-focus the map on the approaching car AND the rider so they never
          // have to scroll to find it - done once when the driver first appears.
          if (!didFitDriverRef.current) {
            try {
              const b = new google.maps.LatLngBounds();
              b.extend(pos);
              const pk = pickupRef.current;
              if (pk) b.extend({ lat: pk.lat, lng: pk.lng });
              map.fitBounds(b, { top: 120, bottom: 360, left: 60, right: 60 });
              userZoomedRef.current = false;
              setMapPanned(false);
              didFitDriverRef.current = true;
            } catch {}
          }
        } else if (moved) {
          // Hand the new fix to the animator as a PATH to walk, not a point to
          // jump to. Where we have the route the driver is following, the walk
          // goes through that road's own corners, so the marker turns with the
          // bend instead of cutting the inside of it.
          const from = renderPosRef.current || pos;
          const route = routePathRef.current;
          let leg: LL[] = [];
          if (route && route.length > 1) {
            const pFrom = projectOnPath(from, route);
            const pTo = projectOnPath(pos, route);
            // Both fixes have to actually be ON the route (within 40m - enough
            // for GPS scatter and a wide dual carriageway, tight enough that a
            // driver who has genuinely left the route isn't dragged along it),
            // and the new one has to be further along it. If the driver
            // backtracks or detours, a straight line is the honest answer.
            const ON_ROUTE_M = 40;
            if (pFrom && pTo && pFrom.offM < ON_ROUTE_M && pTo.offM < ON_ROUTE_M && pTo.alongM > pFrom.alongM) {
              leg = pathBetween(pFrom, pTo, route);
            }
          }
          leg.push(pos);
          queueRef.current = leg;

          // How long the walk should take. Fixes arrive on their own irregular
          // rhythm (network, Redis freshness, a driver in a tunnel), so the
          // animator is told to finish just as the NEXT one is expected - a
          // rolling average of recent gaps rather than a constant. Land early
          // and the marker sits still; land late and the next fix cuts this
          // walk short. Either one is the stutter riders read as "buffering".
          const now = performance.now();
          const gap = now - lastMoveTsRef.current;
          lastMoveTsRef.current = now;
          // 60/40 in favour of history: one slow response shouldn't stretch the
          // next walk to a crawl, but a genuinely slower feed should be tracked.
          avgGapRef.current = avgGapRef.current ? avgGapRef.current * 0.6 + gap * 0.4 : gap;
          // Floor above the 2s poll so consecutive fixes overlap slightly rather
          // than leaving a dead frame between them; ceiling so a 30s GPS outage
          // doesn't leave the marker crawling for half a minute afterwards.
          queueDurationRef.current = Math.min(8000, Math.max(2200, avgGapRef.current * 1.15));
          queueStartedRef.current = now;
        }
        if (moved) lastRealPosRef.current = pos;

        // The gold bake finished after this marker was created - swap the green
        // PNG out on the next frame the animator draws.
        if (activeBakeTickRef.current !== vehicleBakeTickRef.current) {
          activeBakeTickRef.current = vehicleBakeTickRef.current;
          appliedHeadingRef.current = NaN; // force the icon comparison below to miss
        }

        setDriverLivePos(pos);
        const pk = pickupRef.current;
        if (rideStatusRef.current === 'accepted' && pk) {
          // Estimate seconds-to-pickup from the live straight-line distance
          // (×1.35 road factor) at ~25 km/h. Refreshed every poll; a 1s ticker
          // (below) counts it down smoothly in between.
          const meters = haversineMeters(pos, { lat: pk.lat, lng: pk.lng }) * 1.35;
          setPickupEtaSec(Math.round(meters / 6.94));
        }
      } catch {}
    };

    // ─── The animator ────────────────────────────────────────────────────────
    // One long-lived rAF loop for the whole ride, rather than a fresh
    // start→end tween per fix. That's the change that removes the stutter:
    // the old tween eased OUT into every fix, so the marker decelerated to a
    // standstill twice a second and re-launched - a gallop the eye reads as
    // buffering. This loop just walks the queue of waypoints at whatever
    // constant speed clears it by the deadline, so speed only changes when
    // the driver's speed actually changes.
    //
    // It also never extrapolates past the last known fix. Guessing ahead looks
    // smoother for a moment and then snaps back when the real fix disagrees,
    // which is more jarring than briefly holding still.
    let raf = 0;
    let lastFrame = performance.now();
    const tick = (now: number) => {
      raf = requestAnimationFrame(tick);
      const dt = Math.min(100, now - lastFrame); // cap after a backgrounded tab
      lastFrame = now;
      const marker = activeMarkerRef.current;
      const cur = renderPosRef.current;
      if (!marker || !cur) return;

      // `cur` IS renderPosRef.current and is mutated in place by advanceAlong -
      // the queue is walked many times a second, and allocating a fresh position
      // object per frame is garbage the animation doesn't need to make.
      const queue = queueRef.current;
      if (queue.length) {
        // Constant speed to arrive exactly on the deadline. Distance remaining
        // is measured ALONG the queued path, not as the crow flies - the
        // shortcut would make a route with corners in it crawl. Recomputed every
        // frame, so a dropped frame is absorbed by a fractionally higher speed
        // rather than pushing the whole walk late.
        const step = stepFor(cur, queue, dt, queueDurationRef.current - (now - queueStartedRef.current));
        const facing = advanceAlong(cur, queue, step);
        // Face the waypoint being walked towards. Because the queue follows the
        // road, this turns through a bend one vertex at a time instead of
        // pointing straight at a fix on the far side of it.
        if (facing != null) targetHeadingRef.current = facing;
        marker.setPosition({ lat: cur.lat, lng: cur.lng });
      }

      // Ease the drawn heading towards the target along the SHORT way round.
      // Rotation is deliberately slower than the movement (~150ms to close the
      // gap): a vehicle leans into a turn, it doesn't snap to it, and matching
      // the two exactly makes GPS scatter look like twitching.
      const turn = shortestTurn(renderHeadingRef.current, targetHeadingRef.current);
      if (Math.abs(turn) > 0.2) {
        renderHeadingRef.current = (renderHeadingRef.current + turn * Math.min(1, dt / 150) + 360) % 360;
      }
      // Only touch the icon when it would actually change bucket - setIcon is
      // the expensive call here, and the cache above means equal buckets return
      // the identical object anyway.
      const bucket = Math.round(renderHeadingRef.current / ICON_ANGLE_STEP);
      const appliedBucket = Math.round(appliedHeadingRef.current / ICON_ANGLE_STEP);
      if (!(bucket === appliedBucket)) {  // NaN-safe: forces a re-set after the gold bake
        const driverRideType = assignedDriverRef.current?.driver_vehicles?.[0]?.ride_type || selectedRideRef.current || '';
        marker.setIcon(getLiveDriverIcon(renderHeadingRef.current, driverRideType));
        appliedHeadingRef.current = renderHeadingRef.current;
      }
    };
    raf = requestAnimationFrame(tick);

    poll();
    // Poll the driver's live position every 2s; the animator above keeps motion
    // smooth and continuous even when server updates arrive less often.
    const interval = setInterval(() => { if (!document.hidden) poll(); }, 2000);
    return () => {
      clearInterval(interval);
      cancelAnimationFrame(raf);
      if (markerAnimRef.current) { cancelAnimationFrame(markerAnimRef.current); markerAnimRef.current = null; }
      lastRealPosRef.current = null;
      renderPosRef.current = null;
      queueRef.current = [];
      avgGapRef.current = 0;
      didFitDriverRef.current = false;
      if (activeMarkerRef.current) { activeMarkerRef.current.setMap(null); activeMarkerRef.current = null; }
    };
  }, [assignedDriverId, activeRideId, map, isLoaded]);

  // Count the pickup ETA down smoothly (1s) between the 3s location polls, and
  // clear it whenever the driver isn't on the way to pickup.
  useEffect(() => {
    if (rideStatus !== 'accepted') { setPickupEtaSec(null); return; }
    const t = setInterval(() => setPickupEtaSec((s) => (s == null ? s : Math.max(0, s - 1))), 1000);
    return () => clearInterval(t);
  }, [rideStatus]);


  const handleDropoffSearch = async (query: string) => {
    setDropoffQuery(query);
    if (query.length > 2) {
      try {
        const results = await searchLocation(query);
        // Show only the best 5 matches so the list stays focused.
        if (results.length > 0) setDropoffSuggestions(results.slice(0, 5));
      } catch (e) {}
    } else {
      setDropoffSuggestions([]);
    }
  };

  // Pickup search - only used when booking for someone else (type their address).
  const handlePickupSearch = async (query: string) => {
    setPickupQuery(query);
    if (query.length > 2) {
      try {
        const results = await searchLocation(query);
        if (results.length > 0) setPickupSuggestions(results);
      } catch {}
    } else {
      setPickupSuggestions([]);
    }
  };
  const selectPickup = async (loc: Location) => {
    const r = await resolveLocation(loc);
    followMyLocationRef.current = false;
    setPickup(r);
    setPickupQuery(r.address);
    setPickupSuggestions([]);
    setRideResults([]);
  };

  const addStop = () => {
    setStops(prev => [...prev, null]);
    setStopQueries(prev => [...prev, '']);
    setStopSuggestions(prev => [...prev, []]);
  };

  // Replaces the local stop arrays with the ride's stops as the SERVER has them -
  // used after a mid-trip add and on active-ride recovery, so the "My destination"
  // list shows the real route (and survives a reload) instead of only the stops
  // this session happened to add before booking.
  const applyServerStops = (raw: any) => {
    const valid = (Array.isArray(raw) ? raw : []).filter(
      (s: any) => typeof s?.lat === 'number' && typeof s?.lng === 'number',
    ) as Location[];
    setStops(valid);
    setStopQueries(valid.map((s: any) => s.address || ''));
    setStopSuggestions(valid.map(() => []));
  };

  const removeStop = (index: number) => {
    setStops(prev => prev.filter((_, i) => i !== index));
    setStopQueries(prev => prev.filter((_, i) => i !== index));
    setStopSuggestions(prev => prev.filter((_, i) => i !== index));
    setRideResults([]);
  };

  // Swaps pickup and dropoff - only meaningful once a destination is actually
  // chosen (nothing to swap into pickup otherwise). Disables the GPS live-
  // location follower, same as any other manual pickup set, so it doesn't
  // immediately overwrite the swapped-in value.
  const swapPickupDropoff = () => {
    // Only requires SOME text/location on the destination side, not a fully
    // resolved Location - a destination that's only been typed (not yet
    // picked from a suggestion) is still meaningful to swap, and left the
    // button looking permanently greyed out/broken for the very common case
    // of swapping before finishing a search.
    if (!dropoff && !dropoffQuery.trim()) return;
    followMyLocationRef.current = false;
    const oldPickup = pickup;
    const oldDropoff = dropoff;
    const oldDropoffQuery = dropoffQuery;
    setPickup(oldDropoff);
    setPickupQuery(oldDropoff ? ((oldDropoff as any).name || oldDropoff.address || '') : oldDropoffQuery);
    setDropoff(oldPickup);
    setDropoffQuery(oldPickup ? ((oldPickup as any).name || oldPickup.address || '') : pickupQuery);
    // Hand editing straight to the pickup box - that's the one that just
    // received the old destination and most likely needs a fresh location
    // typed into it next.
    setActiveSearchField('pickup');
    setTimeout(() => pickupSearchInputRef.current?.focus(), 0);
  };

  const handleStopSearch = async (index: number, query: string) => {
    setStopQueries(prev => { const n = [...prev]; n[index] = query; return n; });
    if (query.length > 2) {
      try {
        const results = await searchLocation(query);
        setStopSuggestions(prev => { const n = [...prev]; n[index] = results; return n; });
      } catch {}
    } else {
      setStopSuggestions(prev => { const n = [...prev]; n[index] = []; return n; });
    }
  };

  // Return the best display address for a location - prefixes the place name
  // (e.g. "Jamia Mosque") when it is not already in the address string, so
  // the driver's split(',')[0] always shows the recognisable place name.
  const namedAddress = (loc: Location | null): string => {
    if (!loc) return '';
    const name = (loc as any).name || '';
    if (name && !loc.address.toLowerCase().startsWith(name.toLowerCase())) {
      return `${name}, ${loc.address}`;
    }
    return loc.address;
  };

  // Resolve a Google Places suggestion to real lat/lng before using it.
  // If the result has a name (e.g. "BBS Mall") not already in the formatted
  // address, prepend it so the driver's split(',')[0] shows the right place.
  const resolveLocation = async (loc: Location): Promise<Location> => {
    if (loc.lat !== 0 || !loc.placeId) return loc;
    const resolved = await resolvePlaceId(loc.placeId);
    if (!resolved) return loc;
    const name = (loc as any).name || '';
    const address = name && !resolved.address.toLowerCase().startsWith(name.toLowerCase())
      ? `${name}, ${resolved.address}`
      : resolved.address;
    return { ...loc, lat: resolved.lat, lng: resolved.lng, address };
  };

  const selectStop = async (index: number, loc: Location) => {
    const resolved = await resolveLocation(loc);
    setStops(prev => { const n = [...prev]; n[index] = resolved; return n; });
    setStopQueries(prev => { const n = [...prev]; n[index] = resolved.address; return n; });
    setStopSuggestions(prev => { const n = [...prev]; n[index] = []; return n; });
  };

  // Single entry point for picking a place from the search screen. Handles three
  // modes: saving a Home/Work address, adding a mid-trip stop, or setting the
  // dropoff for a new booking.
  const selectPlace = async (loc: any) => {
    const r = await resolveLocation(loc);
    if (settingPlace) {
      // Append to the slot's list (up to 5), de-duping by address.
      const list = (savedPlaces[settingPlace] || []).filter((p: any) => p.address !== r.address);
      persistSavedPlaces({ ...savedPlaces, [settingPlace]: [...list, r].slice(-5) });
      setSettingPlace(null);
      setDropoffQuery('');
      setDropoffSuggestions([]);
      setSearchFocused(false);
      setActiveTab('places'); // back to the manager so they can add more / see it
      return;
    }
    if (addStopMode) {
      await addStopToTrip(r);
      saveRecentPlace(r);
      setAddStopMode(false);
      setDropoffSuggestions([]);
      setSearchFocused(false);
      return;
    }
    if (editDropoffMode) {
      await updateRideDropoff(r);
      saveRecentPlace(r);
      setEditDropoffMode(false);
      setDropoffSuggestions([]);
      setSearchFocused(false);
      return;
    }
    setDropoff(r);
    setDropoffQuery((r as any).name || r.address);
    setDropoffSuggestions([]);
    setSearchFocused(false);
    saveRecentPlace(r);
  };

  // Add a stop to the CURRENT active trip; alerts the driver + bumps the fare.
  const addStopToTrip = async (loc: Location) => {
    if (!activeRideId) return;
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/rides/${activeRideId}/add-stop`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ stop: { lat: loc.lat, lng: loc.lng, address: loc.address } }),
      });
      const data = await res.json();
      if (data?.success) {
        // Show the stop in the trip, not just in a toast that's gone in 6s. The
        // add-stop call only ever hit the server before, so "My destination" -
        // which renders local `stops` - stayed on pickup -> dropoff and the rider
        // had no lasting confirmation their stop was actually on the route.
        // Uses the server's authoritative array (it appends to whatever the ride
        // already had, including stops added from another device), keeping the
        // three parallel stop arrays in step the way addStop/removeStop do.
        applyServerStops(data.stops);
        setStopAddedMsg(`Stop added • new fare KES ${Number(data.price || 0).toLocaleString()}`);
        setTimeout(() => setStopAddedMsg(''), 6000);
      } else {
        setStopAddedMsg('Could not add stop. Please try again.');
        setTimeout(() => setStopAddedMsg(''), 4000);
      }
    } catch {
      setStopAddedMsg('Could not add stop. Please try again.');
      setTimeout(() => setStopAddedMsg(''), 4000);
    }
  };

  const updateRideDropoff = async (loc: Location) => {
    if (!activeRideId) return;
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/rides/${activeRideId}/dropoff`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ dropoff_address: loc.address, dropoff_lat: loc.lat, dropoff_lng: loc.lng }),
      });
      const data = await res.json();
      if (data?.success !== false) {
        setDropoffEditMsg('Drop-off updated - driver has been notified');
        setDropoff(loc);
      } else {
        setDropoffEditMsg('Could not update drop-off. Try again.');
      }
    } catch {
      setDropoffEditMsg('Could not update drop-off. Try again.');
    }
    setTimeout(() => setDropoffEditMsg(''), 5000);
  };

  const fetchNearbyDrivers = async (lat: number, lng: number) => {
    try {
      const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000') + '/api/v1';
      const token = localStorage.getItem('accessToken');
      // Cumulative online fleet (all drivers online, no radius cap) so the map
      // shows real supply around the city - dispatch handles the radius itself.
      const res = await fetch(`${API_URL}/driver/online`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setNearbyDrivers(data.drivers || data || []);
    } catch (e) { setNearbyDrivers([]); }
  };

  // STRICT vehicle-type match - a driver is only eligible for a request whose
  // ride_type EXACTLY matches their registered vehicle. No "send takes anyone",
  // no empty-type wildcard, no cross-type fallback. A motorbike request goes only
  // to motorbike drivers; a Safari Send request only to Safari Send drivers.
  const rideTypeMatches = (driverRideType: any, requested: any) => {
    const r = (requested ?? '').toString().trim().toLowerCase();
    const d = (driverRideType ?? '').toString().trim().toLowerCase();
    return r !== '' && d !== '' && d === r;
  };

  // A driver serves a request when its ride_type is their registered class OR is
  // one of the extra classes they've opted into (also_accept) - e.g. a motorbike
  // taking Safari Send, or a Safari Women car taking Safari Basic.
  const driverServes = (d: any, rideType: string | null) => {
    if (rideTypeMatches(d?.ride_type, rideType)) return true;
    const r = (rideType ?? '').toString().trim().toLowerCase();
    if (r === '' || !Array.isArray(d?.also_accept)) return false;
    return d.also_accept.some((t: any) => String(t).trim().toLowerCase() === r);
  };

  const hasDriverForRide = (rideType: string) => {
    if (nearbyDrivers.length === 0) return false;
    return nearbyDrivers.some((d: any) => driverServes(d, rideType));
  };

  const handleFindRides = async () => {
    if (!pickup || !dropoff) return;
    // Booking for someone else requires the passenger's name + phone first.
    if (bookingForOther && (!passengerName.trim() || !passengerPhone.trim())) {
      setPassengerError(true);
      return;
    }
    setPassengerError(false);
    // Safari Send requires both sender and receiver contact details - the
    // driver needs the receiver's phone to hand off the package.
    if (isSafariSend && (!senderName.trim() || !senderPhone.trim() || !receiverName.trim() || !receiverPhone.trim())) {
      setSendDetailsError(true);
      return;
    }
    setSendDetailsError(false);
    setIsLoading(true); setRideResults([]);
    if (pickup) fetchNearbyDrivers(pickup.lat, pickup.lng);
    
    // Get route from OSRM (include any stops as waypoints)
    const validStops = stops.filter(Boolean) as Location[];
    const results = await getRideOptions(pickup, dropoff, validStops).catch(() => []);
    setRideResults(results);
    // Default-select whichever ride type the customer tapped in from the home
    // tiles (Motorbike/Electric/Safari Send), if it's actually available in
    // these results, else just the first option - same as the reference
    // design always has one option pre-selected/highlighted.
    setSelectedRide(
      (preselectRideType && results.some(r => r.option.id === preselectRideType))
        ? preselectRideType
        : (results[0]?.option.id ?? null)
    );

    // Draw blue route line
    if (results.length > 0 && results[0].route.polyline) {
      try {
        let coords;
        try { coords = JSON.parse(results[0].route.polyline); } catch {
          // Google encoded polyline
          coords = google.maps.geometry.encoding.decodePath(results[0].route.polyline).map((p: any) => [p.lng(), p.lat()]);
        }
        const path = coords.map((c: number[]) => ({ lat: c[1], lng: c[0] }));
        setDirections({
          routes: [{ overview_path: path }],
          request: { origin: pickup, destination: dropoff, travelMode: 'DRIVING' as any },
        } as any);
        // Auto zoom to fit route with padding - skipped if the user has manually
        // adjusted the zoom so the map stays where they set it.
        // Framing is NOT done here any more - see the fit effect below, which
        // waits for the ride-options sheet to settle and then insets by its real
        // measured height. Fitting from here could only ever guess that height,
        // and the guess was 350px against a sheet that grows to 80dvh.
      } catch (e) { console.log('Route error:', e); }
    }
    setIsLoading(false);
  };

  const handleRequestRide = (rideType: RideType) => {
    setSelectedRide(rideType);
    // A Safari Send can't go to the confirm sheet without both contacts - the
    // driver has no way to hand the package over otherwise. handleFindRides
    // checks the same thing, but it runs before a ride type is even chosen, so on
    // the normal path this is the check that actually fires. Reads rideType
    // directly rather than isSafariSend: selectedRide is set in the line above
    // and won't be visible to this closure until the next render.
    const sendType = rideType === 'safari-send-bike' || rideType === 'safari-send-car';
    if (sendType && (!senderName.trim() || !senderPhone.trim() || !receiverName.trim() || !receiverPhone.trim())) {
      setSendDetailsError(true);
      return;
    }
    setSendDetailsError(false);
    setShowConfirm(true);
  };

  // Schedule a prepaid trip - pay via Paystack first, then create on callback
  const handleSchedulePay = async () => {
    if (!selectedRideData || !pickup || !dropoff || !scheduleFor) return;
    setBookingError('');
    setIsLoading(true);
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      const token = localStorage.getItem('accessToken');
      const finalFare = Math.max(0, selectedRideData.price.estimatedPrice - appliedDiscount);

      // Stash the trip so we can create it after Paystack returns
      localStorage.setItem('pendingScheduledTrip', JSON.stringify({
        pickup: { address: namedAddress(pickup), lat: pickup.lat, lng: pickup.lng },
        dropoff: { address: namedAddress(dropoff), lat: dropoff.lat, lng: dropoff.lng },
        stops: stops.filter(Boolean).map((s: any) => ({ address: namedAddress(s), lat: s.lat, lng: s.lng })),
        rideType: selectedRide,
        price: finalFare, distance: selectedRideData.price.distance, duration: selectedRideData.price.duration,
        scheduledFor: new Date(scheduleFor).toISOString(),
      }));

      const initRes = await fetch(`${API_URL}/payment/initialize`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          email: u.email || `${u.phone}@safaricab.app`,
          amount: finalFare,
          callback_url: `${window.location.origin}?scheduled=1`,
          metadata: { rider_id: u.id, reason: 'Scheduled Trip Prepayment' },
        }),
      });
      const initData = await initRes.json();
      if (initData.success && initData.authorization_url) {
        window.location.href = initData.authorization_url;
      } else {
        setBookingError(initData.message || 'Could not start payment');
        setIsLoading(false);
      }
    } catch {
      setBookingError('Could not start payment');
      setIsLoading(false);
    }
  };

  // Kick off a server-side broadcast search: create ONE 'searching' ride (no
  // driver picked here) and let the backend offer it to the nearest matching
  // drivers, expanding the radius each round until one accepts (first-accept-wins,
  // ── Corporate ───────────────────────────────────────────────────────────────
  // Checked when the rider switches the toggle on, so an unenrolled employee is
  // told immediately rather than after choosing a car.
  const checkCorporateEligibility = async () => {
    setCorporateChecking(true);
    try {
      const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000') + '/api/v1';
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/corporate/eligibility`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data?.eligible) {
        setCorporateInfo(data.company);
        setCorporateOn(true);
      } else {
        setCorporateOn(false);
        setCorporateInfo(null);
        setCorporateError(data?.message || 'You are not enabled for corporate rides with your company.');
      }
    } catch {
      setCorporateError('Could not check your corporate access. Please try again.');
    }
    setCorporateChecking(false);
  };

  // Holds the trip pending approval INSTEAD of dispatching. No driver is
  // disturbed until the company says yes, so nothing here touches ride state.
  const startCorporateApproval = async (priceData: { price: number; distance: number; duration: number }): Promise<void> => {
    if (!pickup || !dropoff || !selectedRide) throw new Error('Missing trip details. Try again.');
    // Refuse here rather than sending the approver a request they cannot judge.
    // One tap on a chip satisfies it, so this is a nudge, not a wall.
    if (!corporateReason.trim()) {
      throw new Error('Add a reason for this trip — your approver needs it to decide.');
    }
    const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000') + '/api/v1';
    const token = localStorage.getItem('accessToken');
    const res = await fetch(`${API_URL}/corporate/request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        pickup: { address: namedAddress(pickup), lat: pickup.lat, lng: pickup.lng },
        dropoff: { address: namedAddress(dropoff), lat: dropoff.lat, lng: dropoff.lng },
        stops: stops.filter(Boolean).map((s: any) => ({ address: namedAddress(s), lat: s.lat, lng: s.lng })),
        rideType: selectedRide,
        price: priceData.price,
        // Ask for a ticket rather than a car right now: same trip, same
        // approver, but she keeps the choice of when to actually travel.
        asTicket: corporateAsTicket || undefined,
        scheduledFor: corporateAsTicket && corporateTicketTime ? corporateTicketTime : undefined,
        reason: corporateReason.trim() || undefined,
      }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Could not send the approval request');

    bookedRideRef.current = priceData;
    setCorporateRequest({
      id: data.requestId,
      company: data.company?.name || corporateInfo?.name || 'your company',
      expiresAt: data.expiresAt,
      isTicket: !!data.isTicket,
    });
    setCorporateOutcome(null);
    setRideResults([]);
    // Back to the default for the next booking. Left sticky, a box she ticked
    // once would silently turn her next "book now" into a ticket - the two do
    // very different things and the choice has to be made deliberately each time.
    setCorporateAsTicket(false);
    setCorporateTicketTime('');
    setCorporateReason('');
  };

  // Poll the pending request. On approval the server has already created the ride
  // and kicked dispatch, so we just adopt its id and drop into the normal
  // searching-for-a-driver UI.
  useEffect(() => {
    if (!corporateRequest) return;
    const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000') + '/api/v1';
    let stopped = false;

    const tick = async () => {
      setCorporateSecsLeft(Math.max(0, Math.round((new Date(corporateRequest.expiresAt).getTime() - Date.now()) / 1000)));
      try {
        const token = localStorage.getItem('accessToken');
        const res = await fetch(`${API_URL}/corporate/request/${corporateRequest.id}`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        if (stopped) return;
        if (data?.status === 'approved' && data.rideId) {
          setCorporateRequest(null);
          setActiveRideId(data.rideId);
          setAssignedDriver(null);
          setAssignedDriverId('');
          setRideStatus('searching');
          setIsSearchingDriver(true);
          setNavigationMode(true);
        } else if (data?.status === 'ticket') {
          // Approved, but as a ticket - nobody is coming yet and she is not
          // waiting on a driver. Stop the countdown, tell her she has it, and
          // let the ticket card take over.
          setCorporateRequest(null);
          setCorporateTicketIssued({ code: data.ticketCode || null });
          setTicketsRefresh((n) => n + 1);
        } else if (data?.status === 'declined' || data?.status === 'expired') {
          setCorporateRequest(null);
          setCorporateOutcome(data.status);
        }
      } catch { /* transient - the next tick retries */ }
    };

    tick();
    const id = setInterval(tick, 3000);
    return () => { stopped = true; clearInterval(id); };
  }, [corporateRequest]);

  const cancelCorporateRequest = async () => {
    if (!corporateRequest) return;
    const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000') + '/api/v1';
    const token = localStorage.getItem('accessToken');
    fetch(`${API_URL}/corporate/request/${corporateRequest.id}/cancel`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {});
    setCorporateRequest(null);
    setCorporateOutcome(null);
  };

  // Declined or lapsed → fall back to the rider's own payment method, which is
  // the ordinary booking path with the corporate flag dropped.
  const bookWithoutCorporate = async () => {
    setCorporateOutcome(null);
    setCorporateOn(false);
    setCorporateInfo(null);
    const priceData = bookedRideRef.current;
    if (!priceData) return;
    try { await startBroadcastSearch(priceData); } catch { /* surfaced by the normal flow */ }
  };

  // strict ride_type - all enforced server-side). Reused for a fresh booking and
  // for re-search after a driver cancels an accepted trip.
  const startBroadcastSearch = async (priceData: { price: number; parkingFee?: number; distance: number; duration: number }): Promise<void> => {
    if (!pickup || !dropoff || !selectedRide) throw new Error('Missing trip details. Try again.');
    const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000') + '/api/v1';
    const token = localStorage.getItem('accessToken');
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    const res = await fetch(`${API_URL}/rides/request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        pickup: { address: namedAddress(pickup), lat: pickup.lat, lng: pickup.lng },
        dropoff: { address: namedAddress(dropoff), lat: dropoff.lat, lng: dropoff.lng },
        stops: stops.filter(Boolean).map((s: any) => ({ address: namedAddress(s), lat: s.lat, lng: s.lng })),
        rideType: selectedRide, ...priceData,
        // The fare ALONE. priceData.price is the parking-inclusive total (it has
        // to be, for the pay-the-driver screen), so it is unwound here - the
        // server re-derives the parking from its own copy of the zone and adds
        // it back. Sending the total would charge parking twice, and would do it
        // again on every re-search after a driver cancels.
        price: priceData.price - (priceData.parkingFee || 0),
        paymentMethod: u.paymentMethod || 'cash',
        passengerName: bookingForOther ? passengerName.trim() : undefined,
        passengerPhone: bookingForOther ? passengerPhone.trim() : undefined,
        senderName: isSafariSend ? senderName.trim() : undefined,
        senderPhone: isSafariSend ? senderPhone.trim() : undefined,
        receiverName: isSafariSend ? receiverName.trim() : undefined,
        receiverPhone: isSafariSend ? receiverPhone.trim() : undefined,
        discountCode: appliedDiscount > 0 && discountCode ? discountCode.trim() : undefined,
        // Only the CHOICE travels, never the amount - the server looks the fee up
        // from its own copy of the zone, so a tampered request can't name its own
        // parking figure. Read off priceData rather than the live component
        // state, so a re-search replays exactly what the rider agreed to.
        includeParking: (priceData.parkingFee || 0) > 0 ? true : undefined,
      }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to book ride');

    bookedRideRef.current = priceData;
    setActiveRideId(data.id);
    setAssignedDriver(null);
    setAssignedDriverId('');
    setRideStatus('searching');
    setIsSearchingDriver(true);
    setDriverDeclinedMsg('');
    setDeclinedReason('');
    setRatingDone(false);
    setShowRating(false);
    setSelectedRating(0);
    setRatingComment('');
    setNavigationMode(true);
    if (u.paymentMethod === 'card') localStorage.setItem('pendingCardPaymentRide', data.id);
    setRideResults([]);

    const ride = {
      id: data.id,
      date: new Date().toLocaleString(),
      pickup: pickup.address?.split(',')[0] || 'Pickup',
      dropoff: dropoff.address?.split(',')[0] || 'Dropoff',
      ...priceData,
      rideType: selectedRideData?.option.name,
      driver: 'Finding a driver…',
      rating: 0,
    };
    const history = JSON.parse(localStorage.getItem('rideHistory') || '[]');
    history.unshift(ride);
    localStorage.setItem('rideHistory', JSON.stringify(history.slice(0, 50)));
  };

  const handleConfirmRide = async () => {
    if (!selectedRideData || !pickup || !dropoff) return;
    setShowConfirm(false);
    setBookingError('');
    setIsLoading(true);
    setDriverPaidConfirmed(false);

    // `price` here is the TOTAL the rider owes, parking included - it's what the
    // "Pay KES X to your driver" screen reads, and shorting that by the parking
    // would leave the driver collecting less than they paid the barrier.
    // `parkingFee` rides along so the request below can subtract it back out:
    // the server adds parking itself from its own copy of the zone, so what goes
    // over the wire has to be the fare alone or it would be counted twice - once
    // on the first booking and again on a re-search after a driver cancels.
    const priceData = {
      price: Math.max(0, selectedRideData.price.estimatedPrice - appliedDiscount) + parkingFee,
      parkingFee,
      distance: selectedRideData.price.distance,
      duration: selectedRideData.price.duration,
    };

    try {
      // Corporate trips are held for approval instead of dispatched. The
      // re-search path after a driver cancels deliberately does NOT come back
      // through here - the company already approved that trip.
      if (corporateOn && corporateInfo) await startCorporateApproval(priceData);
      else await startBroadcastSearch(priceData);
    } catch (e: any) {
      setBookingError(e.message || 'Could not book ride. Try again.');
      setShowConfirm(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Poll ride status after booking
  useEffect(() => {
    if (!activeRideId || rideStatus === 'completed' || rideStatus === 'rejected') return;
    const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000') + '/api/v1';
    const token = localStorage.getItem('accessToken');
    const tick = async () => {
      try {
        const res = await fetch(`${API_URL}/rides/${activeRideId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (data?.status) {
          setRideStatus(data.status);
          // One-shot sound cue on status transitions (accepted / arrived / cancelled).
          if (data.status !== prevStatusRef.current) {
            if (data.status === 'accepted' || data.status === 'arrived'
                || data.status === 'cancelled' || data.status === 'rejected') {
              playCustomerCue();
            }
            prevStatusRef.current = data.status;
          }
          if (data.driver) {
            // Merge - never drop the vehicle info we already have (prevents plate/color flicker)
            setAssignedDriver((prev: any) => ({
              ...prev, ...data.driver,
              driver_vehicles: data.driver.driver_vehicles?.length ? data.driver.driver_vehicles : prev?.driver_vehicles,
            }));
            // Update history entry with driver phone + plate once known
            const plate = data.driver.driver_vehicles?.[0]?.plate_number || '';
            if (data.driver.phone || plate) {
              const hist = JSON.parse(localStorage.getItem('rideHistory') || '[]');
              const updated = hist.map((r: any) =>
                r.id === data.id
                  ? { ...r, driverPhone: data.driver.phone || r.driverPhone, driverPlate: plate || r.driverPlate }
                  : r
              );
              localStorage.setItem('rideHistory', JSON.stringify(updated));
            }
          }
          if (data.driver_id && !assignedDriverId) setAssignedDriverId(data.driver_id);
          if (data.status === 'accepted' || data.status === 'arrived' || data.status === 'in_progress') {
            setIsSearchingDriver(false);
            setDriverDeclinedMsg('');
            setDeclinedReason('');
          }
          // Still broadcasting. Once a batch of drivers has a LIVE offer out
          // (active_offers > 0) tell the rider a driver was FOUND and we're
          // waiting on them; between rounds (nobody offered yet / batch lapsed)
          // show the expanding-radius search progress.
          if (data.status === 'searching') {
            setIsSearchingDriver(true);
            const offers = data.active_offers || 0;
            if (offers > 0) {
              setDriverDeclinedMsg(offers === 1
                ? 'Driver found nearby - waiting for them to accept…'
                : `Found ${offers} drivers nearby - waiting for one to accept…`);
            } else if ((data.dispatch_round || 0) >= 2) {
              // A previous round lapsed with no acceptance - reassure the rider
              // that we're re-broadcasting to other nearby drivers.
              setDriverDeclinedMsg('Driver didn’t respond - finding you another driver…');
            } else {
              setDriverDeclinedMsg('Finding your driver…');
            }
          }
          // No driver found after the full expanding search.
          if (data.status === 'no_drivers') {
            clearInterval(interval);
            setIsSearchingDriver(false);
            setDriverDeclinedMsg('');
            setDeclinedReason('');
            setNoDriversModal(true);
            setNavigationMode(false);
            setActiveRideId('');
            setRideStatus('');
          }
          if (data.status === 'rejected') {
            clearInterval(interval);
            const reason = data.cancel_reason ? `"${data.cancel_reason}"` : '';
            setDeclinedReason(reason);
            setDriverDeclinedMsg('Driver declined - finding you another driver...');
            setIsSearchingDriver(true);
            findAnotherDriver();
          }
          if (data.status === 'completed' && !ratingDone) {
            clearInterval(interval);
            const u = JSON.parse(localStorage.getItem('user') || '{}');
            const alreadyPaid = localStorage.getItem('paidRide') === data.id;
            // Always capture the final fare (stops included) for the pay screen.
            // Cash used to rely on bookedRideRef only, which is empty after an
            // app restart and showed KES 0.
            if (data.price != null) setPendingPaymentPrice(Number(data.price));
            if (u.paymentMethod === 'card' && data.price && !alreadyPaid) {
              setPendingPaymentRideId(data.id);
              setShowPayment(true);
            } else {
              setShowRating(true);
            }
          }
          if (data.status === 'cancelled') {
            clearInterval(interval);
            // Whoever cancelled, the dead ride's history entry shows "Cancelled",
            // never a billed fare (a re-search books a NEW ride/entry).
            markHistoryCancelled(data.id);
            // Only re-search when the DRIVER cancelled an accepted trip. The
            // customer's own cancel (a poll tick racing the cancel) must not
            // re-dispatch the dead ride.
            if (data.cancelled_by === 'driver') {
              const reason = data.cancel_reason ? `"${data.cancel_reason}"` : '';
              setDeclinedReason(reason);
              setDriverDeclinedMsg('Driver cancelled - finding you another driver...');
              setIsSearchingDriver(true);
              findAnotherDriver();
            }
          }
        }
      } catch {}
    };
    const interval = setInterval(tick, 3000);
    tick(); // immediate first check
    return () => clearInterval(interval);
  }, [activeRideId, rideStatus, ratingDone]);

  // Safety net: whenever the active ride is completed, (1) free the customer
  // from the trip UI immediately so they can book another ride - whether or not
  // they rate - and (2) open the rating prompt, independent of the polling
  // tick's timing. Cash rates now; card customers pay first (the payment modal
  // opens the rating afterwards).
  useEffect(() => {
    if (rideStatus !== 'completed' || !activeRideId) return;
    setNavigationMode(false); // unlock the booking UI right away
    if (ratingDone || showPayment) return;
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    const alreadyPaid = localStorage.getItem('paidRide') === activeRideId;
    if (u.paymentMethod === 'card' && !alreadyPaid) return;
    setShowRating(true);
  }, [rideStatus, activeRideId, ratingDone, showPayment]);

  // Ride status now comes from the 3s active-ride poll + Web Push
  // (usePushNotifications) - no Supabase Realtime WebSocket is held for the rider.

  // Load recent destinations whenever the Where-to search screen opens, and
  // once on mount so the home-screen discovery sheet can show them too.
  useEffect(() => { setRecentPlaces(getRecentPlaces()); }, []); // eslint-disable-line
  useEffect(() => {
    if (searchFocused) setRecentPlaces(getRecentPlaces());
  }, [searchFocused]);

  // Home screen "Schedule" tile: once the rider reaches the Confirm modal,
  // pre-fill the schedule field (default 30 min out) instead of leaving them
  // to notice and fill in an optional field themselves.
  useEffect(() => {
    if (showConfirm && scheduleIntent && !scheduleFor) {
      setScheduleFor(new Date(Date.now() + 30 * 60000).toISOString().slice(0, 16));
    }
  }, [showConfirm, scheduleIntent, scheduleFor]);

  // Delayed focus on the search input - waits for the slide-up animation to
  // finish before opening the keyboard. Fixes iOS Safari "pops too high" bug
  // where autoFocus fires before the fixed overlay settles, shifting it offscreen.
  useEffect(() => {
    if (!searchFocused) return;
    const t = setTimeout(() => searchInputRef.current?.focus(), 380);
    return () => clearTimeout(t);
  }, [searchFocused]);

  // Pop the sheet open whenever fresh ride options arrive, so the rider sees
  // "Choose a ride" without having to swipe up first.
  useEffect(() => {
    if (rideResults.length > 0) setSheetExpanded(true);
  }, [rideResults.length]);

  // Fetch live route + ETA during in_progress
  useEffect(() => {
    if (!isLoaded || rideStatus !== 'in_progress' || !driverLivePos || !dropoff || !map) return;
    const now = Date.now();
    if (now - lastDirFetchRef.current < 15000) return;
    lastDirFetchRef.current = now;
    if (!inProgressDirRef.current) inProgressDirRef.current = new google.maps.DirectionsService();
    inProgressDirRef.current.route(
      { origin: driverLivePos, destination: { lat: dropoff.lat, lng: dropoff.lng }, travelMode: google.maps.TravelMode.DRIVING },
      (result, status) => {
        if (status === 'OK' && result) {
          setInProgressDirections(result);
          const secs = result.routes[0].legs[0].duration?.value || 0;
          const eta = new Date(Date.now() + secs * 1000);
          setTripEta(eta.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
          setTripMins(`${Math.max(1, Math.round(secs / 60))} min`);
          // Fit the route only once at the start of the trip; afterwards leave
          // the zoom/pan exactly where the user put it (don't snap back).
          if (!didFitInProgressRef.current) {
            map.fitBounds(result.routes[0].bounds, { top: 80, right: 20, bottom: 300, left: 20 });
            didFitInProgressRef.current = true;
          }
        }
      }
    );
  }, [isLoaded, rideStatus, driverLivePos, dropoff, map]);

  // Driver → pickup route while the driver is on the way (pending/accepted/
  // arrived). This is the nav-style line that shows HOW the driver is reaching
  // you; the destination route only appears once the trip starts.
  useEffect(() => {
    if (!isLoaded || !map || !driverLivePos || !pickup || !['pending', 'accepted', 'arrived'].includes(rideStatus)) {
      setToPickupDirections(null);
      setPickupEtaSec(null);
      return;
    }
    const now = Date.now();
    if (now - lastToPickupFetchRef.current < 12000) return;
    lastToPickupFetchRef.current = now;
    if (!toPickupDirRef.current) toPickupDirRef.current = new google.maps.DirectionsService();
    toPickupDirRef.current.route(
      { origin: driverLivePos, destination: { lat: pickup.lat, lng: pickup.lng }, travelMode: google.maps.TravelMode.DRIVING },
      (result, status) => {
        if (status === 'OK' && result) {
          setToPickupDirections(result);
          // Feeds the "Your driver arrives in X min" heading - previously
          // nothing ever set this, so that headline could never appear and
          // the panel silently fell back to the generic "Driver is on the
          // way" text no matter how close the driver actually was.
          setPickupEtaSec(result.routes[0]?.legs?.[0]?.duration?.value ?? null);
        }
      },
    );
  }, [isLoaded, map, driverLivePos, pickup, rideStatus]);

  // Reset the one-time fit flag whenever we're not actively in a trip.
  useEffect(() => {
    if (rideStatus !== 'in_progress') didFitInProgressRef.current = false;
  }, [rideStatus]);

  // Background message poll for unread badge
  useEffect(() => {
    if (!activeRideId || showChat) return;
    const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000') + '/api/v1';
    const myId = user.id;
    const poll = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        const res = await fetch(`${API_URL}/rides/${activeRideId}/messages`, { headers: { Authorization: `Bearer ${token}` } });
        const msgs: any[] = await res.json();
        if (!Array.isArray(msgs)) return;
        const otherCount = msgs.filter((m: any) => m.sender_id !== myId).length;
        // Sound cue when a NEW message arrives (not on first load, not while chat open).
        if (prevMsgCountRef.current !== -1 && otherCount > prevMsgCountRef.current) {
          playCustomerCue();
        }
        prevMsgCountRef.current = otherCount;
        setUnreadMessages(Math.max(0, otherCount - seenMsgCount.current));
      } catch {}
    };
    poll();
    const interval = setInterval(poll, 3000);
    return () => clearInterval(interval);
  }, [activeRideId, showChat, user.id]);

  const openChat = () => {
    seenMsgCount.current += unreadMessages;
    setUnreadMessages(0);
    setShowChat(true);
  };

  // Re-search after a driver cancels an accepted trip: start a fresh server-side
  // broadcast for the same trip. The re-entrancy lock stops two overlapping poll
  // ticks from spawning two parallel searches.
  const findAnotherDriver = async () => {
    if (dispatchingRef.current) return;
    dispatchingRef.current = true;
    try {
      const priceData = bookedRideRef.current;
      if (!priceData || !pickup || !dropoff || !selectedRide) {
        setIsSearchingDriver(false);
        setDriverDeclinedMsg('');
        return;
      }
      await startBroadcastSearch(priceData);
    } catch (e: any) {
      setIsSearchingDriver(false);
      setDriverDeclinedMsg('');
      setDeclinedReason('');
      setBookingError(e.message || 'Could not find another driver.');
    } finally {
      dispatchingRef.current = false;
    }
  };

  const handleCancelRide = () => {
    setCancelReason('');
    setShowCancelModal(true);
  };

  // Flag the local history entry so it renders as "Cancelled" with no fare billed.
  const markHistoryCancelled = (rideId: string) => {
    if (!rideId) return;
    try {
      const hist = JSON.parse(localStorage.getItem('rideHistory') || '[]');
      localStorage.setItem('rideHistory', JSON.stringify(
        hist.map((r: any) => (r.id === rideId ? { ...r, status: 'cancelled' } : r))
      ));
    } catch {}
  };

  const confirmCancelRide = async () => {
    if (activeRideId) {
      try {
        const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000') + '/api/v1';
        const token = localStorage.getItem('accessToken');
        await fetch(`${API_URL}/rides/${activeRideId}/cancel`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ reason: cancelReasonToSend || null }),
        });
      } catch (e) {}
      markHistoryCancelled(activeRideId);
    }
    setShowCancelModal(false);
    setCancelReason('');
    setCancelReasonOther('');
    setNavigationMode(false);
    setSelectedRide(null);
    setDirections(null);
    setInProgressDirections(null);
    setTripEta('');
    setTripMins('');
    setDriverLivePos(null);
    lastDirFetchRef.current = 0;
    setDropoff(null);
    setDropoffQuery('');
    setPreselectRideType(null);
    setScheduleIntent(false);
    setActiveRideId('');
    setRideStatus('');
    setAssignedDriver(null);
    setAssignedDriverId('');
    setBookingError('');
    setShowRating(false);
    setSelectedRating(0);
    setRatingComment('');
    setRatingDone(false);
    setStops([]);
    setStopQueries([]);
    setStopSuggestions([]);
  };

  const handleLogout = () => { clearAuthSession(); navigate('/'); };

  // Open the pre-send countdown. A firm shake OR the deliberate button lands here
  // first - never fires instantly - so an accidental bump/tap can be cancelled.
  const triggerSos = (via: 'manual' | 'shake') => {
    if (sosCountdown !== null || sosSent) return; // already arming/sent
    sosViaRef.current = via;
    setSosCountdown(5);
  };
  const cancelSos = () => setSosCountdown(null);

  const toggleShake = (enabled: boolean) => {
    setShakeEnabled(enabled);
    localStorage.setItem('shakeSosEnabled', enabled ? '1' : '0');
  };

  // A firm shake anywhere in the app arms the alert (when enabled).
  useShakeDetection({ enabled: shakeEnabled, onShake: () => triggerSos('shake') });

  // Actually send the alert: records the SOS + fans out SMS to emergency contacts
  // server-side. We do NOT auto-dial 999 here (a false shake shouldn't call
  // police) - the "alert sent" panel offers a prominent Call 999 button instead.
  const fireSos = async () => {
    setSosCountdown(null);
    setSosSent(true);
    try {
      const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000') + '/api/v1';
      const token = localStorage.getItem('accessToken');
      const via = sosViaRef.current;
      const post = (extra: object) => fetch(`${API_URL}/safety/sos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ rideId: activeRideId || null, via, ...extra }),
      });
      navigator.geolocation?.getCurrentPosition(
        (pos) => { post({ lat: pos.coords.latitude, lng: pos.coords.longitude }); },
        () => { post({}); },
        { enableHighAccuracy: true, timeout: 8000 },
      );
    } catch {}
    setTimeout(() => setSosSent(false), 15000);
  };

  // Tick the pre-send countdown; fire when it reaches 0.
  useEffect(() => {
    if (sosCountdown === null) return;
    if (sosCountdown <= 0) { fireSos(); return; }
    const id = setTimeout(() => setSosCountdown((c) => (c === null ? null : c - 1)), 1000);
    return () => clearTimeout(id);
  }, [sosCountdown]); // eslint-disable-line

  // Safety audio recording (native only): record for the whole trip, from the
  // moment it starts until it ends. The native foreground service keeps going
  // through backgrounding / screen-lock and uploads the file when we stop.
  useEffect(() => {
    if (rideStatus === 'in_progress' && activeRideId) {
      startTripRecording(activeRideId);
    } else {
      stopTripRecording();
    }
  }, [rideStatus, activeRideId]);

  // Share a live tracking link to the current trip (Web Share, clipboard fallback).
  const shareTrip = async (rideId?: string | null) => {
    if (!rideId) return;
    const url = `https://safaricab.app/track/${rideId}`;
    const text = 'Follow my SafariCab trip live:';
    // Native share sheet (WhatsApp / Messages / …) in the installed app.
    try {
      const { Capacitor } = await import('@capacitor/core');
      if (Capacitor.isNativePlatform()) {
        const { Share } = await import('@capacitor/share');
        await Share.share({ title: 'My SafariCab trip', text, url });
        return;
      }
    } catch {}
    // Web share sheet (works in iOS Safari / WKWebView).
    try {
      if (navigator.share) { await navigator.share({ title: 'My SafariCab trip', text, url }); return; }
    } catch { return; }
    // Last resort: copy the link.
    try {
      await navigator.clipboard.writeText(url);
      setStopAddedMsg('Trip link copied - share it with anyone');
      setTimeout(() => setStopAddedMsg(''), 3000);
    } catch {}
  };

  const selectedRideData = rideResults.find(r => r.option.id === selectedRide);
  // Mirrors the discount badge's own render condition (see inside the sheet
  // below) - used to grow the sheet's fixed-height states by the badge's own
  // row height, so it doesn't eat into and squeeze down the actual content
  // (e.g. the "Where to?" search box) within a height that never grew to fit it.
  const discountBadgeVisible = rideResults.length === 0 && !bookingForOther && (appliedDiscount > 0 || (liveOfferPct ?? 0) > 0);
  const discountBadgeExtraPx = discountBadgeVisible ? 44 : 0;
  const paymentMethod = (() => { try { return (JSON.parse(localStorage.getItem('user') || '{}').paymentMethod) || 'cash'; } catch { return 'cash'; } })();
  // Fare owed for the just-finished trip. The server-reported price wins (it
  // includes mid-trip stops/edits and survives app restarts); the in-memory
  // booking quote is only the fallback.
  const fareDue = pendingPaymentPrice > 0 ? pendingPaymentPrice : (bookedRideRef.current?.price ?? 0);
  // For cash, the rating waits behind the "Pay X to driver" confirmation.
  const needsDriverPay = paymentMethod === 'cash' && !driverPaidConfirmed;

  // Waiting-for-driver panel - 1:1 clone of the reference screenshot (Bolt
  // "Captain found" sheet), only recolored to SafariCab dark/gold. Add-stop and
  // edit-destinations reuse the existing mid-trip flows (search overlay modes).
  const openAddStop = () => { setAddStopMode(true); setDropoffSuggestions([]); setSearchFocused(true); };
  const openEditDestinations = () => { setEditDropoffMode(true); setDropoffSuggestions([]); setSearchFocused(true); };

  // Distance in metres to the nearest eligible online driver, or null when there
  // isn't one. Split out from nearestDriverInfo below so the map's "Pickup N min"
  // bubble and the waiting card's text line are the same number, derived once -
  // two independent estimates of the same wait disagreeing on screen is worse
  // than either of them being slightly off.
  const nearestDriverMeters = (() => {
    if (!pickup) return null;
    let best: number | null = null;
    nearbyDrivers.forEach((d: any) => {
      const lat = Number(d.current_lat ?? d.lat);
      const lng = Number(d.current_lng ?? d.lng);
      if (!lat || !lng) return;
      if (selectedRide && !driverServes(d, selectedRide)) return;
      const m = haversineMeters({ lat: pickup.lat, lng: pickup.lng }, { lat, lng });
      if (best == null || m < best) best = m;
    });
    return best;
  })();

  /** ~24 km/h urban driving - the same assumption the waiting card has always used. */
  const metersToMins = (m: number) => Math.max(1, Math.round(m / 400));

  const nearestDriverMins = nearestDriverMeters == null ? null : metersToMins(nearestDriverMeters);

  // "0.16 km away · 1 min" line for the waiting card: nearest eligible online driver.
  const nearestDriverInfo = (() => {
    if (nearestDriverMeters == null) return '';
    const km = nearestDriverMeters / 1000;
    return `${km < 10 ? km.toFixed(2) : Math.round(km)} km away · ${metersToMins(nearestDriverMeters)} min`;
  })();

  // The "Enter Destination" suggestions list drives whichever of the two rows
  // is active - pickup editing only applies in the main flow (never inside
  // addStopMode/editDropoffMode/settingPlace, which only ever touch dropoff).
  const isPickupSearchActive = !addStopMode && !editDropoffMode && !settingPlace && activeSearchField === 'pickup';
  const activeSearchQuery = isPickupSearchActive ? pickupQuery : dropoffQuery;
  const activeSearchSuggestions = isPickupSearchActive ? pickupSuggestions : dropoffSuggestions;
  const selectActiveSuggestion = (loc: any) => { if (isPickupSearchActive) { selectPickup(loc); return; } selectPlace(loc); };

  // Split in two so the collapsed tracking-panel peek can show "My
  // destination" too (not just the one-line status) while payment/tips stay
  // expanded-only - previously both were one block, all-or-nothing.
  const myDestinationCard = (
    <div className="mt-3 bg-gray-100 rounded-2xl p-4">
      <p className="text-gray-900 font-bold text-base mb-3">My destination</p>
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <span className="w-5 h-5 rounded-full border-[5px] border-green-500 bg-white flex-shrink-0" />
          <span className="text-gray-700 text-sm leading-snug">{pickup?.address}</span>
        </div>
        {stops.filter(Boolean).map((stop: any, i) => (
          <div key={i} className="flex items-center gap-3">
            <div className="w-5 h-5 rounded-full bg-safaricab-gold/40 flex items-center justify-center flex-shrink-0"><span className="text-safaricab-gold text-[10px] font-bold">{i + 1}</span></div>
            <span className="text-gray-600 text-sm leading-snug">{stop.address}</span>
          </div>
        ))}
        <button onClick={openAddStop} className="flex items-center gap-3 w-full text-left">
          <MapPin className="w-5 h-5 text-gray-300 flex-shrink-0" />
          <span className="text-gray-400 text-sm">Add stop</span>
        </button>
        <div className="flex items-center gap-3">
          <MapPin className="w-5 h-5 text-safaricab-gold flex-shrink-0" />
          <span className="text-gray-700 text-sm leading-snug flex-1">{dropoff?.address}</span>
          <button onClick={openEditDestinations} className="p-1.5 text-gray-400 flex-shrink-0"><Pencil className="w-4 h-4" /></button>
        </div>
      </div>
      <button onClick={openEditDestinations} className="flex items-center gap-2 text-safaricab-gold text-sm font-medium mt-4">
        <Navigation className="w-4 h-4" /> Edit destinations
      </button>
      {/* Confirmation/error feedback for both actions above - lives here
          (not just in the accepted/in_progress phase blocks that used to
          render it) since this card, and these two buttons, now show across
          every active-ride phase and in both the collapsed and expanded
          tracking panel. */}
      {stopAddedMsg && (
        <p className="text-safaricab-gold text-xs text-center mt-3 bg-safaricab-gold/10 py-2 rounded-lg">{stopAddedMsg}</p>
      )}
      {dropoffEditMsg && (
        <p className="text-blue-500 text-xs text-center mt-3 bg-blue-50 py-2 rounded-lg">{dropoffEditMsg}</p>
      )}
    </div>
  );
  const paymentAndTipsSummary = (
    <div className="mt-3 space-y-3">
      {/* Payment method */}
      <div className="bg-gray-100 rounded-2xl p-4">
        <p className="text-gray-900 font-bold text-base mb-3">Payment method</p>
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 rounded-lg bg-green-500/15 flex items-center justify-center flex-shrink-0">
            {paymentMethod === 'card'
              ? <CreditCard className="w-5 h-5 text-safaricab-gold" />
              : paymentMethod === 'wallet'
              ? <WalletIcon className="w-5 h-5 text-safaricab-gold" />
              : <DollarSign className="w-5 h-5 text-green-500" />}
          </span>
          <span className="text-gray-900 text-sm capitalize">{paymentMethod}</span>
          {bookedRideRef.current?.price != null && (
            <span className="ml-auto text-gray-900 font-bold text-base">Ksh {bookedRideRef.current.price}</span>
          )}
        </div>
      </div>
      {/* Did you know? - a waiting-phase tip only. Its copy is about drivers
          accepting and about losing your place in the queue if you cancel, which
          stops being true the moment the trip actually starts: the rider is in the
          car, there's no queue left to go back to. Hidden for in_progress; the
          payment card above still shows in every phase. */}
      {rideStatus !== 'in_progress' && (
        <div className="bg-gray-100 rounded-2xl p-4">
          <p className="text-safaricab-gold font-bold text-sm mb-1.5 flex items-center gap-2"><MessageCircle className="w-4 h-4" /> Did you know?</p>
          <p className="text-gray-500 text-sm leading-snug">Drivers usually accept quickly. Cancelling now will place you back at the start of the queue.</p>
        </div>
      )}
    </div>
  );
  const waitingTripSummary = (
    <>
      {myDestinationCard}
      {paymentAndTipsSummary}
    </>
  );

  // Reused across accepted/arrived/in_progress (previously each had its own
  // near-identical copy) - vehicle+plate row, then a 3-column avatar/call/
  // message grid, matching the reference layout exactly (white card, black
  // text, light gray badges/buttons) with SafariCab's gold as the one
  // accent color, instead of keeping the app's usual dark theme here.
  //
  // Two skins, one markup. The in_progress phase renders this on SafariCab's dark
  // gold theme (see that branch below) while every other phase keeps the white
  // card - so the light/dark classes are picked per call rather than the card
  // being duplicated and left to drift apart the way the three copies did before.
  const renderDriverContactCard = (dark: boolean) => assignedDriver && (
    <div>
      {assignedDriver.driver_vehicles?.[0] && (
        <button onClick={() => setShowDriverDetail(true)} className="w-full flex items-center justify-between py-2">
          <span className={`text-sm ${dark ? 'text-white/60' : 'text-gray-600'}`}>{assignedDriver.driver_vehicles[0].make}, {assignedDriver.driver_vehicles[0].color}</span>
          <span className={`font-bold text-xs px-2.5 py-1.5 rounded-lg tracking-wider ${dark ? 'bg-safaricab-gold text-black' : 'bg-gray-100 text-gray-900'}`}>{assignedDriver.driver_vehicles[0].plate_number}</span>
        </button>
      )}
      <div className={`border-t ${dark ? 'border-white/10' : 'border-gray-100'}`} />
      <div className="grid grid-cols-3 gap-1 py-3">
        <button onClick={() => setShowDriverDetail(true)} className="flex flex-col items-center gap-1.5 min-w-0">
          {assignedDriver.avatar_url ? (
            <img src={assignedDriver.avatar_url} className="w-12 h-12 rounded-full object-cover" alt={assignedDriver.full_name} />
          ) : (
            <div className="w-12 h-12 rounded-full bg-safaricab-gold/20 flex items-center justify-center text-safaricab-gold font-bold">
              {assignedDriver.full_name?.charAt(0)}
            </div>
          )}
          <span className={`text-xs font-semibold truncate max-w-full ${dark ? 'text-white' : 'text-gray-900'}`}>{assignedDriver.full_name}</span>
        </button>
        {assignedDriver.phone && (
          <a href={`tel:${assignedDriver.phone}`} className="flex flex-col items-center gap-1.5">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${dark ? 'bg-safaricab-gold/15 border border-safaricab-gold/30' : 'bg-gray-100'}`}>
              <Phone className="w-5 h-5 text-safaricab-gold" />
            </div>
            <span className={`text-xs ${dark ? 'text-white/70' : 'text-gray-500'}`}>Contact Driver</span>
          </a>
        )}
        <button onClick={openChat} className="flex flex-col items-center gap-1.5">
          <div className={`relative w-12 h-12 rounded-full flex items-center justify-center ${dark ? 'bg-safaricab-gold/15 border border-safaricab-gold/30' : 'bg-gray-100'}`}>
            <MessageCircle className="w-5 h-5 text-safaricab-gold" />
            {unreadMessages > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1">{unreadMessages}</span>
            )}
          </div>
          <span className={`text-xs ${dark ? 'text-white/70' : 'text-gray-500'}`}>Message Driver</span>
        </button>
      </div>
      <div className={`border-t ${dark ? 'border-white/10' : 'border-gray-100'}`} />
    </div>
  );
  const driverContactCard = renderDriverContactCard(false);

  // One definition, rendered in whichever step the rider is on when the ride type
  // is known to be a Safari Send: the trip form (service pre-picked) or the ride
  // list (the normal path - the type isn't known until they pick it there).
  const safariSendDetails = (
    <div className="bg-gray-50 rounded-xl px-4 py-3 space-y-3">
      <div>
        <p className="text-gray-900 text-sm font-semibold mb-2">Sender details</p>
        <div className="space-y-2">
          <input value={senderName} onFocus={keepFieldVisible} onChange={(e) => { setSenderName(e.target.value); setSendDetailsError(false); }} placeholder={sendDetailsError && !senderName.trim() ? 'Sender name is required' : "Sender's name"} className={`w-full bg-white border rounded-xl px-4 py-2.5 text-gray-900 text-sm outline-none ${sendDetailsError && !senderName.trim() ? 'border-red-500 placeholder-red-400' : 'border-gray-200'}`} />
          <input value={senderPhone} onFocus={keepFieldVisible} onChange={(e) => { setSenderPhone(e.target.value); setSendDetailsError(false); }} placeholder={sendDetailsError && !senderPhone.trim() ? 'Sender phone is required' : "Sender's phone (e.g. 0712 345678)"} inputMode="tel" className={`w-full bg-white border rounded-xl px-4 py-2.5 text-gray-900 text-sm outline-none ${sendDetailsError && !senderPhone.trim() ? 'border-red-500 placeholder-red-400' : 'border-gray-200'}`} />
        </div>
      </div>
      <div>
        <p className="text-gray-900 text-sm font-semibold mb-2">Receiver details</p>
        <div className="space-y-2">
          <input value={receiverName} onFocus={keepFieldVisible} onChange={(e) => { setReceiverName(e.target.value); setSendDetailsError(false); }} placeholder={sendDetailsError && !receiverName.trim() ? 'Receiver name is required' : "Receiver's name"} className={`w-full bg-white border rounded-xl px-4 py-2.5 text-gray-900 text-sm outline-none ${sendDetailsError && !receiverName.trim() ? 'border-red-500 placeholder-red-400' : 'border-gray-200'}`} />
          <input value={receiverPhone} onFocus={keepFieldVisible} onChange={(e) => { setReceiverPhone(e.target.value); setSendDetailsError(false); }} placeholder={sendDetailsError && !receiverPhone.trim() ? 'Receiver phone is required' : "Receiver's phone (e.g. 0712 345678)"} inputMode="tel" className={`w-full bg-white border rounded-xl px-4 py-2.5 text-gray-900 text-sm outline-none ${sendDetailsError && !receiverPhone.trim() ? 'border-red-500 placeholder-red-400' : 'border-gray-200'}`} />
        </div>
      </div>
      <p className={`text-xs ${sendDetailsError ? 'text-red-400' : 'text-gray-400'}`}>{sendDetailsError ? 'Fill in both sender and receiver details to continue.' : "The driver will contact the receiver directly to hand off the package."}</p>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-safaricab-darker flex flex-col overflow-hidden">
      {showSplash && <div className="sc-splash"><span className="sc-splash-ring" /></div>}
      <UpdatePrompt />
      <EnableNotificationsPrompt />
      {/* Map Background */}
      <div className="absolute inset-0 z-0">
        {isLoaded && GOOGLE_KEY && GOOGLE_KEY !== 'YOUR_GOOGLE_MAPS_API_KEY' ? (
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={mapCenter}
            onLoad={(m) => { setMap(m); mapRef.current = m; m.setZoom(17); }}
            onZoomChanged={() => { userZoomedRef.current = true; setMapPanned(true); }}
            onDragStart={() => { userZoomedRef.current = true; setMapPanned(true); }}
            onIdle={() => { if (pinMode && mapRef.current) { const c = mapRef.current.getCenter(); if (c) setPinCenter({ lat: c.lat(), lng: c.lng() }); } }}
            // `styles` unconditionally, never `mapId`. A vector Map ID makes
            // Google ignore `styles` entirely and take its palette from the
            // cloud console instead - which would silently throw away both the
            // dark idle look and the daylight tracking look below, depending on
            // an env var set outside this repo. The Map ID was only ever here to
            // unlock 360° rotation, and nothing in this app rotates the map
            // (no setTilt/setHeading anywhere) or uses AdvancedMarkerElement,
            // so it was buying nothing and costing the styling.
            options={{ disableDefaultUI: true, gestureHandling: 'greedy', clickableIcons: false, styles: boltDayStyles }}
          >
            {/* Route line drawn via useEffect (booking route) */}
            {pickup && rideStatus !== 'in_progress' && (
              <OverlayView
                position={{ lat: pickup.lat, lng: pickup.lng }}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                getPixelPositionOffset={() => ({ x: -16, y: -16 })}
              >
                <div className="sc-pulse-wrap">
                  {/* Google-style dot: static soft halo always on; the ripple only plays while finding a ride */}
                  <span className="sc-loc-halo" />
                  {/* Direction beam - the triangular wedge between the dot and the halo edge.
                      Rotates with the compass when available, else points north. */}
                  <div className="sc-loc-beam-rot" style={{ transform: `rotate(${compassHeading ?? 0}deg)` }}>
                    <span className="sc-loc-beam" />
                  </div>
                  {isSearchingDriver && (
                    <>
                      <span className="sc-pulse-ring" />
                      <span className="sc-pulse-ring sc-pulse-ring-2" />
                    </>
                  )}
                  <span className="sc-pulse-dot" />
                </div>
              </OverlayView>
            )}
            {dropoff && rideStatus !== 'in_progress' && (
              <Marker position={{ lat: dropoff.lat, lng: dropoff.lng }} icon={getOrangePinIcon()} draggable={true} onDragEnd={async (e: any) => { if (!e.latLng) return; const newLat = e.latLng.lat(); const newLng = e.latLng.lng(); const addr = await reverseGeocode(newLat, newLng); setDropoff({ lat: newLat, lng: newLng, address: addr }); setDropoffQuery(addr); setRideResults([]); setDirections(null); }} />
            )}

            {/* ── Route callouts, ride-selection screen only ──────────────────
                Two labels pinned to the ends of the preview route: how long
                until a car reaches the pickup, and how long the trip itself
                takes. Both numbers already existed - the wait was buried in the
                waiting card and the duration in a line of the ride list - but
                reading them off the map is what makes the route mean something
                before you have chosen anything.

                Deliberately scoped to `rideResults.length > 0`, i.e. the moment
                between "find rides" and picking one. Every other map state
                (idle, searching, driver on the way, in progress) is untouched
                and keeps exactly the markers it had.

                FLOAT_PANE + pointer-events-none, NOT the mouse-target pane.
                Float is the topmost pane, so the label can't end up underneath
                the very pin it belongs to (overlayLayer sits BELOW markers); and
                pointer-events-none matters because a label that swallowed a drag
                would stop the map panning where it happened to sit. */}
            {rideResults.length > 0 && pickup && (
              <OverlayView
                position={{ lat: pickup.lat, lng: pickup.lng }}
                mapPaneName={OverlayView.FLOAT_PANE}
                getPixelPositionOffset={(w, h) => ({ x: -(w / 2), y: -h - 26 })}
              >
                <div className="pointer-events-none select-none flex flex-col items-center">
                  <div className="bg-safaricab-dark rounded-xl px-3 py-1.5 shadow-lg whitespace-nowrap">
                    <p className="text-white/60 text-[10px] font-medium leading-tight">Pickup</p>
                    <p className="text-white text-base font-bold leading-tight">
                      {nearestDriverMins != null ? `${nearestDriverMins} min` : '--'}
                    </p>
                  </div>
                  {/* Little tail so the label reads as belonging to the dot
                      beneath it rather than floating over the map. */}
                  <div className="w-0 h-0 border-l-[5px] border-r-[5px] border-t-[6px] border-l-transparent border-r-transparent border-t-safaricab-dark" />
                </div>
              </OverlayView>
            )}
            {rideResults.length > 0 && dropoff && (
              <OverlayView
                position={{ lat: dropoff.lat, lng: dropoff.lng }}
                mapPaneName={OverlayView.FLOAT_PANE}
                getPixelPositionOffset={(w, h) => ({ x: -(w / 2), y: -h - 38 })}
              >
                <div className="pointer-events-none select-none flex flex-col items-center">
                  <div className="bg-safaricab-dark rounded-xl px-3 py-1.5 shadow-lg whitespace-nowrap">
                    <p className="text-white/60 text-[10px] font-medium leading-tight">Dropoff</p>
                    <p className="text-white text-base font-bold leading-tight">
                      {(selectedRideData?.price?.duration ?? rideResults[0]?.price?.duration) != null
                        ? `${selectedRideData?.price?.duration ?? rideResults[0]?.price?.duration} min`
                        : '--'}
                    </p>
                  </div>
                  <div className="w-0 h-0 border-l-[5px] border-r-[5px] border-t-[6px] border-l-transparent border-r-transparent border-t-safaricab-dark" />
                </div>
              </OverlayView>
            )}
            {/* Destination pin during the trip (we suppress the route's default markers). */}
            {dropoff && rideStatus === 'in_progress' && (
              <Marker position={{ lat: dropoff.lat, lng: dropoff.lng }} icon={getOrangePinIcon()} title="Destination" />
            )}
            {/* Driver → pickup while on the way (accepted/arrived): same cyan-over-
                dark-casing route styling as the driver app's own turn-by-turn
                navigation map, instead of a plain blue line - re-fetched every
                12s from the driver's live position (see the effect above), so
                it stays current as they actually move. */}
            {toPickupDirections && ['pending', 'accepted', 'arrived'].includes(rideStatus) && (
              <>
                <Polyline path={toPickupDirections.routes[0].overview_path} options={{ strokeColor: ROUTE_CASING, strokeOpacity: 1, strokeWeight: 12, zIndex: 2 }} />
                <Polyline path={toPickupDirections.routes[0].overview_path} options={{ strokeColor: ROUTE_CORE, strokeOpacity: 1, strokeWeight: 8, zIndex: 3 }} />
              </>
            )}
            {/* Live route during in_progress: driver → dropoff (destination), same styling */}
            {inProgressDirections && rideStatus === 'in_progress' && (
              <>
                <Polyline path={inProgressDirections.routes[0].overview_path} options={{ strokeColor: ROUTE_CASING, strokeOpacity: 1, strokeWeight: 12, zIndex: 2 }} />
                <Polyline path={inProgressDirections.routes[0].overview_path} options={{ strokeColor: ROUTE_CORE, strokeOpacity: 1, strokeWeight: 8, zIndex: 3 }} />
              </>
            )}
          </GoogleMap>
        ) : (
          // Placeholder in the basemap's own ground colour, not the app's dark
          // card colour - otherwise every cold start flashes a black screen for
          // the moment before a white map paints over it.
          <div className="w-full h-full flex flex-col items-center justify-center text-sm" style={{ backgroundColor: '#d8cba8', color: 'rgba(0,0,0,0.35)' }}><div className="w-8 h-8 border-2 border-safaricab-gold border-t-transparent rounded-full animate-spin mb-3" /><p>Loading map...</p></div>
        )}
      </div>

      {/* Gradient overlay for readability. The old heavy top-and-bottom scrim
          existed to float white text over a dark map; over a white basemap it
          just makes it look dirty, and the bottom half sits behind a sheet
          anyway. What's left is a light top-only wash - enough to keep the
          header's glass buttons anchored when they pass over a white block. */}
      <div className="absolute inset-0 z-10 pointer-events-none bg-gradient-to-b from-black/25 via-transparent to-transparent" />

      {/* Pin Drop Mode overlay */}
      {pinMode && (
        <>
          {/* Centered pin */}
          <div className="absolute inset-0 z-30 flex items-center justify-center pointer-events-none" style={{ paddingBottom: '120px' }}>
            <div className="flex flex-col items-center">
              <div className="w-8 h-8 bg-safaricab-gold rounded-full border-4 border-white shadow-2xl" />
              <div className="w-1 h-6 bg-safaricab-gold shadow" />
              <div className="w-3 h-1 bg-safaricab-gold/40 rounded-full mt-0.5" />
            </div>
          </div>
          {/* Confirm panel */}
          <div className="absolute bottom-0 left-0 right-0 z-40 bg-safaricab-dark rounded-t-3xl px-4 pt-4 shadow-2xl" style={{ paddingBottom: 'var(--sai-dock)' }}>
            <div className="w-10 h-1 bg-white/30 rounded-full mx-auto mb-4" />
            <p className="text-white/60 text-sm text-center mb-3">Drag the map to position the pin, then confirm</p>
            <div className="flex gap-3">
              <button onClick={() => setPinMode(false)} className="flex-1 py-3.5 rounded-xl border border-white/20 text-white text-sm font-medium">
                Cancel
              </button>
              <button
                disabled={pinGeocoding}
                onClick={async () => {
                  const center = pinCenter || (map ? { lat: map.getCenter()!.lat(), lng: map.getCenter()!.lng() } : null);
                  if (!center) return;
                  setPinGeocoding(true);
                  try {
                    const addr = await reverseGeocode(center.lat, center.lng);
                    const label = addr || `${center.lat.toFixed(5)}, ${center.lng.toFixed(5)}`;
                    if (pinTarget === 'pickup') {
                      followMyLocationRef.current = false;
                      setPickup({ lat: center.lat, lng: center.lng, address: addr });
                      setPickupQuery(label);
                      setRideResults([]);
                    } else {
                      setDropoff({ lat: center.lat, lng: center.lng, address: addr });
                      setDropoffQuery(label);
                      setRideResults([]);
                      setDirections(null);
                    }
                  } finally {
                    setPinGeocoding(false);
                    setPinMode(false);
                  }
                }}
                className="flex-2 flex-1 py-3.5 rounded-xl bg-safaricab-gold text-black font-bold text-sm disabled:opacity-60"
              >
                {pinGeocoding ? 'Getting address...' : (pinTarget === 'pickup' ? 'Set as Pickup' : 'Set as Dropoff')}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Header */}
      <div className="relative z-20 px-4 pb-3 pt-[calc(var(--sai-top)+1.25rem)] flex items-center justify-between pointer-events-auto">
        <button onClick={() => setSidebarOpen(true)} style={{ transform: 'translateZ(0)' }} className="relative text-white bg-black/30 backdrop-blur-sm p-2 rounded-full">
          <Menu className="w-5 h-5" />
          {unreadNotifs > 0 && <span className="absolute top-0 right-0 w-2.5 h-2.5 rounded-full bg-red-500 ring-2 ring-black/40" />}
        </button>
        <button onClick={() => setActiveTab('wallet')} style={{ transform: 'translateZ(0)' }} className="flex items-center gap-2 bg-black/30 backdrop-blur-sm rounded-full px-4 py-1.5">
          <WalletIcon className="w-4 h-4 text-safaricab-gold" />
          <span className="text-white font-bold text-xs">
            {headerWalletBalance === null ? 'Wallet' : `KES ${headerWalletBalance.toLocaleString()}`}
          </span>
        </button>
        <button onClick={() => setActiveTab('profile')} className="w-8 h-8 rounded-full bg-safaricab-gold/30 flex items-center justify-center overflow-hidden">
          {avatarUrl
            ? <img src={avatarUrl} alt="Profile" className="w-full h-full object-cover" />
            : <User className="w-4 h-4 text-white" />}
        </button>
      </div>

      {/* Promotions - sits directly under the hamburger on the left edge, so the
          two share a column. Same visibility rules as the Safety button: home tab
          only, and hidden whenever a full-screen overlay or the side drawer is
          up, since this is a z-[150] map button that would otherwise float above
          them. */}
      {activeTab === 'home' && !navigationMode && !searchFocused && !pinMode && !sidebarOpen && rideResults.length === 0 && !bookingForOther && (
        <div className="fixed left-4 z-[150] pointer-events-auto flex items-center gap-2" style={{ top: 'calc(var(--sai-top) + 4.75rem)' }}>
          <button
            onClick={openPromotions}
            className="w-9 h-9 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center shadow-2xl shrink-0"
            title="Promotions"
            aria-label="Promotions"
          >
            <Megaphone className="w-5 h-5 text-safaricab-gold" />
          </button>
          {/* Points back at the megaphone on its left. Draws nothing unless a
              poster or video is actually published - see PromoNudge. */}
          <AnimatePresence>
            {!promoNudgeSeen && <PromoNudge key={promoNudgeRun} onOpen={openPromotions} />}
          </AnimatePresence>
        </div>
      )}

      {/* SOS moved to Menu → Emergency Contacts (and shake-to-alert) so it can't
          be tapped by accident. No always-visible floating button anymore. */}

      {/* Recenter button - only once the user has actually panned/zoomed away from
          their location; hides again the moment they're back on-center. Anchored
          from the bottom, just above the search sheet, at its live MEASURED height
          (sheetHeightPx via ResizeObserver) plus a 32px safety gap - generous
          enough to clear the discount badge (now the sheet's own first child,
          ~44px tall) even if the ResizeObserver update lags a frame behind the
          sheet's spring animation. ResizeObserver's contentRect excludes padding,
          so the sheet's own `var(--sai-bottom)` padding-bottom (the iOS
          home-indicator area) isn't part of sheetHeightPx - added back explicitly
          here, otherwise on notched iPhones the button sits that much lower than
          intended and overlaps the badge. Hidden while the side menu is open so
          it can never bleed over that drawer, and on any non-home tab: those are
          full-screen overlays at z-50, so a z-[150] map button would float on top
          of them. */}
      {activeTab === 'home' && !navigationMode && !searchFocused && !pinMode && !sidebarOpen && rideResults.length === 0 && !bookingForOther && mapPanned && (
        <div
          className="fixed right-4 z-[150] pointer-events-auto"
          style={{ bottom: `calc(${sheetHeightPx}px + 32px + var(--sai-bottom))` }}
        >
          <button
            onClick={() => {
              if (!mapRef.current || !pickup) return;
              mapRef.current.panTo(getVisuallyCenteredMapPoint(pickup.lat, pickup.lng));
              mapRef.current.setZoom(17);
              userZoomedRef.current = false;
              setMapPanned(false);
            }}
            // Solid white with a real shadow, matching the tracking screen's
            // pair - dark glass was built to sit on a dark map and reads as a
            // hole punched in a white one.
            className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-[0_4px_16px_rgba(0,0,0,0.22)] active:bg-gray-100 transition-colors"
            title="Back to my location"
          >
            <LocateFixed className="w-[22px] h-[22px] text-gray-900" />
          </button>
        </div>
      )}

      {/* Safety centre - the recenter button's mirror image on the left edge (same
          bottom anchor, so the two always sit level whatever height the sheet is
          at). A red hand: it opens Emergency Contacts, where the SOS button and
          shake-to-alert live. That screen was reachable only through the side menu
          before, which is not where anyone looks in a hurry.
          Deliberately NOT an SOS trigger itself - it navigates, so a stray tap
          costs nothing, which is the same reasoning that took the old floating SOS
          button off this map. Visibility matches the recenter button except for
          mapPanned: this one doesn't wait for the map to be moved - including
          activeTab, or tapping it would leave both buttons floating over the
          Emergency Contacts screen it just opened.
          The "Safety" caption is positioned ABSOLUTELY below the button: in the
          normal flow it added its own height to a bottom-anchored box, pushing
          this button ~16px higher than the recenter button on the other edge, so
          the two never sat level. Out of flow, both boxes are just the 48px
          circle and the pair lines up. */}
      {activeTab === 'home' && !navigationMode && !searchFocused && !pinMode && !sidebarOpen && rideResults.length === 0 && !bookingForOther && (
        <div
          className="fixed left-4 z-[150] pointer-events-auto"
          style={{ bottom: `calc(${sheetHeightPx}px + 32px + var(--sai-bottom))` }}
        >
          {/* Same white pill as the tracking screen, so Safety looks like one
              control wherever you meet it. The label is now inside the pill
              rather than floated underneath - on a white map the old caption's
              drop-shadow was doing all the work of making it readable. */}
          <button
            onClick={() => setActiveTab('emergency')}
            className="h-12 pl-4 pr-5 rounded-full bg-white flex items-center gap-2.5 shadow-[0_4px_16px_rgba(0,0,0,0.22)] active:bg-gray-100 transition-colors"
            title="Safety centre"
          >
            <Shield className="w-5 h-5 text-gray-900" fill="currentColor" />
            <span className="text-gray-900 font-semibold text-[15px]">Safety</span>
          </button>
        </div>
      )}

      {/* ─── Tracking-screen map controls ──────────────────────────────────────
          The pair above are the idle/home versions: dark glass circles that read
          well over the dark idle map. They're hidden during a ride (activeTab
          checks plus !navigationMode), which left the tracking map - the screen
          a rider stares at longest - with no Safety button and no way back to
          the driver after a pan.

          These are their daylight counterparts, styled for the white map the way
          the reference does it: solid white, real shadow, dark glyphs. Safety is
          a labelled pill rather than a bare circle, because on this screen it is
          the one control that has to be findable without looking for it.

          Both anchor to the measured height of the banner + panel stack, so they
          ride up and down with the sheet instead of being swallowed by it. */}
      {navigationMode && activeRideId && isTracking && !sidebarOpen && !showChat && (
        <>
          <div className="fixed left-4 z-[150] pointer-events-auto" style={{ bottom: `${trackingStackHeightPx + 16}px` }}>
            <button
              onClick={() => setActiveTab('emergency')}
              className="h-12 pl-4 pr-5 rounded-full bg-white flex items-center gap-2.5 shadow-[0_4px_16px_rgba(0,0,0,0.22)] active:bg-gray-100 transition-colors"
              title="Safety centre"
            >
              {/* Filled shield, matching the reference - an outline glyph reads
                  as decoration at this size, a solid one reads as a button. */}
              <Shield className="w-5 h-5 text-gray-900" fill="currentColor" />
              <span className="text-gray-900 font-semibold text-[15px]">Safety</span>
            </button>
          </div>
          <div className="fixed right-4 z-[150] pointer-events-auto" style={{ bottom: `${trackingStackHeightPx + 16}px` }}>
            <button
              onClick={() => {
                const m = mapRef.current;
                if (!m) return;
                // Frame the whole live picture - the driver AND wherever they're
                // headed next - rather than snapping to the rider's own pin,
                // which is what the idle recentre does. During a pickup that's
                // the rider; once rolling it's the destination.
                try {
                  const b = new google.maps.LatLngBounds();
                  let any = false;
                  if (driverLivePos) { b.extend(driverLivePos); any = true; }
                  const target = rideStatus === 'in_progress' ? dropoff : pickup;
                  if (target) { b.extend({ lat: target.lat, lng: target.lng }); any = true; }
                  if (!any) return;
                  // Bottom padding clears the sheet; without it fitBounds centres
                  // on the full screen and parks half the route behind the panel.
                  m.fitBounds(b, { top: 120, bottom: trackingStackHeightPx + 48, left: 60, right: 60 });
                } catch {}
                userZoomedRef.current = false;
                setMapPanned(false);
              }}
              className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-[0_4px_16px_rgba(0,0,0,0.22)] active:bg-gray-100 transition-colors"
              title="Recentre"
              aria-label="Recentre the map"
            >
              {/* Crosshair-with-centre-dot, the same glyph the reference uses -
                  distinct from the plain Crosshair on the idle map, which has no
                  dot and reads as "target" rather than "my location". */}
              <LocateFixed className="w-[22px] h-[22px] text-gray-900" />
            </button>
          </div>
        </>
      )}

      {/* SOS countdown / sent modal - armed by shake or the Emergency screen button */}
      <AnimatePresence>
        {(sosCountdown !== null || sosSent) && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/80 z-[200] flex items-center justify-center p-4 pointer-events-auto">
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} transition={{ duration: 0.18, ease: 'easeOut' }}
              className="bg-safaricab-dark rounded-3xl p-6 w-full max-w-sm text-center border-2 border-red-500">
              {sosCountdown !== null ? (
                <>
                  <div className="w-20 h-20 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                    <span className="text-white font-black text-3xl">{sosCountdown}</span>
                  </div>
                  <h3 className="text-white font-bold text-xl mb-2">Sending emergency alert…</h3>
                  <p className="text-white/60 text-sm mb-6">
                    We'll text your emergency contacts your location &amp; trip details, and alert our safety team. Cancel now if this was a mistake.
                  </p>
                  <button onClick={fireSos} className="w-full bg-red-600 text-white font-black py-4 rounded-2xl text-lg mb-3">
                    Send now
                  </button>
                  <button onClick={cancelSos} className="w-full bg-white/10 text-white py-3 rounded-2xl text-sm font-semibold">Cancel</button>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <ShieldAlert className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-white font-bold text-xl mb-2">Alert sent</h3>
                  <p className="text-white/60 text-sm mb-6">
                    Your emergency contacts and our safety team have been notified with your location.
                  </p>
                  <button onClick={() => { window.location.href = 'tel:999'; }} className="w-full bg-red-600 text-white font-black py-4 rounded-2xl text-lg mb-3 flex items-center justify-center gap-2">
                    <Phone className="w-5 h-5" /> Call 999
                  </button>
                  <button onClick={() => setSosSent(false)} className="w-full text-white/50 py-2 text-sm">Close</button>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* All drivers busy - prominent, clear result of a search that found no
          driver (instead of the searching card silently disappearing). */}
      <AnimatePresence>
        {noDriversModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/80 z-[200] flex items-center justify-center p-4 pointer-events-auto">
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} transition={{ duration: 0.18, ease: 'easeOut' }}
              className="bg-safaricab-dark rounded-3xl p-6 w-full max-w-sm text-center border border-white/10">
              <div className="w-16 h-16 bg-safaricab-gold/15 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-safaricab-gold" />
              </div>
              <h3 className="text-white font-bold text-xl mb-2">All drivers are busy</h3>
              <p className="text-white/60 text-sm mb-6">Seems all drivers are currently busy. Please try again in a moment.</p>
              <button onClick={() => setNoDriversModal(false)} className="w-full bg-safaricab-gold text-black font-bold py-4 rounded-2xl text-lg">
                Try again
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Ride results + bottom sheet */}
      {/* justify-end docks the sheet to the bottom of the shell; padding the
          container by the keyboard's height lifts everything in it above the
          keyboard while one of its fields is being typed into, and drops straight
          back when it closes. See keyboardPx above. */}
      <div
        className="relative z-20 flex-1 flex flex-col justify-end pointer-events-none"
        style={keyboardPx ? { paddingBottom: keyboardPx } : undefined}
      >
        {/* Selected locations display */}
        {pickup && dropoff && !isLoading && rideResults.length > 0 && (
          <div className="px-4 mb-2 pointer-events-auto">
            <div className="bg-black/40 backdrop-blur-sm rounded-xl px-4 py-2 text-sm">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
                <span className="text-white/90 truncate">{pickup.address?.split(',')[0] || 'Pickup'}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />
                <span className="text-white/90 truncate">{(dropoff as any).name || dropoff.address?.split(',')[0] || 'Dropoff'}</span>
              </div>
              {rideResults[0] && (
                <div className="flex items-center gap-3 mt-1.5 pt-1.5 border-t border-white/10 text-xs text-white/60">
                  <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {rideResults[0].price.distance} km</span>
                  <span>⏱️ ~{rideResults[0].price.duration} min</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Full-screen Search Overlay - ONE solid opaque dark layer covering the
            WHOLE screen, including behind the keyboard. The iOS keyboard is
            translucent and samples whatever is behind it, so keeping a solid dark
            surface there is what stops it picking up the gold dashboard as you
            scroll (this is what makes it behave like Android). The interactive
            content is capped to the visual-viewport height so the search box sits
            above the keyboard and never scrolls up under the notch, while the
            panel's dark background fills the area the keyboard overlaps. */}
        {searchFocused && (
          <motion.div
            // No enter/exit animation - the overlay appears instantly (no slide or
            // blink). touch-none: the panel can't be panned/dragged, so on iPhone
            // the whole overlay no longer scrolls/bounces; only the list below
            // (touch-pan-y) scrolls. Taps still work - touch-action gates pan/zoom.
            // height 150vh (anchored at top): when iOS shifts this fixed panel up
            // as the keyboard opens, the extra dark below the screen keeps covering
            // the viewport, so the momentary shift can't flash the gold WebView
            // background beneath. The interactive content stays capped to the
            // visible area (inner wrapper), so nothing else changes.
            style={{ height: '150vh', bottom: 'auto' }}
            className="fixed inset-0 z-50 bg-white pointer-events-auto touch-none"
          >
            <div
              className="flex flex-col overscroll-contain"
              style={{
                paddingTop: 'max(var(--sai-top), 14px)',
                paddingBottom: 'var(--sai-bottom)',
                // Cap the content to the visible area above the keyboard; the
                // solid panel behind still fills the rest of the screen.
                height: vvRect ? `${vvRect.height}px` : '100dvh',
              }}
            >
            <div className="px-4 pt-2 pb-3">
              {/* Header: back + title + jump-to-map-pin shortcut */}
              <div className="flex items-center justify-between mb-4">
                <button onClick={() => { setSearchFocused(false); setDropoffSuggestions([]); setPickupSuggestions([]); setAddStopMode(false); setEditDropoffMode(false); setActiveSearchField('dropoff'); }} className="text-gray-900 p-1 -ml-1">
                  <ArrowLeft className="w-6 h-6" />
                </button>
                <h2 className="text-gray-900 font-bold text-lg">{addStopMode ? 'Add a stop' : editDropoffMode ? 'New drop-off' : 'Enter Destination'}</h2>
                <button
                  onClick={() => {
                    const target = !addStopMode && !editDropoffMode ? activeSearchField : 'dropoff';
                    setPinTarget(target);
                    setPinCenter(target === 'pickup' && pickup ? { lat: pickup.lat, lng: pickup.lng } : null);
                    setPinMode(true);
                    setSearchFocused(false);
                  }}
                  className="text-gray-900 p-1 -mr-1"
                  title="Drop pin on map"
                >
                  <MapPin className="w-6 h-6" />
                </button>
              </div>

              {!addStopMode && !editDropoffMode ? (
                <div className="flex gap-3">
                  {/* Pin + connecting line, spanning both rows */}
                  <div className="flex flex-col items-center" style={{ width: 12 }}>
                    <span className="w-3 h-3 rounded-full border-[3px] border-blue-500 bg-white flex-shrink-0 mt-4" />
                    <span className="flex-1 w-px bg-gray-200 my-1" style={{ minHeight: 20 }} />
                    <span className="w-3 h-3 rounded-full border-[3px] border-green-500 bg-white flex-shrink-0 mb-4" />
                  </div>
                  <div className="flex-1 min-w-0 space-y-2">
                    <div className="flex items-center gap-2">
                      <div className={`flex-1 min-w-0 flex items-center gap-2 bg-gray-100 rounded-xl px-3.5 py-3 border ${activeSearchField === 'pickup' ? 'border-safaricab-gold/50' : 'border-transparent'}`}>
                        <input
                          ref={pickupSearchInputRef}
                          value={pickupQuery}
                          onFocus={() => setActiveSearchField('pickup')}
                          onChange={(e) => handlePickupSearch(e.target.value)}
                          placeholder="Detecting your location…"
                          className="bg-transparent text-gray-900 flex-1 min-w-0 outline-none text-sm"
                          style={{ fontSize: '16px' }}
                        />
                        {activeSearchField === 'pickup' && pickupQuery.length > 0 && (
                          <button onClick={() => handlePickupSearch('')} className="text-gray-400 flex-shrink-0">
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <button onClick={addStop} className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-xl bg-gray-100 active:bg-gray-200 transition-colors" title={t('add_stop')}>
                        <Plus className="w-5 h-5 text-safaricab-gold" />
                      </button>
                    </div>
                    {/* Stop inputs - "+" above creates one of these right here,
                        between pickup and destination, instead of silently
                        adding to a list with no visible feedback. */}
                    {stops.map((stop, index) => (
                      <div key={index} className="relative flex items-center gap-2">
                        <div className="flex-1 min-w-0 flex items-center gap-2 bg-gray-100 rounded-xl px-3.5 py-3">
                          <div className="w-5 h-5 rounded-full bg-safaricab-gold/20 flex items-center justify-center flex-shrink-0">
                            <span className="text-safaricab-gold text-[10px] font-bold">{index + 1}</span>
                          </div>
                          <input
                            value={stopQueries[index] || ''}
                            onChange={(e) => handleStopSearch(index, e.target.value)}
                            placeholder={`Stop ${index + 1}`}
                            className="bg-transparent text-gray-900 flex-1 min-w-0 outline-none text-sm"
                            style={{ fontSize: '16px' }}
                          />
                          {stopQueries[index] && (
                            <button onClick={() => { handleStopSearch(index, ''); setStops(p => { const n = [...p]; n[index] = null; return n; }); }} className="text-gray-400 flex-shrink-0">
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        <button onClick={() => removeStop(index)} className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-xl bg-red-50 active:bg-red-100 transition-colors">
                          <Minus className="w-4 h-4 text-red-500" />
                        </button>
                        {(stopSuggestions[index] || []).length > 0 && (
                          <div className="absolute z-30 mt-1 bg-white rounded-xl overflow-hidden border border-gray-100 shadow-lg max-h-40 overflow-y-auto" style={{ left: 0, right: '3.5rem', top: '100%' }}>
                            {stopSuggestions[index].map((loc, i) => (
                              <button key={i} onClick={() => selectStop(index, loc)} className="w-full text-left px-4 py-3 text-gray-900 text-sm hover:bg-gray-50 border-b border-gray-100 truncate">{loc.address}</button>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                    <div className="flex items-center gap-2">
                      <div className={`flex-1 min-w-0 flex items-center gap-2 bg-gray-100 rounded-xl px-3.5 py-3 border ${activeSearchField === 'dropoff' ? 'border-safaricab-gold/50' : 'border-transparent'}`}>
                        <input
                          ref={searchInputRef}
                          value={dropoffQuery}
                          onFocus={() => setActiveSearchField('dropoff')}
                          onChange={(e) => handleDropoffSearch(e.target.value)}
                          placeholder="Enter destination point"
                          className="bg-transparent text-gray-900 flex-1 min-w-0 outline-none text-sm"
                          style={{ fontSize: '16px' }}
                        />
                        {activeSearchField === 'dropoff' && dropoffQuery.length > 0 && (
                          <button onClick={() => handleDropoffSearch('')} className="text-gray-400 flex-shrink-0">
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <button
                        onClick={swapPickupDropoff}
                        className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-xl bg-gray-100 active:bg-gray-200 transition-colors"
                        title="Swap pickup and destination"
                      >
                        <ArrowUpDown className="w-5 h-5 text-gray-600" />
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3 bg-gray-100 rounded-xl px-4 py-3.5">
                  <Navigation className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <input
                    ref={searchInputRef}
                    value={dropoffQuery}
                    onChange={(e) => handleDropoffSearch(e.target.value)}
                    placeholder={addStopMode ? 'Search a stop to add...' : 'Search new drop-off...'}
                    className="bg-transparent text-gray-900 flex-1 outline-none text-base"
                    style={{ fontSize: '16px' }}
                  />
                </div>
              )}
            </div>

            {/* Suggestions List */}
            <div className="flex-1 overflow-y-auto overscroll-contain touch-pan-y px-4">
              {isSearching && (
                <div className="flex items-center gap-2 py-4 text-gray-500 text-sm">
                  <div className="w-4 h-4 border-2 border-safaricab-gold border-t-transparent rounded-full animate-spin" />
                  Searching...
                </div>
              )}
              {!isSearching && activeSearchQuery.length < 2 && (
                <div className="py-2">
                  {settingPlace && (
                    <p className="text-safaricab-gold text-xs mb-2">Search and pick your {settingPlace === 'home' ? 'Home' : 'Work'} address</p>
                  )}
                  <div className="flex gap-2 mb-1">
                    {(['home', 'work'] as const).map((slot) => {
                      const list = savedPlaces[slot] || [];
                      return (
                        <button key={slot} onClick={() => setExpandSlot(expandSlot === slot ? null : slot)}
                          className={`flex-1 flex items-center gap-2 rounded-xl px-3 py-3 text-left border ${expandSlot === slot ? 'border-safaricab-gold bg-safaricab-gold/10' : 'border-gray-200 bg-gray-50'}`}>
                          <span className="text-lg flex-shrink-0">{slot === 'home' ? '🏠' : '💼'}</span>
                          <div className="min-w-0">
                            <p className="text-gray-900 text-sm font-medium">{slot === 'home' ? 'Home' : 'Work'}</p>
                            <p className="text-gray-400 text-xs truncate">{list.length ? `${list.length} saved` : 'None saved'}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  {expandSlot && (
                    <div className="mb-1">
                      {(savedPlaces[expandSlot] || []).length === 0 ? (
                        <p className="text-gray-400 text-xs py-2">No {expandSlot} places yet - add them from the menu → Saved Places.</p>
                      ) : (savedPlaces[expandSlot] || []).map((loc: any, i: number) => (
                        <button key={`${expandSlot}-${i}`} onClick={() => selectActiveSuggestion(loc)} className="w-full flex items-center gap-3 py-3 border-b border-gray-100 text-left">
                          <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-base">{expandSlot === 'home' ? '🏠' : '💼'}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-gray-900 text-sm font-medium truncate">{loc.name || loc.address?.split(',')[0]}</p>
                            <p className="text-gray-400 text-xs truncate">{loc.address}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {!isSearching && activeSearchQuery.length < 2 && recentPlaces.length > 0 && (
                <div className="py-2">
                  {recentPlaces.map((loc, i) => (
                    <button
                      key={`recent-${i}`}
                      onClick={() => selectActiveSuggestion(loc)}
                      className="w-full flex items-center gap-3 py-3.5 border-b border-gray-100 text-left"
                    >
                      <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-4 h-4 text-gray-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-900 text-sm font-medium truncate">{loc.name || loc.address?.split(',')[0]}</p>
                        <p className="text-gray-400 text-xs truncate">Recently Visited</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {activeSearchSuggestions.length > 0 && (
                <div className="py-2">
                  <p className="text-gray-400 text-xs mb-2">{t('suggestions_near_you')}</p>
                  {activeSearchSuggestions.map((loc, i) => (
                    <button
                      key={i}
                      onClick={() => selectActiveSuggestion(loc)}
                      className="w-full flex items-center gap-3 py-3.5 border-b border-gray-100 text-left"
                    >
                      <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 text-base">
                        {loc.icon && loc.icon !== '📍' ? loc.icon : <MapPin className="w-4 h-4 text-gray-500" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-900 text-sm font-medium truncate">{loc.name || loc.address?.split(',')[0]}</p>
                        <p className="text-gray-400 text-xs truncate">{loc.address}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {!isSearching && activeSearchQuery.length >= 2 && activeSearchSuggestions.length === 0 && (
                <p className="text-gray-400 text-sm text-center py-8">{t('no_results')}</p>
              )}
            </div>
            </div>
          </motion.div>
        )}

        {/* Dispatch Queue Panel - waiting for an auto-match */}
        {navigationMode && inQueue && !activeRideId && (
          <div className="bg-safaricab-dark rounded-t-3xl px-4 pt-4 shadow-2xl pointer-events-auto" style={{ paddingBottom: 'var(--sai-dock)' }}>
            <div className="w-10 h-1 bg-white/30 rounded-full mx-auto mb-4" />
            <div className="py-4">
              <h3 className="text-white font-bold text-lg text-center mb-1">Finding you a driver…</h3>
              <p className="text-white/50 text-sm text-center mb-4">
                {queuePosition > 1 ? `You're #${queuePosition} in the queue` : "You're next in line"} - we'll connect you automatically
              </p>
              <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden mb-4">
                <div className="h-full w-2/5 bg-safaricab-gold rounded-full scan-bar" />
              </div>
              <button
                onClick={async () => {
                  const u = JSON.parse(localStorage.getItem('user') || '{}');
                  const token = localStorage.getItem('accessToken');
                  try { await fetch(`${API_URL}/rides/queue/${u.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }); } catch {}
                  setInQueue(false); setNavigationMode(false);
                }}
                className="w-full text-red-400 text-sm font-medium text-center"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Active Ride Status Panel - the handle bar drags/taps to collapse to a
            peek height. Slides up from off-screen on mount (initial y: '100%'
            -> animate y: 0) - without an explicit `initial` Framer Motion has
            nothing to interpolate from on first render and just snaps
            straight to the `animate` values, which is what made confirming a
            ride feel like this panel was jumping/popping into place instead
            of sliding in like the rest of the app's sheets. */}
        {navigationMode && activeRideId && rideStatus !== 'completed' && (
          <div ref={trackingStackRefCb}>
          {/* No-waiting-zone banner - sits between the map and the ride panel,
              exactly where the reference puts it, because that is the last
              thing the eye crosses on its way from the approaching vehicle down
              to "Driver is on the way".

              Only while the driver is still COMING (pending/accepted/arrived).
              Once the trip starts the pickup is history and the banner would be
              telling the rider to hurry to a kerb they're already past.

              Collapsed it is one line, because the fact ("you can't be picked up
              slowly here") is the part that has to land in a glance. Tapping it
              opens the zone's own advice - which differs per zone, and is where
              an airport says "use the pickup bay" rather than "stand at the
              kerb". */}
          {pickupZone && ['pending', 'accepted', 'arrived'].includes(rideStatus) && (
            <div className="pointer-events-auto">
              <AnimatePresence initial={false}>
                {zoneNoticeOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.22, ease: 'easeOut' }}
                    className="overflow-hidden bg-safaricab-gold rounded-t-2xl"
                  >
                    <div className="px-5 pt-4 text-black/75 text-[13px] leading-relaxed">
                      <p className="text-black font-bold text-sm mb-1">{pickupZone.name}</p>
                      <p>{pickupZone.advice}</p>
                      {pickupZone.alt_name && (
                        <p className="mt-2 text-black font-semibold">
                          Your driver will wait at {pickupZone.alt_name}.
                        </p>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              <button
                onClick={() => setZoneNoticeOpen((v) => !v)}
                className={`w-full bg-safaricab-gold px-5 py-3 flex items-center justify-center gap-2 ${zoneNoticeOpen ? '' : 'rounded-t-2xl'}`}
                aria-expanded={zoneNoticeOpen}
              >
                <span className="text-black font-bold text-[15px]">Pickup in no-waiting zone</span>
                <ChevronDown className={`w-5 h-5 text-black transition-transform duration-200 ${zoneNoticeOpen ? 'rotate-180' : ''}`} />
              </button>
            </div>
          )}
          <motion.div
            initial={{ y: '100%' }}
            // Both collapsed and expanded now size to the real measured
            // content (trackingContentHeightPx via ResizeObserver) instead
            // of collapsed being a flat 136px and expanded a flat 75vh with
            // its own overflow-y-auto - that inner scrollbar both looked
            // wrong and ate the swipe gesture (a "swipe up" starting on a
            // scrollable child just scrolls that child instead of reaching
            // the panel-level handlers). Collapsed content now includes "My
            // destination", not just the one-line status, so it's taller
            // than the old flat 136px too - this still shrinks/grows to
            // exactly fit whichever is showing rather than guessing either.
            // The 64px buffer covers the handle bar plus the 24px floor of the
            // padding-bottom below. Where the real inset exceeds that floor (any
            // Android nav bar), the surplus has to be added too or the panel ends
            // one nav bar short and clips its last control - which is why "Cancel
            // trip" was landing under the nav buttons.
            animate={{ y: 0, height: `min(92dvh, ${trackingContentHeightPx + 64 + Math.max(safeBottomPx - 24, 0) + 16}px)` }}
            transition={{ type: 'spring', stiffness: 320, damping: 34 }}
            // White card, matching the reference screenshot exactly (black
            // text, light gray badges/buttons, gold kept as the one accent
            // color) - was the app's usual dark theme, which is why this
            // panel didn't actually resemble the reference despite the
            // layout already matching it structurally.
            className="bg-white rounded-t-3xl px-4 pt-4 shadow-2xl pointer-events-auto overflow-hidden overflow-y-auto overscroll-contain"
            // +16px on top of the inset so "Cancel trip" - the last child of every
            // phase block in here - clears the nav bar with a visible gap instead
            // of sitting flush against it.
            style={{ paddingBottom: 'var(--sai-dock)' }}
          >
            {/* Plain touch handlers instead of Framer Motion's pointer-based
                drag system - dragControls.start(e) needs a genuine
                PointerEvent, and some Android WebView/Chromium builds don't
                fire pointer events reliably, silently breaking both the
                swipe AND (since dragListener={false} routes through it) the
                gesture recognizer entirely. touchstart/touchmove/touchend
                are universally supported. Tap still toggles too, and the hit
                area is generous (not just the thin bar) so it's easy to grab. */}
            <div
              onTouchStart={handleTrackingTouchStart}
              onTouchEnd={handleTrackingTouchEnd}
              onClick={() => setTrackingSheetExpanded((v) => !v)}
              className="-mx-4 -mt-4 px-4 pt-4 pb-3 cursor-grab active:cursor-grabbing touch-none"
              style={{ touchAction: 'none' }}
            >
              <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto" />
            </div>
            {/* Collapsed (default, "swiped down") state shows just a one-line
                status - not the full per-phase content clipped by
                overflow-hidden, which used to leave a half-cut card peeking
                out and no way to tell what it even was. Swipe/tap the handle
                to expand and see the full driver card, map-adjacent details
                and (once expanded) the cancel button. Same collapsed/expanded
                split across every phase - searching, pending, accepted,
                arrived, in_progress. Wrapped in the measured ref so the panel
                above sizes itself to whichever of these two is actually
                showing. */}
            <div ref={trackingContentRefCb}>
            {!trackingSheetExpanded ? (() => {
              const driverFound = isSearchingDriver && /found/i.test(driverDeclinedMsg);
              const summary = isSearchingDriver
                ? { title: driverFound ? 'Driver found' : 'Finding your driver', subtitle: 'Waiting for driver to accept.' }
                : rideStatus === 'pending' && assignedDriverId
                ? { title: 'Driver found!', subtitle: 'Waiting for driver to confirm…' }
                : rideStatus === 'pending'
                ? { title: 'Looking for your driver…', subtitle: 'Please wait while we confirm your ride' }
                : rideStatus === 'accepted'
                ? { title: 'Driver is on the way', subtitle: pickupEtaSec != null ? `Arriving in ${formatEta(pickupEtaSec)}` : 'Watch the map - your driver is approaching' }
                : rideStatus === 'arrived'
                ? { title: 'Your driver has arrived!', subtitle: 'Head outside - your ride is waiting' }
                : rideStatus === 'in_progress'
                ? { title: 'Heading to your destination', subtitle: tripMins ? `${tripMins} away` : 'Sit back and enjoy the ride' }
                : { title: '', subtitle: '' };
              return (
                <>
                  <div
                    onTouchStart={handleTrackingTouchStart}
                    onTouchEnd={handleTrackingTouchEnd}
                    onClick={() => setTrackingSheetExpanded(true)}
                    className={driverFound ? 'py-2 touch-none' : 'py-2 text-center touch-none'}
                    style={{ touchAction: 'none' }}
                  >
                    {driverFound ? (
                      // Same vehicle-icon card as the expanded "Finding your
                      // driver" view below - the collapsed peek used to be
                      // plain text only, giving no visual cue of which ride
                      // type is waiting to be accepted.
                      <div className="flex items-center gap-3 bg-safaricab-gold/10 border border-safaricab-gold/40 rounded-2xl p-3">
                        <div className="w-11 h-11 bg-white rounded-full flex items-center justify-center flex-shrink-0 border border-gray-100">
                          <img src={rideIconMap[selectedRide || ''] || '/icons/rides/motorbike.png'} alt="vehicle" className="w-8 h-8 object-contain" />
                        </div>
                        <div>
                          <p className="text-gray-900 font-bold text-sm">{summary.title}</p>
                          <p className="text-gray-500 text-xs mt-0.5">{summary.subtitle}</p>
                        </div>
                      </div>
                    ) : (
                      <>
                        <h3 className="text-gray-900 font-bold text-base">{summary.title}</h3>
                        {summary.subtitle && <p className="text-gray-500 text-xs mt-1">{summary.subtitle}</p>}
                      </>
                    )}
                  </div>
                  {/* "My destination" now shows even while collapsed - swipe
                      up still reveals everything else (payment method, tips,
                      cancel/edit buttons). Not part of the swipeable div
                      above so its own Add stop/Edit destinations buttons stay
                      independently tappable. */}
                  {dropoff && myDestinationCard}
                </>
              );
            })() : (
            <>
            {isSearchingDriver ? (
              <div className="py-2">
                {/* Clone of the reference sheet: centered title, status line, progress,
                    then the bordered waiting card with the vehicle icon. */}
                <h3 className="text-gray-900 font-bold text-xl text-center mb-2">
                  {/found/i.test(driverDeclinedMsg) ? 'Driver found' : 'Finding your driver'}
                </h3>
                <p className="text-gray-600 text-[15px] mb-1">
                  {driverDeclinedMsg || 'Finding your driver! Waiting for confirmation.'}
                </p>
                {declinedReason && (
                  <p className="text-gray-400 text-xs mb-1">Reason: {declinedReason}</p>
                )}
                <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden mt-2 mb-3">
                  <div className="h-full w-2/5 bg-safaricab-gold rounded-full scan-bar" />
                </div>
                <div className="flex items-center gap-3 bg-safaricab-gold/10 border border-safaricab-gold/40 rounded-2xl p-3">
                  <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                    <img src={rideIconMap[selectedRide || ''] || '/icons/rides/motorbike.png'} alt="vehicle" className="w-11 h-11 object-contain" />
                  </div>
                  <div>
                    <p className="text-gray-900 font-bold text-sm">Waiting for driver to accept.</p>
                    {nearestDriverInfo && <p className="text-gray-500 text-xs mt-0.5">{nearestDriverInfo}</p>}
                  </div>
                </div>
                {waitingTripSummary}
                {/* Let the rider stop the search at any point. Direct cancel (no
                    reason modal) - there's no driver yet to explain a decline to. */}
                <button
                  onClick={confirmCancelRide}
                  className="w-full bg-red-500 text-white font-bold text-base py-4 rounded-full mt-4"
                >
                  Cancel trip
                </button>
              </div>
            ) : rideStatus === 'pending' && assignedDriverId ? (
              <div className="py-3">
                <div className="flex items-center gap-4 mb-3">
                  <div className="flex-shrink-0 w-16 h-16 rounded-full bg-safaricab-gold/10 border-2 border-safaricab-gold/40 flex items-center justify-center overflow-hidden">
                    <img
                      src={isMotorbikeType(selectedRide || '') ? '/icons/dere1.png' : '/icons/dere.png'}
                      alt="vehicle"
                      className={`w-14 h-14 object-contain${isMotorbikeType(selectedRide || '') ? ' icon-gold' : ''}`}
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-gray-900 font-bold text-base">Driver found!</h3>
                    <p className="text-safaricab-gold text-sm">Waiting for driver to confirm…</p>
                  </div>
                </div>
                <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full w-2/5 bg-safaricab-gold/70 rounded-full scan-bar" />
                </div>
                {waitingTripSummary}
              </div>
            ) : rideStatus === 'pending' ? (
              <div className="py-4">
                <h3 className="text-gray-900 font-bold text-lg text-center mb-1">Looking for your driver…</h3>
                <p className="text-gray-500 text-sm text-center mb-3">Please wait while we confirm your ride</p>
                <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full w-2/5 bg-safaricab-gold rounded-full scan-bar" />
                </div>
              </div>
            ) : rideStatus === 'accepted' ? (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-gray-900 font-bold text-lg">
                    {pickupEtaSec != null
                      ? <>Your driver arrives in <span className="text-safaricab-gold">{formatEta(pickupEtaSec)}</span></>
                      : 'Driver is on the way'}
                  </h3>
                  <button onClick={() => shareTrip(activeRideId)} className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0" title="Share trip">
                    <Share2 className="w-4 h-4 text-gray-600" />
                  </button>
                </div>
                {pickupEtaSec == null && (
                  <p className="text-gray-500 text-xs mb-2">Watch the map - your driver is approaching</p>
                )}
                {driverContactCard}
                {waitingTripSummary}
                <button
                  onClick={() => { setEditDropoffMode(true); setDropoffSuggestions([]); setSearchFocused(true); }}
                  className="w-full mt-3 flex items-center justify-center gap-2 bg-gray-100 border border-gray-100 text-gray-900 text-sm py-3 rounded-xl"
                >
                  <Navigation className="w-4 h-4 text-blue-500" /> Edit drop-off location
                </button>
              </div>
            ) : rideStatus === 'arrived' ? (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-gray-900 font-bold text-lg">Your driver has arrived!</h3>
                  <button onClick={() => shareTrip(activeRideId)} className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0" title="Share trip">
                    <Share2 className="w-4 h-4 text-gray-600" />
                  </button>
                </div>
                <p className="text-gray-500 text-xs mb-2">Head outside - your ride is waiting</p>
                {driverContactCard}
                {waitingTripSummary}
              </div>
            ) : rideStatus === 'in_progress' ? (
              <div>
                <div className="flex items-center gap-1 mb-1.5 text-gray-400 text-[10px]">
                  <span className="w-1 h-1 rounded-full bg-red-500 animate-pulse flex-shrink-0" />
                  <span>Audio is being recorded for your safety</span>
                </div>
                {/* "Heading to your destination" through "Contact/Message
                    Driver" - and only that stretch - runs on SafariCab's own dark
                    gold theme instead of the white card the rest of this panel
                    uses, so the live trip and the driver you'd reach mid-ride read
                    as one block. Everything below it (the trip summary, Share my
                    trip, Add a stop, Cancel) stays white; the recording notice
                    above stays outside the card too. */}
                <div className="bg-safaricab-dark border border-safaricab-gold/25 rounded-2xl px-3 pt-3 pb-1 mb-1">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 bg-safaricab-gold/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <Navigation className="w-3.5 h-3.5 text-safaricab-gold" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-bold text-sm">Heading to your destination</h3>
                      {dropoff?.address && (
                        <p className="text-white/70 text-[11px] flex items-center gap-1 truncate">
                          <Navigation className="w-2.5 h-2.5 text-red-500 flex-shrink-0" />
                          <span className="truncate">{dropoff.address}</span>
                        </p>
                      )}
                      <p className="text-white/50 text-[11px]">
                        {tripMins ? <span className="text-safaricab-gold font-semibold">{tripMins} away</span> : 'Sit back and enjoy the ride'}
                      </p>
                    </div>
                    {tripEta && (
                      <div className="bg-safaricab-gold/15 rounded-lg px-2 py-1 text-right flex-shrink-0">
                        <p className="text-white/50 text-[9px]">Arrive by</p>
                        <p className="text-safaricab-gold font-bold text-xs">{tripEta}</p>
                      </div>
                    )}
                  </div>
                  {renderDriverContactCard(true)}
                </div>
                {waitingTripSummary}
                {/* The bottom "Add a stop" and "Edit drop-off" buttons were removed
                    from here - both actions already live in the "My destination"
                    card above ("Add stop" in the route list, "Edit destinations"
                    under it), so this was a second copy of each, further from the
                    route they change. Share my trip stays; it has nowhere else. */}
                <button
                  onClick={() => shareTrip(activeRideId)}
                  className="w-full mt-3 flex items-center justify-center gap-2 bg-gray-100 border border-gray-100 text-gray-900 text-sm py-3 rounded-xl"
                >
                  <Share2 className="w-4 h-4 text-safaricab-gold" /> {t('share_my_trip')}
                </button>
              </div>
            ) : null}
            {/* Cancel is allowed right up until the trip starts (accepted/arrived).
                Once in_progress NOBODY can cancel - no emergency-cancel escape hatch. */}
            {!isSearchingDriver && rideStatus !== 'in_progress' && (
              <button onClick={handleCancelRide} className="w-full bg-red-500 text-white font-bold text-base py-4 rounded-full mt-3">Cancel trip</button>
            )}
            </>
            )}
            </div>
            {/* Cancel Reason Modal */}
            <AnimatePresence>
              {showCancelModal && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="fixed inset-0 bg-black/70 z-[200] flex items-end justify-center p-4"
                  // Same lift as the bottom sheet: this sheet is bottom-docked in
                  // its own fixed overlay, so the "Other" box was landing under
                  // the keyboard with the rider unable to see what they typed.
                  style={keyboardPx ? { paddingBottom: keyboardPx + 16 } : undefined}>
                  {/* White with gold accents, like the trip form, ride list and
                      Confirm Ride sheet - this was the last dark panel left in the
                      booking flow. The Confirm Cancel button stays red: it's the
                      destructive action, and it matches the red "Cancel trip"
                      button that opens this sheet from the white tracking panel. */}
                  <motion.div initial={{ y: 80 }} animate={{ y: 0 }} exit={{ y: 80 }}
                    className="bg-white rounded-3xl px-6 pt-6 w-full max-w-sm shadow-2xl" style={{ paddingBottom: 'var(--sai-dock)' }}>
                    <h3 className="text-gray-900 font-bold text-lg mb-1">Cancel Ride</h3>
                    <p className="text-gray-500 text-sm mb-4">Please tell us why you're cancelling</p>
                    <div className="space-y-2 mb-4">
                      {['Driver is taking too long', 'Changed my mind', 'Wrong pickup location', 'Found another ride', 'Other'].map((r) => (
                        <button key={r} onClick={() => setCancelReason(r)}
                          className={`w-full text-left px-4 py-3 rounded-xl text-sm border transition-colors ${cancelReason === r ? 'bg-safaricab-gold/15 border-safaricab-gold text-gray-900 font-semibold' : 'bg-gray-100 border-gray-200 text-gray-700'}`}>
                          {r}
                        </button>
                      ))}
                      {/* "Other" opens a box to say what actually happened - the
                          typed text is what's saved as the ride's cancel_reason
                          and what the admin dashboard shows next to the trip. */}
                      {cancelReason === 'Other' && (
                        <textarea
                          value={cancelReasonOther}
                          onChange={(e) => setCancelReasonOther(e.target.value)}
                          onFocus={keepFieldVisible}
                          rows={3}
                          maxLength={300}
                          autoFocus
                          placeholder="Tell us what happened"
                          className="w-full bg-gray-100 border border-gray-200 focus:border-safaricab-gold rounded-xl px-4 py-3 text-gray-900 text-sm outline-none resize-none"
                        />
                      )}
                    </div>
                    <button onClick={confirmCancelRide} disabled={!cancelReasonToSend}
                      className="w-full bg-red-500 disabled:bg-red-500/30 text-white font-bold py-3 rounded-2xl mb-2">
                      Confirm Cancel
                    </button>
                    <button onClick={() => { setShowCancelModal(false); setCancelReason(''); setCancelReasonOther(''); }} className="w-full text-gray-500 py-2 text-sm">Keep Ride</button>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
          </div>
        )}

        {/* Chat overlay */}
        <AnimatePresence>
          {showChat && activeRideId && (
            <div className="pointer-events-auto">
              <ChatBox rideId={activeRideId} onClose={() => setShowChat(false)} />
            </div>
          )}
        </AnimatePresence>

        {/* Expanded driver & vehicle detail card */}
        <AnimatePresence>
          {showDriverDetail && assignedDriver && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setShowDriverDetail(false)}
              className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4 pointer-events-auto"
            >
              <motion.div
                initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-md bg-safaricab-card rounded-3xl p-5 border border-white/10 max-h-[85vh] overflow-y-auto"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-bold text-lg">Driver & Vehicle</h3>
                  <button onClick={() => setShowDriverDetail(false)} className="bg-white/10 text-white p-2 rounded-full">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Driver */}
                <div className="flex items-center gap-3 mb-4">
                  {assignedDriver.avatar_url ? (
                    <img src={assignedDriver.avatar_url} className="w-16 h-16 rounded-full object-cover border-2 border-safaricab-gold/40" alt={assignedDriver.full_name} />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-safaricab-gold/20 flex items-center justify-center text-safaricab-gold font-bold text-2xl border-2 border-safaricab-gold/40">
                      {assignedDriver.full_name?.charAt(0)}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-bold text-lg">{assignedDriver.full_name}</p>
                    <div className="flex items-center gap-1 mt-0.5">
                      <Star className="w-4 h-4 text-yellow-400 fill-current" />
                      <span className="text-yellow-400 text-sm font-medium">{assignedDriver.rating ?? '5.0'}</span>
                    </div>
                  </div>
                  {assignedDriver.phone && (
                    <a href={`tel:${assignedDriver.phone}`} className="bg-green-500/20 text-green-400 p-3 rounded-xl">
                      <Phone className="w-5 h-5" />
                    </a>
                  )}
                </div>

                {(() => {
                  const v = assignedDriver.driver_vehicles?.[0];
                  if (!v) return <p className="text-white/40 text-sm text-center py-4">No vehicle details available.</p>;
                  return (
                    <div className="bg-safaricab-darker rounded-2xl p-4">
                      <div className="flex items-center gap-3 mb-4">
                        {v.photo_front ? (
                          <img src={v.photo_front} alt="vehicle" className="w-16 h-16 rounded-xl object-cover flex-shrink-0" />
                        ) : (
                          <img src={vehicleIcon(v.vehicle_type || v.ride_type)} alt="vehicle" className="w-16 h-16 object-contain" />
                        )}
                        <div className="min-w-0">
                          <p className="text-white font-bold text-base truncate">{[v.make, v.name].filter(Boolean).join(' ') || 'Vehicle'}</p>
                          {v.ride_type && <p className="text-safaricab-gold text-xs uppercase tracking-wider">{v.ride_type}</p>}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-white/5 rounded-xl p-3">
                          <p className="text-white/40 text-[11px] uppercase tracking-wider mb-1">Number Plate</p>
                          <p className="text-white font-bold tracking-wider">{v.plate_number || '-'}</p>
                        </div>
                        <div className="bg-white/5 rounded-xl p-3">
                          <p className="text-white/40 text-[11px] uppercase tracking-wider mb-1">Color</p>
                          <div className="flex items-center gap-2">
                            <span className="w-4 h-4 rounded-full border border-white/30" style={{ backgroundColor: (v.color || '').toLowerCase() || 'transparent' }} />
                            <p className="text-white font-medium">{v.color || '-'}</p>
                          </div>
                        </div>
                        <div className="bg-white/5 rounded-xl p-3">
                          <p className="text-white/40 text-[11px] uppercase tracking-wider mb-1">Passengers</p>
                          <div className="flex items-center gap-1.5">
                            <User className="w-4 h-4 text-white/60" />
                            <p className="text-white font-medium">{v.passengers ?? '-'}</p>
                          </div>
                        </div>
                        <div className="bg-white/5 rounded-xl p-3">
                          <p className="text-white/40 text-[11px] uppercase tracking-wider mb-1">Year</p>
                          <p className="text-white font-medium">{v.year_of_manufacture || '-'}</p>
                        </div>
                        {v.fuel_type && (
                          <div className="bg-white/5 rounded-xl p-3 col-span-2">
                            <p className="text-white/40 text-[11px] uppercase tracking-wider mb-1">Fuel</p>
                            <p className="text-white font-medium capitalize">{v.fuel_type}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                <button onClick={() => setShowDriverDetail(false)} className="w-full mt-4 bg-safaricab-gold text-black font-bold py-3.5 rounded-2xl">
                  Close
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Bottom Sheet - flush to the bottom; the map fills only the space
            above it. Swipe the centre handle up to expand / down to collapse. */}
        {(!navigationMode || !activeRideId || rideStatus === 'completed') && !pinMode && (
        <motion.div
          ref={sheetRefCb}
          animate={{
            // Both the trip-details form (dropoff set, no results yet) and the
            // ride-options list (results in) target the exact same height here -
            // so tapping "Find rides" never animates the sheet's height at all,
            // only the inner content below crossfades/slides. Previously the
            // form side used 'auto', which Framer Motion can't interpolate
            // smoothly against a fixed dvh value - that mismatch was the visible
            // jump on both iOS and Android.
            height: (rideResults.length === 0 && !dropoff)
              // Sized to the actual measured content - just the "Where to?"
              // button when collapsed, heading + search + Recent when
              // expanded (homeContentRef wraps content that changes shape
              // with homeSheetExpanded, so this single measurement already
              // tracks both states) - plus a fixed buffer for the handle bar
              // and body padding around it, capped at 70dvh. Previously a
              // flat 88dvh/25dvh guess per state, which left a lot of empty
              // white space in BOTH states once the service-tile carousel
              // was removed and Recent got capped at 3.
              // Badge inside the cap here too - same reason as the trip-form
              // branch below, and this is the sheet that now also carries any
              // live ride tickets, so it reaches the ceiling far more easily.
              ? `min(70dvh, ${homeContentHeightPx + 64 + sheetBottomPx + discountBadgeExtraPx}px)`
              // Same idea for the trip-details form / ride-options list -
              // both read the one shared measurement (formOrResultsRef gets
              // reattached to whichever is mounted), so the height never
              // changes across the Find-rides transition (still avoids the
              // jump this was written to fix) while no longer leaving a dark
              // gap below content shorter than the old flat 620px cap. Cap
              // raised from 72dvh to 92dvh - at 72dvh the pickup/dropoff/add
              // stop/Find-rides form was routinely a few px taller than the
              // cap on real phones, so "Find rides" sat just below the fold
              // and needed a scroll to reach; 92dvh fits the normal form at a
              // glance while still leaving a sliver of map visible up top.
              //
              // Ride-options never collapses to the bare 132px handle-only
              // strip the trip-form uses - swiping down should still leave
              // the top 2 ride cards and the "Select <ride>" footer visible
              // (a bad-UX regression otherwise: users had no way to know
              // there even was a button without swiping up first). Instead
              // `sheetExpanded` only changes what's rendered INSIDE (top 2 vs
              // a nested-scrollbox holding all of them - see the ride-card
              // block below), and the sheet just measures whichever is
              // showing, same as every other state here. formOrResultsHeightPx
              // no longer includes the "Select <ride>" button itself (it's a
              // flex-shrink-0 footer outside the measured/scrollable area,
              // always pinned right after the list) - add its height back in
              // here so the sheet still grows to make room for it.
              // Both pinned footers ("Select <ride>", "Find rides") sit outside
              // the measured/scrollable area, so their ~92px has to be added back
              // here or the sheet won't grow to make room for them. The collapsed
              // trip-form peek includes it too: the footer is always mounted once
              // a dropoff is set, and without the extra height it would squeeze
              // the form above it down to nothing.
              // discountBadgeExtraPx belongs on EVERY branch, not just the home
              // one: discountBadgeVisible only requires rideResults to be empty,
              // so the badge renders over the trip form too - and it's a
              // flex-shrink-0 first child, so its 44px came straight out of the
              // pinned "Find rides" footer at the bottom. That was the button
              // being clipped mid-render with a gold sliver below it.
              // The badge belongs INSIDE the cap, not added on after it. As
              // `calc(min(92dvh, X) + 44px)` a sheet that hit the clamp became
              // 92dvh + 44px, i.e. taller than the screen - and since the sheet
              // is bottom-anchored with a pinned footer, the overflow came off
              // the bottom and put "Find rides" underneath the phone's nav bar,
              // unreachable. The cap has to be the real ceiling for the whole
              // sheet; the body below scrolls in whatever room is left.
              // 62dvh, not 80: the ride-options sheet is the one screen where the
              // MAP has a job to do - it is showing the route you are about to
              // pay for, with how long until a car arrives and how long the trip
              // takes. At 80dvh the map got a fifth of the screen, the route had
              // to be zoomed out into a letterbox to fit, and the whole thing
              // read as broken rather than informative. 62 gives the map ~38%,
              // matching the reference app this was modelled on. Nothing is lost:
              // the list scrolls inside the smaller sheet exactly as it already
              // did, with the same pinned "Select <ride>" footer.
              : (rideResults.length > 0
                  ? `min(62dvh, ${formOrResultsHeightPx + 64 + sheetBottomPx + (selectedRideData ? 92 : 0)}px)`
                  : (sheetExpanded
                      ? `min(92dvh, ${formOrResultsHeightPx + 64 + sheetBottomPx + 92 + discountBadgeExtraPx}px)`
                      : `${132 + sheetBottomPx + 92 + discountBadgeExtraPx}px`)),
          }}
          transition={{ type: 'spring', stiffness: 320, damping: 34 }}
          className="bg-white rounded-t-3xl shadow-2xl pointer-events-auto flex flex-col overflow-hidden"
          // Floored at 24px to match sheetBottomPx above. The pinned footer adds
          // its own 16px, so the button clears the boundary by that much again.
          style={{ paddingBottom: 'max(var(--sai-bottom), 24px)' }}
        >
          {/* Discount badge - a real first child of the sheet (not a floating
              overlay guessing/measuring the sheet's position), so it's flush with
              the sheet's top edge by construction - no gap, no overlap, and it
              automatically follows the sheet's rounded top corners (parent's
              overflow-hidden) and is hidden whenever the sheet itself is (e.g.
              behind the side menu), instead of bleeding over other screens.
              Prefers a manually-entered discount code's actual % once applied;
              otherwise falls back to the admin's system-wide offer (liveOfferPct),
              known immediately. Condition mirrors discountBadgeVisible above,
              which also grows the sheet's fixed-height states to make room for
              this row instead of it squeezing the actual content down. */}
          {discountBadgeVisible && (
            <div className="flex-shrink-0 flex items-center justify-center gap-1.5 bg-safaricab-gold text-black text-sm font-bold py-2.5">
              <Tag className="w-4 h-4" />
              {appliedDiscount > 0
                ? (selectedRideData?.price?.estimatedPrice
                  ? `${Math.round((appliedDiscount / selectedRideData.price.estimatedPrice) * 100)}% discount applied`
                  : `KES ${appliedDiscount} discount applied`)
                : `${liveOfferPct}% discount applied`}
            </div>
          )}
          {/* Centre handle - drag/swipe to expand or collapse (tap toggles too).
              The idle "discovery" sheet (no dropoff picked yet) tracks its own
              homeSheetExpanded flag - separate from sheetExpanded, which is the
              ride-RESULTS sheet's collapse state - so this never disturbs that.
              dragListener is off here - dragControls.start() is fired both by
              this bar AND by the discovery view's peek content below, so the
              whole visible peek area (not just this 24px strip) can start the
              gesture instead of the content div's scroll grabbing it first. */}
          <motion.div
            drag="y"
            dragListener={false}
            dragControls={sheetDragControls}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={0.4}
            onDragEnd={(_, info) => {
              const expand = info.offset.y < -40 || info.velocity.y < -300;
              const collapse = info.offset.y > 40 || info.velocity.y > 300;
              if (rideResults.length === 0 && !dropoff) {
                if (expand) setHomeSheetExpanded(true);
                else if (collapse) setHomeSheetExpanded(false);
              } else {
                if (expand) setSheetExpanded(true);
                // The trip-details form (dropoff picked, no results yet) can't be
                // swiped down any more - collapsing it left a 132px sliver with
                // "Find rides" off-screen, which read as the app refusing to
                // search. It stays up until the rider actually finds rides; the
                // ride-options list below still collapses (it keeps the top cards
                // and its Select footer visible when it does).
                else if (collapse && rideResults.length > 0) setSheetExpanded(false);
              }
            }}
            className="flex-shrink-0"
          >
            <div
              onPointerDown={(e) => sheetDragControls.start(e)}
              // Tap-to-toggle follows the same rule as the swipe above: it can't
              // collapse the trip-details form either, or the tap would undo what
              // disabling the swipe-down was for.
              onClick={() => {
                if (rideResults.length === 0 && !dropoff) setHomeSheetExpanded((v) => !v);
                else if (rideResults.length > 0) setSheetExpanded((v) => !v);
              }}
              className="pt-3 pb-2 cursor-grab active:cursor-grabbing touch-none"
            >
              <div className="w-10 h-1.5 rounded-full mx-auto bg-gray-300" />
            </div>
          </motion.div>
          {/* Scrollable sheet body */}
          <div className="px-4 pb-4 flex-1 overflow-y-auto overscroll-contain">

          {/* Idle discovery view: no destination picked yet - Bolt-style
              service tiles + "Where to?" launcher + recent destinations. Once
              a dropoff is picked, this gives way to the existing trip-details
              form below (pickup review, stops, Find Rides). */}
          {rideResults.length === 0 && !dropoff && (
            <div ref={homeContentRefCb} className="space-y-4 pt-1">
              {/* Any approved ride ticket sits ABOVE "where would you like to
                  go?", because for someone holding one that question is already
                  answered - her company approved this exact trip and all that is
                  left is to call the car. Renders nothing when there are none,
                  which is every non-corporate rider. Deliberately outside the
                  drag hitbox below: a swipe on the card must not be stolen by
                  the sheet, and its buttons need real taps. */}
              {activeTab === 'home' && !navigationMode && !activeRideId && (
                <CorporateTickets
                  key={ticketsRefresh}
                  onRedeemed={(rideId) => {
                    // Exactly what the corporate approval poll does when a trip
                    // is approved - adopt the ride and drop into the normal
                    // searching-for-a-driver UI.
                    setActiveRideId(rideId);
                    setAssignedDriver(null);
                    setAssignedDriverId('');
                    setRideStatus('searching');
                    setIsSearchingDriver(true);
                    setNavigationMode(true);
                  }}
                />
              )}

              {/* Drag hitbox extends over this whole group (not just the 24px
                  handle bar above) - a swipe starting anywhere on the heading,
                  search launcher, or tiles lifts/collapses the sheet. Recent
                  destinations below stays a plain scrollable list instead, so
                  a long list can still be scrolled once the sheet is expanded. */}
              <div onPointerDown={(e) => sheetDragControls.start(e)} style={{ touchAction: 'none' }} className="space-y-4">
                {/* Collapsed (swiped down) shows the "Where to?" launcher only -
                    heading, service tiles and Recent all wait for the sheet to
                    be swiped up. */}
                {homeSheetExpanded && (
                  <h1 className="text-gray-900 font-extrabold text-2xl leading-tight">Where would<br />you like to go?</h1>
                )}

                <button
                  onClick={() => { setPreselectRideType(null); setScheduleIntent(false); setSearchFocused(true); }}
                  className="w-full flex items-center gap-3 bg-gray-100 rounded-2xl px-4 py-3.5 active:bg-gray-200 transition-colors"
                >
                  <Search className="w-5 h-5 text-gray-500 flex-shrink-0" />
                  <span className="text-gray-900 font-semibold flex-1 text-left truncate">{t('where_to')}</span>
                </button>

              </div>

              {homeSheetExpanded && recentPlaces.length > 0 && (
                <div className="pt-1">
                  <p className="text-gray-400 text-[11px] font-semibold uppercase tracking-wider mb-2">Recent</p>
                  <div className="space-y-1">
                    {recentPlaces.slice(0, 3).map((loc, i) => (
                      <button
                        key={i}
                        onClick={async () => { const r = await resolveLocation(loc); setDropoff(r); setDropoffQuery((r as any).name || r.address); saveRecentPlace(r); }}
                        className="w-full flex items-center gap-3 py-2.5 text-left active:bg-gray-50 rounded-xl -mx-1 px-1"
                      >
                        <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                          <Clock className="w-4 h-4 text-gray-500" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-gray-900 text-sm font-semibold truncate">{loc.name || loc.address?.split(',')[0]}</p>
                          <p className="text-gray-400 text-xs truncate">{loc.address}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Location Inputs once a dropoff is picked, and the ride-options list
              once "Find rides" resolves - crossfade+slide between the two
              instead of the instant swap that used to accompany the outer
              sheet's height jump (see the `animate` height comment above). */}
          <AnimatePresence mode="wait">
          {rideResults.length === 0 && dropoff && (
            <motion.div ref={formOrResultsRefCb} key="trip-form" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.28, ease: 'easeOut' }} className="space-y-3">
              <div className="relative">
                {/* Pickup - read-only GPS normally; editable (type or drop a pin)
                    when booking for someone else, OR when GPS detection failed so
                    the rider can still set their pickup manually. */}
                {(bookingForOther || locationFailed) ? (
                  <div className="relative">
                    {locationFailed && !bookingForOther && (
                      <p className="text-amber-400 text-xs mb-1.5">Couldn't detect your location - enter your pickup below or drop a pin.</p>
                    )}
                    <div className="flex items-start gap-2">
                      <div className="flex-1 flex items-center gap-3 bg-gray-100 rounded-xl px-4 py-3.5 border border-safaricab-gold/30">
                        <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-60" />
                          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
                        </span>
                        <input
                          value={pickupQuery}
                          onChange={(e) => handlePickupSearch(e.target.value)}
                          placeholder={bookingForOther ? "Passenger's pickup location" : 'Enter your pickup location'}
                          className="bg-transparent text-gray-900 flex-1 outline-none text-sm"
                        />
                      </div>
                      <button
                        onClick={() => { setPinTarget('pickup'); setPinCenter(pickup ? { lat: pickup.lat, lng: pickup.lng } : null); setPinMode(true); }}
                        className="mt-1 p-2.5 bg-gray-100 rounded-xl border border-safaricab-gold/30 flex-shrink-0"
                        title="Drop pin on map"
                      >
                        <MapPin className="w-5 h-5 text-safaricab-gold" />
                      </button>
                    </div>
                    {pickupSuggestions.length > 0 && (
                      <div className="absolute z-30 mt-1 bg-gray-100 rounded-xl overflow-hidden border border-gray-200 max-h-48 overflow-y-auto" style={{ left: 0, right: '3.5rem' }}>
                        {pickupSuggestions.map((loc, i) => (
                          <button key={i} onClick={() => selectPickup(loc)} className="w-full text-left px-4 py-3 text-gray-900 text-sm hover:bg-gray-50 border-b border-gray-100">{loc.address}</button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-3 bg-gray-100 rounded-xl px-4 py-3.5">
                    <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-60" />
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-400 text-[10px] uppercase tracking-wide">Current location</p>
                      <p className="text-gray-900 text-sm truncate">{pickupQuery || 'Detecting your location…'}</p>
                    </div>
                  </div>
                )}
              </div>
              {/* Stop inputs */}
              {stops.map((stop, index) => (
                <div key={index} className="relative flex items-start gap-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 bg-gray-100 rounded-xl px-4 py-3.5 border border-safaricab-gold/20">
                      <div className="w-5 h-5 rounded-full bg-safaricab-gold/30 flex items-center justify-center flex-shrink-0">
                        <span className="text-safaricab-gold text-[10px] font-bold">{index + 1}</span>
                      </div>
                      <input
                        value={stopQueries[index] || ''}
                        onChange={(e) => handleStopSearch(index, e.target.value)}
                        placeholder={`Stop ${index + 1}`}
                        className="bg-transparent text-gray-900 flex-1 outline-none text-sm"
                      />
                      {stopQueries[index] && <X className="w-4 h-4 text-gray-500 cursor-pointer" onClick={() => { handleStopSearch(index, ''); setStops(p => { const n = [...p]; n[index] = null; return n; }); }} />}
                    </div>
                    {(stopSuggestions[index] || []).length > 0 && (
                      <div className="absolute z-30 w-full mt-1 bg-gray-100 rounded-xl overflow-hidden border border-gray-200 max-h-40 overflow-y-auto" style={{ left: 0, right: '2.5rem' }}>
                        {stopSuggestions[index].map((loc, i) => (
                          <button key={i} onClick={() => selectStop(index, loc)} className="w-full text-left px-4 py-3 text-gray-900 text-sm hover:bg-gray-50 border-b border-gray-100 truncate">{loc.address}</button>
                        ))}
                      </div>
                    )}
                  </div>
                  <button onClick={() => removeStop(index)} className="mt-3 p-1.5 bg-red-500/20 rounded-lg flex-shrink-0">
                    <Minus className="w-4 h-4 text-red-400" />
                  </button>
                </div>
              ))}

              <div className="relative flex items-start gap-2">
                <div className="flex-1 flex items-center gap-3 bg-gray-100 rounded-xl px-4 py-3.5 cursor-pointer" onClick={() => setSearchFocused(true)}>
                  <Navigation className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <input value={dropoffQuery} onChange={(e) => handleDropoffSearch(e.target.value)} onFocus={() => { if (dropoffQuery.length > 2) handleDropoffSearch(dropoffQuery); }} placeholder={isSearching ? t('searching') : t('where_to')} className="bg-transparent text-gray-900 flex-1 outline-none text-sm" />
                  {dropoffQuery && <X className="w-4 h-4 text-gray-500 cursor-pointer" onClick={(e) => { e.stopPropagation(); setDropoffQuery(''); setDropoffSuggestions([]); }} />}
                </div>
                <button
                  onClick={() => { setPinTarget('dropoff'); setPinMode(true); setPinCenter(null); if (map) { const c = map.getCenter(); if (c) setPinCenter({ lat: c.lat(), lng: c.lng() }); } }}
                  className="mt-1 p-2.5 bg-gray-100 rounded-xl border border-safaricab-gold/30 flex-shrink-0"
                  title="Drop pin on map"
                >
                  <MapPin className="w-5 h-5 text-safaricab-gold" />
                </button>
                {dropoffSuggestions.length > 0 && (
                  <div className="absolute z-30 mt-1 bg-gray-100 rounded-xl overflow-hidden border border-gray-200 max-h-48 overflow-y-auto" style={{ left: 0, right: '3.5rem', top: '100%' }}>
                    {dropoffSuggestions.map((loc, i) => (
                      <button key={i} onClick={async () => { const r = await resolveLocation(loc); setDropoff(r); setDropoffQuery((r as any).name || r.address); setDropoffSuggestions([]); }} className="w-full text-left px-4 py-3 text-gray-900 text-sm hover:bg-gray-50 border-b border-gray-100">{(loc as any).name || loc.address}</button>
                    ))}
                  </div>
                )}
              </div>

              {/* Add Stop button */}
              <button onClick={addStop} className="flex items-center gap-2 text-safaricab-gold text-sm font-medium py-1">
                <Plus className="w-4 h-4" /> {t('add_stop')}
              </button>

              {/* Book for someone else */}
              <div className="bg-gray-50 rounded-xl px-4 py-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-900 text-sm">Booking for someone else?</span>
                  {bookingForOther ? (
                    <button
                      onClick={() => { setBookingForOther(false); setPassengerName(''); setPassengerPhone(''); setPassengerError(false); }}
                      className="flex items-center gap-1 text-gray-500 text-xs font-medium"
                    >
                      <X className="w-3.5 h-3.5" /> Cancel
                    </button>
                  ) : (
                    <button onClick={() => setBookingForOther(true)} className="text-safaricab-gold text-xs font-semibold">
                      + Add
                    </button>
                  )}
                </div>
                {bookingForOther && (
                  <div className="mt-3 space-y-2">
                    <input value={passengerName} onFocus={keepFieldVisible} onChange={(e) => { setPassengerName(e.target.value); setPassengerError(false); }} placeholder={passengerError && !passengerName.trim() ? 'Passenger name is required' : "Passenger's name"} className={`w-full bg-white border rounded-xl px-4 py-2.5 text-gray-900 text-sm outline-none ${passengerError && !passengerName.trim() ? 'border-red-500 placeholder-red-400' : 'border-gray-200'}`} />
                    <input value={passengerPhone} onFocus={keepFieldVisible} onChange={(e) => { setPassengerPhone(e.target.value); setPassengerError(false); }} placeholder={passengerError && !passengerPhone.trim() ? 'Passenger phone is required' : "Passenger's phone (e.g. 0712 345678)"} inputMode="tel" className={`w-full bg-white border rounded-xl px-4 py-2.5 text-gray-900 text-sm outline-none ${passengerError && !passengerPhone.trim() ? 'border-red-500 placeholder-red-400' : 'border-gray-200'}`} />
                    <p className={`text-xs ${passengerError ? 'text-red-400' : 'text-gray-400'}`}>{passengerError ? "Enter the passenger's name and phone to continue." : "The driver will see and call this person. Set the pickup to where they are."}</p>
                  </div>
                )}
              </div>

              {/* Is a company paying? Sits directly under "booking for someone
                  else" and behaves the same way, except eligibility is checked
                  against the employer's roster the moment it's switched on -
                  better to fail here than after the rider has picked a car. */}
              <div className="bg-gray-50 rounded-xl px-4 py-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-900 text-sm">Is your company paying for this trip?</span>
                  {corporateOn ? (
                    <button
                      onClick={() => { setCorporateOn(false); setCorporateInfo(null); setCorporateAsTicket(false); setCorporateTicketTime(''); }}
                      className="flex items-center gap-1 text-gray-500 text-xs font-medium"
                    >
                      <X className="w-3.5 h-3.5" /> Cancel
                    </button>
                  ) : (
                    <button
                      onClick={checkCorporateEligibility}
                      disabled={corporateChecking}
                      className="text-safaricab-gold text-xs font-semibold disabled:opacity-50"
                    >
                      {corporateChecking ? 'Checking…' : '+ Yes'}
                    </button>
                  )}
                </div>
                {corporateOn && corporateInfo && (
                  <div className="mt-3 space-y-2">
                    <div className="flex items-center gap-2 bg-safaricab-gold/10 border border-safaricab-gold/30 rounded-xl px-3 py-2.5">
                      <Building2 className="w-4 h-4 text-safaricab-gold flex-shrink-0" />
                      <p className="text-gray-700 text-xs">
                        Billed to <span className="font-semibold text-gray-900">{corporateInfo.name}</span>.
                        {corporateAsTicket
                          ? " They'll be asked to approve, and you'll get a ticket to call a driver with later."
                          : " They'll be asked to approve before a driver is assigned."}
                      </p>
                    </div>

                    {/* Why the company is paying. The approver is authorising
                        real money and, without this, all they get is two
                        addresses and a fare - the same airport run is obviously
                        fine for a client pickup and obviously not for a personal
                        errand, and nothing on their screen said which. Chips
                        first so the common answers are one tap; the box is for
                        the rest. */}
                    <div className="rounded-xl px-3 py-2.5 border border-gray-200 bg-gray-50">
                      <label className="block text-gray-900 text-xs font-semibold mb-2">
                        Reason for this trip <span className="text-red-500">*</span>
                      </label>
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {['Client meeting', 'Airport transfer', 'Site visit', 'Office errand', 'Working late'].map(r => (
                          <button
                            key={r}
                            type="button"
                            onClick={() => setCorporateReason(prev => (prev === r ? '' : r))}
                            className={`px-2.5 py-1.5 rounded-lg text-[11px] font-medium border transition-colors ${
                              corporateReason === r
                                ? 'bg-safaricab-gold border-safaricab-gold text-black'
                                : 'bg-white border-gray-200 text-gray-600'
                            }`}
                          >
                            {r}
                          </button>
                        ))}
                      </div>
                      <input
                        value={corporateReason}
                        onChange={(e) => setCorporateReason(e.target.value.slice(0, 120))}
                        placeholder="Or type your own reason"
                        className="w-full bg-white border border-gray-200 rounded-lg px-2.5 py-2 text-xs text-gray-900 outline-none focus:border-safaricab-gold"
                      />
                      <p className="text-gray-400 text-[10px] mt-1.5">
                        Your approver sees this in the SMS, so it's what they decide on.
                      </p>
                    </div>

                    {/* The escape hatch from the five-minute approval window: if
                        the approver can't answer right now, she does not have to
                        stand there waiting for them. Same trip, same approval,
                        she just keeps the timing. */}
                    <button
                      type="button"
                      onClick={() => setCorporateAsTicket(v => !v)}
                      className={`w-full flex items-start gap-2.5 rounded-xl px-3 py-2.5 border text-left transition-colors ${
                        corporateAsTicket
                          ? 'bg-safaricab-gold/15 border-safaricab-gold/50'
                          : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <span className={`w-4 h-4 mt-0.5 rounded border flex items-center justify-center flex-shrink-0 ${
                        corporateAsTicket ? 'bg-safaricab-gold border-safaricab-gold' : 'border-gray-300'
                      }`}>
                        {corporateAsTicket && <Check className="w-3 h-3 text-black" strokeWidth={3} />}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-gray-900 text-xs font-semibold">Get a ticket instead — travel later today</span>
                        <span className="block text-gray-500 text-[11px] mt-0.5">
                          No driver is called now. Once approved you get a ticket valid 24 hours, and you call the driver when you're ready.
                        </span>
                      </span>
                    </button>

                    {corporateAsTicket && (
                      <div className="flex items-center gap-2 px-1">
                        <CalendarClock className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                        <label className="text-gray-500 text-[11px] flex-1">
                          Roughly when? <span className="text-gray-400">(optional — just so your approver knows)</span>
                        </label>
                        <input
                          type="time"
                          value={corporateTicketTime ? new Date(corporateTicketTime).toTimeString().slice(0, 5) : ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (!v) { setCorporateTicketTime(''); return; }
                            const [hh, mm] = v.split(':').map(Number);
                            const when = new Date();
                            when.setHours(hh, mm, 0, 0);
                            // A time that has already passed today means tomorrow.
                            if (when.getTime() < Date.now()) when.setDate(when.getDate() + 1);
                            setCorporateTicketTime(when.toISOString());
                          }}
                          className="border border-gray-200 rounded-lg px-2 py-1 text-xs text-gray-900 outline-none focus:border-safaricab-gold"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Safari Send: sender/receiver details - required, not optional
                  like "booking for someone else", since the driver needs the
                  receiver's contact to hand off the package at dropoff. Only
                  reached from an entry point that pre-picks the service; the
                  normal path shows the same block in the ride list below, once
                  the rider has actually chosen a Safari Send option. */}
              {isSafariSend && safariSendDetails}

            </motion.div>
          )}

          {/* Ride Options */}
          {rideResults.length > 0 && (
            <motion.div ref={formOrResultsRefCb} key="ride-options" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.28, ease: 'easeOut' }} className="space-y-2">
              <div className="flex items-center gap-3 mb-1">
                <button
                  onClick={() => { setRideResults([]); setDirections(null); setSelectedRide(null); }}
                  className="flex items-center gap-1.5 text-safaricab-gold text-sm font-medium"
                >
                  <ArrowLeft className="w-4 h-4" /> {t('change')}
                </button>
                {/* The "N drivers online" pill used to sit here (ml-auto). Removed
                    from the UI - nearbyDrivers itself stays, it still drives the
                    car markers on the map and the nearest-driver ETA line. */}
              </div>
              <h2 className="text-gray-900 font-bold text-lg mb-2">{t('choose_a_ride')}</h2>
              {/* Promo banner (Bolt-style): shown when the admin offer discounted these fares */}
              {(() => {
                const offerPct = rideResults.find(r => r.price.offerPercent)?.price.offerPercent;
                return offerPct ? (
                  <div className="flex items-center justify-center gap-2 bg-safaricab-gold text-black text-sm font-bold rounded-xl py-2.5 mb-1">
                    <span>✓</span> {offerPct}% Discount applied
                  </div>
                ) : null;
              })()}
              {(() => {
                // Whichever ride is selected floats to the top (tapping any
                // card re-selects it, so this re-sorts live) - everything
                // else keeps its original relative order beneath it.
                const ordered = selectedRide
                  ? [...rideResults].sort((a, b) => (a.option.id === selectedRide ? -1 : b.option.id === selectedRide ? 1 : 0))
                  : rideResults;
                // No more "Show N more options" button. Collapsed (default)
                // shows just the top 2 - swiping the handle up expands to a
                // nested scrollbox holding all of them (up to 8), so the
                // list can be browsed without growing the outer sheet
                // further and pushing "Select <ride>" (a fixed footer below
                // this, see the sticky-breaks-under-Framer-transform note
                // near it) away from where it always sits.
                const visible = sheetExpanded ? ordered : ordered.slice(0, 2);
                return (
                  <div className={sheetExpanded ? 'space-y-2 max-h-[330px] overflow-y-auto overscroll-contain -mx-1 px-1' : 'space-y-2'}>
                    {visible.map(({ option, price }) => {
                      const available = hasDriverForRide(option.id);
                      const isSelected = selectedRide === option.id;
                      return (
                        <motion.button
                          key={option.id}
                          layout
                          whileTap={{ scale: 0.98 }}
                          onClick={() => setSelectedRide(option.id)}
                          className={`w-full flex items-center gap-3 p-3 rounded-xl border relative ${isSelected ? 'border-safaricab-gold bg-safaricab-gold/10' : 'border-gray-200 bg-gray-50'}${available ? '' : ' opacity-40'}`}
                        >
                          <div
                            role="button"
                            onClick={(e) => { e.stopPropagation(); setShowVehicleInfo(option.id); }}
                            className="w-12 h-12 flex items-center justify-center bg-white rounded-full flex-shrink-0"
                            title="View details"
                          >
                            <img src={rideIconMap[option.id] || "/icons/rides/motorbike.png"} alt={option.name} className="w-10 h-10 object-contain" />
                          </div>
                          <div className="flex-1 text-left">
                            <div className="flex items-center gap-2">
                              <span className="text-gray-900 font-semibold text-sm">{option.name}</span>
                              {/* Surge multiplier is applied in the fare but intentionally not shown to the rider.
                                  No per-card -N% badge: the "Discount applied" banner above the list covers it. */}
                            </div>
                            <span className="text-gray-500 text-xs">{Math.round(option.estimatedArrival / 60)} min • {price.distance} km</span>
                          </div>
                          {/* Busy classes keep their price visible but the card greys out, with a quiet "Busy" note under the fare. */}
                          <span className="text-right">
                            <span className="block text-gray-900 font-bold text-sm">KES {price.estimatedPrice}</span>
                            {price.originalPrice && <span className="block text-gray-400 line-through text-[11px] leading-tight">KES {price.originalPrice}</span>}
                            {!available && <span className="block text-gray-400 text-[10px] font-medium">{t('busy_badge')}</span>}
                          </span>
                        </motion.button>
                      );
                    })}
                  </div>
                );
              })()}
              {/* Picking a Safari Send option reveals the sender/receiver fields
                  right here, above the pinned "Select …" button - this is the
                  first moment the app knows the trip is a delivery, and the last
                  step before the confirm sheet, so it's where the details can
                  actually be asked for. handleRequestRide won't open confirm
                  until they're filled. */}
              {isSafariSend && (
                <div className="pt-2">{safariSendDetails}</div>
              )}
            </motion.div>
          )}
          </AnimatePresence>
        </div>
          {/* "Find rides" is pinned here for the same reason "Select <ride>"
              below is: as the last child of the scrollable body it dropped below
              the fold whenever the form ran tall (a Safari Send booking, added
              stops, a manually-entered pickup), so reaching the primary action of
              the screen meant scrolling to find it. Out here it keeps its own
              space at the sheet's true bottom edge - always visible at a glance,
              sitting exactly on the safe-area boundary, with the form scrolling
              in whatever room is left above it. */}
          {rideResults.length === 0 && dropoff && (
            <div className="flex-shrink-0 px-4 pt-3 pb-4 bg-white">
              <motion.button whileTap={{ scale: 0.97 }} onClick={handleFindRides} disabled={!pickup || !dropoff || isLoading} className="w-full bg-safaricab-gold text-black font-bold py-4 rounded-xl disabled:opacity-40">
                {isLoading ? t('finding_rides') : t('find_rides')}
              </motion.button>
            </div>
          )}
          {/* A real flex sibling of the scrollable body above, not a sticky
              child inside it - Framer Motion leaves an inline `transform` on
              animated ancestors (the AnimatePresence slide/fade wrapper this
              used to live inside), which silently breaks `position: sticky`
              on any descendant. Living outside the scrollable flex-1 area
              entirely means it never depends on sticky at all: the list
              scrolls in whatever space is left, and this always keeps its
              own space at the sheet's true bottom edge - reachable without
              scrolling no matter how many ride options are showing. */}
          {rideResults.length > 0 && selectedRideData && (
            <div className="flex-shrink-0 px-4 pt-3 pb-4 bg-white">
              <button
                onClick={() => handleRequestRide(selectedRide as RideType)}
                disabled={!hasDriverForRide(selectedRideData.option.id)}
                className="w-full bg-safaricab-gold text-black font-bold py-4 rounded-2xl text-base disabled:opacity-40"
              >
                {hasDriverForRide(selectedRideData.option.id) ? `Select ${selectedRideData.option.name}` : t('currently_busy')}
              </button>
            </div>
          )}
        </motion.div>
        )}
      </div>

      {/* Card Payment Modal */}
      <AnimatePresence>
        {showPayment && !paymentDone && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-50 flex items-end justify-center">
            <motion.div initial={{ y: 100 }} animate={{ y: 0 }} exit={{ y: 100 }}
              className="bg-white rounded-t-3xl px-6 pt-6 w-full max-w-sm" style={{ paddingBottom: 'var(--sai-dock)' }}>
              <div className="w-12 h-1 bg-gray-200 rounded-full mx-auto mb-5" />
              <h2 className="text-gray-900 font-bold text-xl mb-1 text-center">Pay for your trip</h2>
              <p className="text-gray-500 text-sm text-center mb-6">Secured by Paystack</p>
              <div className="bg-gray-50 rounded-2xl p-5 mb-6 text-center">
                <p className="text-gray-400 text-sm mb-1">Amount Due</p>
                <p className="text-3xl font-bold text-gray-900">KES {pendingPaymentPrice.toLocaleString()}</p>
              </div>
              <button
                onClick={async () => {
                  if (paymentLoading) return;
                  setPaymentLoading(true);
                  try {
                    const user = JSON.parse(localStorage.getItem('user') || '{}');
                    const token = localStorage.getItem('accessToken');
                    // Initialize Paystack
                    const initRes = await fetch(`${API_URL}/payment/initialize`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({
                        email: user.email || `${user.phone}@safaricab.app`,
                        amount: pendingPaymentPrice,
                        callback_url: `${window.location.origin}?payment=done&ride=${pendingPaymentRideId}`,
                        metadata: { rider_id: user.id, ride_id: pendingPaymentRideId, reason: 'Trip Payment' },
                      }),
                    });
                    const initData = await initRes.json();
                    if (initData.success && initData.authorization_url) {
                      // Mark as paid BEFORE leaving - so if Paystack callback loses params, modal won't re-show
                      if (pendingPaymentRideId) localStorage.setItem('paidRide', pendingPaymentRideId);
                      localStorage.removeItem('pendingCardPaymentRide');
                      window.location.href = initData.authorization_url;
                    }
                  } catch {}
                  setPaymentLoading(false);
                }}
                disabled={paymentLoading}
                className="w-full bg-green-500 text-white font-bold py-4 rounded-xl flex items-center justify-center gap-2 mb-3 disabled:opacity-50"
              >
                {paymentLoading
                  ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  : <CreditCard className="w-5 h-5" />}
                {paymentLoading ? 'Redirecting…' : `Pay KES ${pendingPaymentPrice.toLocaleString()}`}
              </button>
              <button onClick={() => {
                localStorage.removeItem('pendingCardPaymentRide');
                if (pendingPaymentRideId) localStorage.setItem('paidRide', pendingPaymentRideId);
                setShowPayment(false);
                setShowRating(true);
              }} className="w-full text-center text-gray-400 text-sm py-2">
                Pay later (cash)
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Rating Modal - only ever for a real, not-yet-rated trip, so a post-submit
          re-trigger (stale/cleared activeRideId, or an already-rated trip) can't
          flash a second, dead modal. */}
      <AnimatePresence mode="wait">
        {/* Pay-the-driver confirmation (cash) - shows before the rating prompt */}
        {showRating && !ratingDone && activeRideId && !isTripHandled(activeRideId) && needsDriverPay && (
          <motion.div key="pay-driver" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.22 }}
            className="fixed inset-0 bg-black/90 z-[120] flex items-center justify-center p-4" style={{ zIndex: 120 }}>
            <motion.div initial={{ scale: 0.97, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.97, opacity: 0 }} transition={{ duration: 0.2, ease: 'easeOut' }}
              className="bg-safaricab-dark rounded-3xl p-6 w-full max-w-sm text-center border border-safaricab-gold/30">
              <p className="text-white/60 text-sm mb-1">Trip complete. Please pay</p>
              <h2 className="text-white font-bold text-3xl mb-1">KES {Number(fareDue).toLocaleString()}</h2>
              <p className="text-white/80 text-sm mb-1">to <span className="text-safaricab-gold font-semibold">{assignedDriver?.full_name || 'your driver'}</span></p>
              <p className="text-white/40 text-xs mb-6">Cash: pay the driver directly. Total includes any stops.</p>
              <button onClick={() => setDriverPaidConfirmed(true)} className="w-full bg-safaricab-gold text-black font-bold py-4 rounded-2xl">
                I've Paid
              </button>
            </motion.div>
          </motion.div>
        )}
        {showRating && !ratingDone && activeRideId && !isTripHandled(activeRideId) && !needsDriverPay && (
          <motion.div
            key="rating-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 z-[120] flex items-center justify-center p-4"
            style={{ zIndex: 120 }}
          >
            <motion.div
              initial={{ scale: 0.97, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.97, opacity: 0 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="bg-safaricab-dark rounded-3xl p-6 w-full max-w-sm text-center border border-safaricab-gold/30"
            >
              <h2 className="text-white font-bold text-xl mb-2">{isRecoveredRating ? 'How was your last trip?' : 'How was your trip?'}</h2>
              {assignedDriver && (
                <div className="flex flex-col items-center mb-4">
                  {assignedDriver.avatar_url ? (
                    <img src={assignedDriver.avatar_url} className="w-16 h-16 rounded-full object-cover mb-2" alt={assignedDriver.full_name} />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-safaricab-gold/20 flex items-center justify-center text-safaricab-gold font-bold text-2xl mb-2">
                      {assignedDriver.full_name?.charAt(0)}
                    </div>
                  )}
                  <p className="text-white font-bold">{assignedDriver.full_name}</p>
                </div>
              )}
              <div className="flex justify-center gap-3 mb-6">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => setSelectedRating(star)}
                    className="p-1 transition-transform active:scale-90"
                  >
                    <Star
                      className={`w-9 h-9 ${star <= selectedRating ? 'text-safaricab-gold fill-safaricab-gold' : 'text-white/30'}`}
                    />
                  </button>
                ))}
              </div>
              <textarea
                value={ratingComment}
                onChange={(e) => setRatingComment(e.target.value)}
                placeholder="Leave a comment (optional)"
                maxLength={1000}
                rows={3}
                className="w-full bg-safaricab-card border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/30 outline-none focus:border-safaricab-gold/50 resize-none mb-4"
              />
              <button
                onClick={async () => {
                  if (!selectedRating || !activeRideId) return;
                  setRatingSubmitting(true);
                  try {
                    const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000') + '/api/v1';
                    const token = localStorage.getItem('accessToken');
                    await fetch(`${API_URL}/rides/${activeRideId}/rate`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ rating: selectedRating, comment: ratingComment.trim() || undefined }),
                    });
                    // Update rating in local ride history
                    const history = JSON.parse(localStorage.getItem('rideHistory') || '[]');
                    localStorage.setItem('rideHistory', JSON.stringify(
                      history.map((r: any) => r.id === activeRideId ? { ...r, rating: selectedRating } : r)
                    ));
                    markTripHandled(activeRideId); // never re-prompt this trip again
                    setRatingDone(true);
                    setShowRating(false);
                    setIsRecoveredRating(false);
                    localStorage.removeItem('pendingCardPaymentRide');
                    setTimeout(() => {
                      setNavigationMode(false);
                      setSelectedRide(null);
                      setDirections(null);
                      setDropoff(null);
                      setDropoffQuery('');
                      setActiveRideId('');
                      setRideStatus('');
                      setAssignedDriver(null);
                      setAssignedDriverId('');
                      setBookingError('');
                      setSelectedRating(0);
                      setRatingComment('');
                      setRatingDone(false);
                      setStops([]);
                      setStopQueries([]);
                      setStopSuggestions([]);
                    }, 1500);
                  } catch (e) {
                    setRatingDone(true);
                    setShowRating(false);
                  } finally {
                    setRatingSubmitting(false);
                  }
                }}
                disabled={!selectedRating || ratingSubmitting}
                className="w-full bg-safaricab-gold text-black font-bold py-4 rounded-xl text-lg disabled:opacity-40"
              >
                {ratingSubmitting ? 'Submitting...' : 'Submit Rating'}
              </button>
              {/* No skip on a recovered rating - the customer rates the finished
                  trip before doing anything else. Normal in-trip flow keeps Skip. */}
              {!isRecoveredRating && (
                <button
                  onClick={() => {
                    setShowRating(false);
                    setRatingDone(true);
                    setTimeout(() => {
                      setNavigationMode(false);
                      setSelectedRide(null);
                      setDirections(null);
                      setDropoff(null);
                      setDropoffQuery('');
                      setActiveRideId('');
                      setRideStatus('');
                      setAssignedDriver(null);
                      setAssignedDriverId('');
                      setBookingError('');
                      setSelectedRating(0);
                      setRatingComment('');
                      setRatingDone(false);
                      setStops([]);
                      setStopQueries([]);
                      setStopSuggestions([]);
                    }, 500);
                  }}
                  className="w-full text-white/40 text-sm py-2 mt-2"
                >
                  Skip
                </button>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Vehicle Info Popup - white bottom sheet shown when tapping any ride option */}
      <AnimatePresence>
        {showVehicleInfo && (() => {
          const infoId = showVehicleInfo;
          const info = vehicleTypeInfo[infoId];
          const rideResult = rideResults.find(r => r.option.id === infoId);
          if (!info || !rideResult) return null;
          const available = hasDriverForRide(infoId);
          return (
            <motion.div key="vehicle-info-backdrop"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setShowVehicleInfo(null)}
              className="fixed inset-0 bg-black/70 z-[100] flex items-end justify-center pointer-events-auto"
            >
              <motion.div
                initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
                transition={{ type: 'spring', stiffness: 340, damping: 36 }}
                onClick={e => e.stopPropagation()}
                className="bg-white rounded-t-3xl w-full max-w-lg px-5 pt-4"
                style={{ paddingBottom: 'var(--sai-dock)' }}
              >
                <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-4" />
                {/* Header */}
                <div className="flex items-center gap-4 mb-5">
                  <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <img src={rideIconMap[infoId] || '/icons/rides/motorbike.png'} alt={rideResult.option.name} className="w-12 h-12 object-contain" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-gray-900 font-bold text-xl">{rideResult.option.name}</h2>
                    {available
                      ? <span className="text-green-600 text-sm font-medium">{t('available')}</span>
                      : <span className="text-orange-500 text-sm font-medium">{t('busy_badge')}</span>
                    }
                  </div>
                  <span className="text-gray-900 font-bold text-lg">KES {rideResult.price.estimatedPrice}</span>
                </div>

                {/* Details */}
                <div className="space-y-3 mb-5">
                  <div className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
                    <span className="text-2xl flex-shrink-0">⛽</span>
                    <div>
                      <p className="text-gray-400 text-xs font-medium uppercase tracking-wide">{t('fuel_type')}</p>
                      <p className="text-gray-800 font-semibold">{info.fuelType}</p>
                    </div>
                  </div>
                  {info.capacity !== undefined && (
                    <div className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
                      <span className="text-2xl flex-shrink-0">👥</span>
                      <div>
                        <p className="text-gray-400 text-xs font-medium uppercase tracking-wide">{t('passengers_label')}</p>
                        <p className="text-gray-800 font-semibold">{info.capacity} {info.capacity === 1 ? 'Passenger' : 'Passengers'}</p>
                      </div>
                    </div>
                  )}
                  {info.goods && (
                    <div className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
                      <span className="text-2xl flex-shrink-0">📦</span>
                      <div>
                        <p className="text-gray-400 text-xs font-medium uppercase tracking-wide">{t('goods_label')}</p>
                        <p className="text-gray-800 font-semibold">{info.goods}</p>
                      </div>
                    </div>
                  )}
                  <div className="flex items-start gap-3 bg-green-50 rounded-xl p-3">
                    <span className="text-2xl flex-shrink-0">🛡️</span>
                    <div>
                      <p className="text-green-700 text-xs font-medium uppercase tracking-wide">{t('safety_tip')}</p>
                      <p className="text-gray-700 text-sm leading-relaxed">{info.safetyTip}</p>
                    </div>
                  </div>
                </div>

                {available ? (
                  <button
                    onClick={() => { setShowVehicleInfo(null); handleRequestRide(infoId as RideType); }}
                    className="w-full bg-black text-white font-bold py-4 rounded-2xl text-base mb-2"
                  >
                    {t('select_this_ride')}
                  </button>
                ) : (
                  <button disabled className="w-full bg-gray-200 text-gray-400 font-bold py-4 rounded-2xl text-base mb-2">
                    {t('currently_busy')}
                  </button>
                )}
                <button onClick={() => setShowVehicleInfo(null)} className="w-full bg-black text-white font-bold py-4 rounded-2xl text-base text-center">
                  {t('go_back')}
                </button>
              </motion.div>
            </motion.div>
          );
        })()}
      </AnimatePresence>

      {/* Confirmation Modal */}
      <AnimatePresence>
        {showConfirm && selectedRideData && pickup && dropoff && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowConfirm(false)} className="fixed inset-0 bg-black/80 z-50" />
            {/* Bottom-docked like the others, and it carries the discount-code
                field - so it takes the same keyboard lift. */}
            <motion.div initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }} className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 px-6 pt-6" style={{ paddingBottom: keyboardPx ? keyboardPx + 16 : 'var(--sai-dock)' }}>
              <div className="w-10 h-1 bg-gray-300 rounded-full mx-auto mb-4" />
              <h2 className="text-gray-900 font-bold text-xl mb-4">Confirm Ride</h2>
              <div className="flex items-center gap-3 mb-4 p-3 bg-gray-100 rounded-xl">
                <img src={rideIconMap[selectedRideData.option.id] || "/icons/rides/motorbike.png"} alt={selectedRideData.option.name} className="w-16 h-16 object-contain" />
                <div>
                  <p className="text-gray-900 font-bold">{selectedRideData.option.name}</p>
                  <p className="text-gray-500 text-xs">{Math.round(selectedRideData.option.estimatedArrival / 60)} min away</p>
                </div>
                <span className="ml-auto text-right">
                  {/* Parking rides on top of the discounted fare, never inside
                      it - a discount code buys the rider a cheaper trip, not a
                      cheaper parking ticket, and the driver hands over the full
                      amount at the barrier either way. */}
                  <span className="block text-gray-900 font-bold text-lg">KES {Math.max(0, selectedRideData.price.estimatedPrice - appliedDiscount) + parkingFee}</span>
                  {/* Struck-through value below the real price: the pre-offer full fare when a promo is on, else the pre-code fare */}
                  {(appliedDiscount > 0 || selectedRideData.price.originalPrice) &&
                    <span className="block text-gray-400 line-through text-sm">KES {(selectedRideData.price.originalPrice || selectedRideData.price.estimatedPrice) + parkingFee}</span>}
                  {parkingFee > 0 &&
                    <span className="block text-gray-500 text-[11px] mt-0.5">incl. KES {parkingFee} parking</span>}
                </span>
              </div>
              {/* Mall parking. Only appears when the drop-off is actually inside
                  a mall's grounds, so an ordinary trip never sees it.

                  Two explicit choices rather than a bare checkbox, because the
                  second one is a real statement about the trip ("I'm not going
                  in"), not the absence of the first. The total above updates the
                  moment either is tapped, so the charge is never something the
                  rider discovers afterwards. */}
              {parkingOffered && dropoffParkingZone && (
                <div className="mb-4 rounded-2xl border border-safaricab-gold/40 bg-safaricab-gold/5 overflow-hidden">
                  <div className="px-4 pt-3 pb-2">
                    <p className="text-gray-900 font-bold text-sm">{dropoffParkingZone.name} charges for parking</p>
                    <p className="text-gray-500 text-xs mt-0.5">{parkingCopy.lede}</p>
                  </div>
                  <div className="px-3 pb-3 space-y-2">
                    <button
                      onClick={() => setIncludeParking(true)}
                      className={`w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-left border transition-colors ${
                        includeParking ? 'border-safaricab-gold bg-white' : 'border-gray-200 bg-white/60'
                      }`}
                    >
                      <span className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                        includeParking ? 'border-safaricab-gold' : 'border-gray-300'
                      }`}>
                        {includeParking && <span className="w-2 h-2 rounded-full bg-safaricab-gold" />}
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-gray-900 text-sm font-medium">{parkingCopy.inTitle}</span>
                        <span className="block text-gray-500 text-xs">{parkingCopy.inSub}</span>
                      </span>
                      <span className="text-gray-900 text-sm font-bold flex-shrink-0">+KES {dropoffParkingZone.parking_fee}</span>
                    </button>
                    <button
                      onClick={() => setIncludeParking(false)}
                      className={`w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-left border transition-colors ${
                        !includeParking ? 'border-safaricab-gold bg-white' : 'border-gray-200 bg-white/60'
                      }`}
                    >
                      <span className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                        !includeParking ? 'border-safaricab-gold' : 'border-gray-300'
                      }`}>
                        {!includeParking && <span className="w-2 h-2 rounded-full bg-safaricab-gold" />}
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-gray-900 text-sm font-medium">{parkingCopy.outTitle}</span>
                        <span className="block text-gray-500 text-xs">{parkingCopy.outSub}</span>
                      </span>
                      <span className="text-gray-500 text-sm font-medium flex-shrink-0">No charge</span>
                    </button>
                  </div>
                </div>
              )}
              <div className="space-y-2 mb-4 text-sm">
                <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-green-500" /><span className="text-gray-600 truncate">{pickup.address}</span></div>
                {stops.filter(Boolean).map((stop: any, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-safaricab-gold/40 flex items-center justify-center flex-shrink-0"><span className="text-safaricab-gold text-[9px] font-bold">{i + 1}</span></div>
                    <span className="text-gray-500 truncate">{stop.address}</span>
                  </div>
                ))}
                <div className="flex items-center gap-2"><Navigation className="w-4 h-4 text-red-500" /><span className="text-gray-600 truncate">{dropoff.address}</span></div>
                <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-safaricab-gold" /><span className="text-gray-600">{selectedRideData.price.duration} min • {selectedRideData.price.distance} km</span></div>
              </div>
              {/* Discount code */}
              <div className="flex items-center gap-2 mb-3">
                <input value={discountCode} onFocus={keepFieldVisible} onChange={e => { setDiscountCode(e.target.value.toUpperCase()); setDiscountMsg(''); }}
                  placeholder="Discount code" className="flex-1 bg-gray-100 border border-gray-200 rounded-xl py-2.5 px-3 text-gray-900 text-sm outline-none focus:border-safaricab-gold/50" />
                <button onClick={async () => {
                  if (!discountCode.trim()) return;
                  setDiscountMsg('');
                  try {
                    const token = localStorage.getItem('accessToken');
                    const res = await fetch(`${API_URL}/referrals/validate-discount`, {
                      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ code: discountCode.trim(), fare: selectedRideData.price.estimatedPrice }),
                    });
                    const d = await res.json();
                    if (res.ok && d.valid) { setAppliedDiscount(d.discount); setDiscountMsg(`✓ KES ${d.discount} off applied`); }
                    else { setAppliedDiscount(0); setDiscountMsg(d.error || 'Invalid code'); }
                  } catch { setDiscountMsg('Could not validate'); }
                }} className="bg-gray-100 text-gray-900 text-sm font-medium px-4 py-2.5 rounded-xl">Apply</button>
              </div>
              {discountMsg && <p className={`text-xs mb-2 ${discountMsg.startsWith('✓') ? 'text-green-400' : 'text-red-400'}`}>{discountMsg}</p>}

              {/* Schedule for later */}
              <div className={`mb-3 ${scheduleIntent ? 'ring-1 ring-safaricab-gold/40 rounded-xl p-2 -m-2' : ''}`}>
                <label className={`text-xs flex items-center gap-1 mb-1 ${scheduleIntent ? 'text-safaricab-gold font-semibold' : 'text-gray-500'}`}><CalendarClock className="w-3.5 h-3.5" /> Schedule for later (optional - prepaid by card)</label>
                <input type="datetime-local" value={scheduleFor} onChange={e => { setScheduleFor(e.target.value); }}
                  min={new Date(Date.now() + 5 * 60000).toISOString().slice(0, 16)}
                  className="w-full bg-gray-100 border border-gray-200 rounded-xl py-2.5 px-3 text-gray-900 text-sm outline-none focus:border-safaricab-gold/50" />
              </div>

              {bookingError && <p className="text-red-400 text-sm text-center mb-2">{bookingError}</p>}

              {scheduleFor ? (
                <button onClick={() => handleSchedulePay()} disabled={isLoading} className="w-full bg-safaricab-gold text-black font-bold py-4 rounded-xl text-lg disabled:opacity-50">
                  {isLoading ? 'Processing...' : `Schedule & Pay KES ${Math.max(0, selectedRideData.price.estimatedPrice - appliedDiscount) + parkingFee}`}
                </button>
              ) : (
                <button onClick={handleConfirmRide} disabled={isLoading} className="w-full bg-safaricab-gold text-black font-bold py-4 rounded-xl text-lg disabled:opacity-50">
                  {isLoading ? t('booking') : t('confirm_ride')}
                </button>
              )}
              <button onClick={() => { setShowConfirm(false); setScheduleFor(''); setAppliedDiscount(0); setDiscountCode(''); setDiscountMsg(''); }} className="w-full text-gray-500 py-3 text-sm mt-2">Cancel</button>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ── Corporate: not enrolled ──
          Centre of the screen, not inline: this ends the corporate path, so it
          needs to be read and dismissed rather than glanced past. */}
      <AnimatePresence>
        {corporateError && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[210] bg-black/70 flex items-center justify-center p-6"
            onClick={() => setCorporateError('')}
          >
            <motion.div
              initial={{ scale: 0.92, y: 12 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, y: 12 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-safaricab-dark border border-red-500/40 rounded-3xl p-6 w-full max-w-sm text-center"
            >
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-8 h-8 text-red-400" />
              </div>
              <h2 className="text-white font-bold text-lg mb-2">Not enabled</h2>
              <p className="text-white/60 text-sm mb-6">{corporateError}</p>
              <button onClick={() => setCorporateError('')} className="w-full bg-safaricab-gold text-black font-bold py-3 rounded-xl">
                OK
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Corporate: waiting for the approver ── */}
      <AnimatePresence>
        {corporateRequest && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[210] bg-black/80 flex items-center justify-center p-6"
          >
            <motion.div
              initial={{ scale: 0.92, y: 12 }} animate={{ scale: 1, y: 0 }}
              className="bg-safaricab-dark border border-safaricab-gold/40 rounded-3xl p-6 w-full max-w-sm text-center"
            >
              <div className="w-16 h-16 bg-safaricab-gold/20 rounded-full flex items-center justify-center mx-auto mb-4">
                {corporateRequest.isTicket
                  ? <Ticket className="w-8 h-8 text-safaricab-gold" />
                  : <Building2 className="w-8 h-8 text-safaricab-gold" />}
              </div>
              <h2 className="text-white font-bold text-lg mb-1">Waiting for {corporateRequest.company}</h2>
              <p className="text-white/50 text-sm mb-4">
                {corporateRequest.isTicket
                  ? "We've asked them to approve your ticket. You don't have to wait here — we'll notify you, and the ticket will be on your home screen."
                  : 'We\'ve asked them to approve this trip. A driver is assigned as soon as they do.'}
              </p>
              {/* An instant request runs on a five-minute kerbside countdown, so
                  the seconds matter. A ticket request gives the approver half a
                  day, where mm:ss would read "719:57" and look broken. */}
              <p className="text-safaricab-gold font-bold text-3xl tabular-nums mb-4">
                {corporateRequest.isTicket
                  ? (corporateSecsLeft >= 3600
                      ? `${Math.floor(corporateSecsLeft / 3600)}h to decide`
                      : `${Math.ceil(corporateSecsLeft / 60)}m to decide`)
                  : `${Math.floor(corporateSecsLeft / 60)}:${String(corporateSecsLeft % 60).padStart(2, '0')}`}
              </p>
              <div className="bg-white/5 rounded-xl px-4 py-3 mb-5 text-left">
                <p className="text-white/70 text-xs truncate">{pickup?.address?.split(',')[0] || 'Pickup'} → {dropoff?.address?.split(',')[0] || 'Drop-off'}</p>
                {bookedRideRef.current && (
                  <p className="text-white/40 text-xs mt-0.5">KES {bookedRideRef.current.price} · billed to your company</p>
                )}
              </div>
              {/* Leaving is not cancelling. A ticket request can sit for hours,
                  and trapping her behind a full-screen overlay that whole time -
                  unable to book anything else, unable to do anything but give up
                  - would make the feature unusable. */}
              {corporateRequest.isTicket && (
                <button
                  onClick={() => setCorporateRequest(null)}
                  className="w-full bg-safaricab-gold text-black font-bold py-3 rounded-xl mb-2"
                >
                  OK, notify me
                </button>
              )}
              <button onClick={cancelCorporateRequest} className="w-full border border-white/20 text-white/70 font-semibold py-3 rounded-xl">
                {corporateRequest.isTicket ? 'Cancel the request' : 'Cancel trip'}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Corporate: the approver issued a ticket ──
          Deliberately NOT the "finding you a driver" screen. Nobody has been
          dispatched and nobody is coming until she says so, and showing her a
          searching animation here would be a lie she'd act on. */}
      <AnimatePresence>
        {corporateTicketIssued && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[210] bg-black/80 flex items-center justify-center p-6"
            onClick={() => setCorporateTicketIssued(null)}
          >
            <motion.div
              initial={{ scale: 0.92, y: 12 }} animate={{ scale: 1, y: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-safaricab-dark border border-safaricab-gold/40 rounded-3xl p-6 w-full max-w-sm text-center"
            >
              <div className="w-16 h-16 bg-safaricab-gold/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Ticket className="w-8 h-8 text-safaricab-gold" />
              </div>
              <h2 className="text-white font-bold text-lg mb-1">Ticket approved</h2>
              {corporateTicketIssued.code && (
                <p className="text-safaricab-gold font-bold tracking-widest text-sm mb-3">{corporateTicketIssued.code}</p>
              )}
              <p className="text-white/60 text-sm mb-6">
                Your trip is approved and paid for. It's on your home screen for the next 24 hours —
                tap <span className="text-white font-semibold">Use ticket</span> whenever you're ready and we'll call a driver.
              </p>
              <button onClick={() => setCorporateTicketIssued(null)} className="w-full bg-safaricab-gold text-black font-bold py-3 rounded-xl">
                Got it
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Corporate: declined or lapsed → offer to pay for it themselves ── */}
      <AnimatePresence>
        {corporateOutcome && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[210] bg-black/80 flex items-center justify-center p-6"
          >
            <motion.div
              initial={{ scale: 0.92, y: 12 }} animate={{ scale: 1, y: 0 }}
              className="bg-safaricab-dark border border-white/15 rounded-3xl p-6 w-full max-w-sm text-center"
            >
              <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-8 h-8 text-white/60" />
              </div>
              <h2 className="text-white font-bold text-lg mb-2">
                {corporateOutcome === 'declined' ? 'Trip not approved' : 'No response in time'}
              </h2>
              <p className="text-white/60 text-sm mb-6">
                {corporateOutcome === 'declined'
                  ? 'Your company did not approve this trip.'
                  : "Nobody at your company approved the trip in time."}
                {' '}You can still take it on your own payment method.
              </p>
              <button onClick={bookWithoutCorporate} className="w-full bg-safaricab-gold text-black font-bold py-3 rounded-xl mb-2">
                Book it myself
              </button>
              <button onClick={() => setCorporateOutcome(null)} className="w-full text-white/50 py-2 text-sm">
                Not now
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Promotions - full-screen posters/videos published from the admin. Sits
          above everything at z-[200] and is dismissed by its own X (or Back). */}
      <AnimatePresence>
        {showPromotions && <PromotionsFeed onClose={() => setShowPromotions(false)} />}
      </AnimatePresence>

      {/* Sidebar */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25, ease: 'easeOut' }} onClick={() => setSidebarOpen(false)} className="fixed inset-0 bg-black/60 z-40" />
            <motion.div initial={{ x: '-100%' }} animate={{ x: 0 }} exit={{ x: '-100%' }} transition={{ duration: 0.28, ease: [0.32, 0, 0.12, 1] }} className="fixed left-0 top-0 bottom-0 w-72 bg-safaricab-dark z-50 overflow-y-auto">
              {/* The drawer is fixed to top-0, so its own header is the only thing
                  standing between the avatar/name and the status bar (notch on iOS,
                  the status bar on Android). py-6 alone left them sitting up in it —
                  pad by the safe-area inset on top of that, with the same 16px floor
                  the full-screen tab headers use for devices that report nothing. */}
              <div className="px-5 py-6 border-b border-white/10" style={{ paddingTop: 'calc(max(var(--sai-top), 16px) + 1.5rem)' }}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-14 h-14 rounded-full bg-safaricab-gold/20 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {avatarUrl
                      ? <img src={avatarUrl} alt="Profile" className="w-full h-full object-cover" />
                      : <User className="w-7 h-7 text-safaricab-gold" />}
                  </div>
                  <div><h3 className="text-white font-bold">{user.fullName || 'User'}</h3><p className="text-white/50 text-xs">{user.phone}</p></div>
                </div>
              </div>
              <div className="py-2">
                {[{ id: 'home', icon: MapPin, key: 'home' }, { id: 'history', icon: History, key: 'history' }, { id: 'payment', icon: CreditCard, key: 'payment_methods' }, { id: 'wallet', icon: WalletIcon, key: 'wallet' }, { id: 'promotions', icon: Gift, key: 'invite_earn' }, { id: 'spin', icon: FerrisWheel, key: 'spin_and_win' }, { id: 'trips', icon: Activity, key: 'activity' }, { id: 'notifications', icon: Bell, key: 'notifications' }, { id: 'places', icon: MapPin, key: 'saved_places' }, { id: 'emergency', icon: ShieldAlert, key: 'emergency_contacts' }, { id: 'profile', icon: User, key: 'profile' }].map(item => (
                  <button key={item.id} onClick={() => { setActiveTab(item.id as SidebarTab); setSidebarOpen(false); setRideResults([]); setDropoff(null); setDropoffQuery(''); }} className={`w-full flex items-center gap-3 px-5 py-3.5 text-sm ${activeTab === item.id ? 'bg-safaricab-gold/10 text-safaricab-gold' : 'text-white/70 hover:text-white'}`}>
                    <item.icon className="w-5 h-5" /><span>{t(item.key)}</span>
                    {item.id === 'notifications' && unreadNotifs > 0 && (
                      <span className="ml-2 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{unreadNotifs > 9 ? '9+' : unreadNotifs}</span>
                    )}
                    {item.id === 'spin' && spinsAvailable > 0 && (
                      <span className="ml-2 min-w-[18px] h-[18px] px-1 rounded-full bg-safaricab-gold text-black text-[10px] font-bold flex items-center justify-center">{spinsAvailable > 9 ? '9+' : spinsAvailable}</span>
                    )}
                    <ChevronRight className="w-4 h-4 ml-auto" />
                  </button>
                ))}
              </div>
              {/* Language switcher */}
              <div className="px-5 py-4 border-t border-white/10">
                <div className="flex items-center gap-2 mb-3">
                  <Globe className="w-4 h-4 text-white/50" />
                  <span className="text-white/50 text-xs uppercase tracking-wider">{t('language')}</span>
                </div>
                <div className="flex gap-2">
                  {(['en', 'sw', 'ar'] as const).map((code) => {
                    const labels: Record<typeof code, string> = { en: 'English', sw: 'Swahili', ar: 'عربي' };
                    return (
                      <button key={code} onClick={() => setLang(code)}
                        className={`flex-1 py-2 rounded-xl text-xs font-semibold border transition-colors ${lang === code ? 'bg-safaricab-gold text-black border-safaricab-gold' : 'bg-white/5 text-white/60 border-white/10'}`}>
                        {labels[code]}
                      </button>
                    );
                  })}
                </div>
              </div>
              <button onClick={handleLogout} className="flex items-center gap-3 text-red-400 text-sm w-full px-5 py-4 border-t border-white/10"><LogOut className="w-5 h-5" /><span>{t('logout')}</span></button>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Other tabs - Full Screen Overlay */}
      <AnimatePresence>
        {activeTab !== 'home' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="fixed inset-0 bg-safaricab-darker z-50 overflow-y-auto"
          >
            <div className="sticky top-0 bg-safaricab-dark px-4 pb-3 flex items-center gap-3 z-10" style={{ paddingTop: 'max(var(--sai-top), 16px)' }}>
              <button onClick={() => setActiveTab('home')} className="text-white p-1">
                <ArrowLeft className="w-6 h-6" />
              </button>
              <span className="text-white font-bold text-lg">
                {activeTab === 'places' ? 'Saved Places' :
                 activeTab === 'history' ? 'Ride History' :
                 activeTab === 'payment' ? 'Payment Methods' : 
                 activeTab === 'trips' ? 'Trips Activity' : 
                 activeTab === 'notifications' ? 'Notifications' :
                 activeTab === 'emergency' ? 'Emergency Contacts' :
                 activeTab === 'spin' ? 'Spin & Win' :
                 'Profile'}
              </span>
            </div>
            {activeTab === 'emergency' && (
              <EmergencyContacts
                shakeEnabled={shakeEnabled}
                onShakeToggle={toggleShake}
                onTriggerSos={() => { setActiveTab('home'); triggerSos('manual'); }}
              />
            )}
            {activeTab === 'history' && <RideHistory activeRideId={activeRideId} rideStatus={rideStatus} assignedDriver={assignedDriver} />}
            {activeTab === 'payment' && <PaymentMethods onOpenWallet={() => setActiveTab('wallet')} />}
          {activeTab === 'wallet' && <Wallet />}
            {activeTab === 'trips' && <TripsActivity />}
            {activeTab === 'notifications' && <Notifications />}
            {activeTab === 'promotions' && <Promotions />}
            {activeTab === 'spin' && <SpinAndWin />}
            {activeTab === 'profile' && <Profile user={user} />}
            {activeTab === 'places' && (
              <div className="px-4 py-4 space-y-6">
                {(['home', 'work'] as const).map((slot) => {
                  const list = savedPlaces[slot] || [];
                  return (
                    <div key={slot}>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-white font-bold flex items-center gap-2">
                          <span>{slot === 'home' ? '🏠' : '💼'}</span>{slot === 'home' ? 'Home' : 'Work'}
                          <span className="text-white/40 text-xs font-normal">({list.length}/5)</span>
                        </h3>
                        <button disabled={list.length >= 5}
                          onClick={() => { setSettingPlace(slot); setActiveTab('home'); setSearchFocused(true); setDropoffQuery(''); setDropoffSuggestions([]); }}
                          className="text-safaricab-gold text-sm font-semibold disabled:opacity-40">+ Add</button>
                      </div>
                      {list.length === 0 ? (
                        <p className="text-white/40 text-sm">No {slot} places yet. Tap “+ Add” to save up to 5.</p>
                      ) : (
                        <div className="space-y-2">
                          {list.map((loc: any, i: number) => (
                            <div key={i} className="flex items-center gap-3 bg-safaricab-card rounded-xl px-3 py-3 border border-white/10">
                              <MapPin className="w-4 h-4 text-safaricab-gold flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-white text-sm font-medium truncate">{loc.name || loc.address?.split(',')[0]}</p>
                                <p className="text-white/50 text-xs truncate">{loc.address}</p>
                              </div>
                              <button onClick={() => removeSavedPlace(slot, i)} className="text-red-400 p-1"><X className="w-4 h-4" /></button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
                <p className="text-white/30 text-xs">These appear as quick suggestions when you tap Home or Work in “Where to”.</p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CustomerDashboard;
